import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { flushSync } from 'react-dom';
import { AnimatePresence, motion } from "framer-motion";
import { ReactFlow, Background, Controls, MiniMap, useNodesState, useEdgesState, addEdge, Handle, Position, ReactFlowProvider, useReactFlow, useStore, getBezierPath, BaseEdge, getSmoothStepPath } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "./WorkflowCanvas.css";
import { Search, ChevronDown, ChevronUp, ChevronRight, Minus, Plus, Loader2, ArrowLeft, Check, X, Headset } from "lucide-react";
import WorkflowService from "../../services/workflow.service";
import AssistantService from "../../services/assistant.service";
import IntegrationService from "../../services/integration.service";
import DatabaseService from "../../services/database.service";
import ZenitLogo from "../../assets/Logo.webp";
import GoogleService from "../../services/google.service";
import { useOrganizations } from "../../hooks/useOrganizations";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { useAuth } from "../../useAuth";
import SchemaForm, { getSchemaFields } from "./SchemaForm";
import StyledSelect from "../ui/StyledSelect";
import ApiCallFields from "./ApiCallFields";
import { useAssistantContext } from "../../contexts/AssistantContext";
import ModelSelectorDialog from "../AssistantsView/ModelSelectorDialog";
import SkillStoreSelector from "../AssistantsView/SkillStoreSelector";
import useToast from "../../hooks/useToast";
import ToastContainer from "../ui/ToastContainer";
import WorkflowTestModal from "../ui/WorkflowTestModal";
import RunInspector from "./RunInspector";
import DataSelector, { resolveTokenLabel } from "./DataSelector";
import TokenField from "./TokenField";
import { StepIcon, ServiceGroupIcon } from "./StepIcon";
import { scheduleToCron, cronToSchedule, describeCron } from "../../utils/cronUtils";
import { normalizeWorkflowStepForRequest } from "./workflowStepSerialization";

// Validar si un nodo tiene campos requeridos sin completar
const hasIncompleteRequiredFields = (nodeData) => {
  const cfg = nodeData.configuration || {};
  const stepType = nodeData.step_type;
  
  // Validar según el tipo de step
  if (stepType === 'sheets_write_range') {
    return !cfg.spreadsheet_id || !cfg.range || !cfg.values || 
      (Array.isArray(cfg.values) && cfg.values.length === 0);
  }
  if (stepType === 'sheets_append_row') {
    return !cfg.spreadsheet_id || !cfg.range || !cfg.values;
  }
  if (stepType === 'gmail_send') {
    return !cfg.to || !cfg.subject || (!cfg.body_text && !cfg.body_html);
  }
  if (stepType === 'ai_agent') {
    return !nodeData.assistant_id;
  }
  if (stepType === 'condition') {
    return !cfg.left || !cfg.operator;
  }
  if (stepType === 'api_call') {
    return !cfg.url || !cfg.method;
  }
  if (stepType === 'database_query') {
    return !cfg.connection_id || !cfg.query;
  }
  if (stepType === 'wait_for_human_action') {
    return !cfg.integration_id || !cfg.recipient || !cfg.subject || !cfg.message || !cfg.actions?.length;
  }
  if (stepType === 'execute_workflow') {
    return !cfg.workflow_id;
  }

  // Validar triggers
  if (nodeData.trigger_type === 'webhook') {
    return !cfg.path || !cfg.method;
  }
  if (nodeData.trigger_type === 'scheduled') {
    return !cfg.cron_expression && !cfg.schedule;
  }
  
  return false;
};

const PinBadge = () => (
  <div className="absolute -bottom-1 -left-1 z-10">
    <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center shadow-md shadow-blue-500/50">
      <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/>
      </svg>
    </div>
  </div>
);

const AgentNode = ({ id, data, selected }) => {
  const { setNodes, setEdges } = useReactFlow();
  // Try multiple possible avatar field names from different API responses
  const avatarUrl = data.avatar_url || data.avatar || data.profile_image_url;
  const modelName = data.model_name || data.llm_id || '-';
  const skillsCount = data.skills_count || 0;
  const executionState = data.executionState;
  const hasWarning = hasIncompleteRequiredFields(data);
  // Reactively detect if satellite nodes exist in the store (works for loaded workflows too)
  const hasSatellites = useStore(state =>
    state.nodes.some(n => n.type === 'satellite' && n.data?.parentId === id)
  );
  // undefined = satellites just loaded → treat as expanded; false = explicitly collapsed
  const isExpanded = data.satellitesExpanded !== false;

  const toggleSatellites = useCallback((e) => {
    e.stopPropagation();
    const nextExpanded = !isExpanded;
    setNodes(nds => nds.map(n => {
      if (n.type === 'satellite' && n.data.parentId === id) {
        return { ...n, hidden: !nextExpanded };
      }
      if (n.id === id) {
        return { ...n, data: { ...n.data, satellitesExpanded: nextExpanded } };
      }
      return n;
    }));
    setEdges(eds => eds.map(e => {
      if (e.id.startsWith(`satellite-${id}`)) {
        return { ...e, hidden: !nextExpanded };
      }
      return e;
    }));
  }, [id, isExpanded, setNodes, setEdges]);

  // Extract model name for display (e.g., "gpt-4o" -> "gpt-4o", "claude-3-5-sonnet-20240620" -> "claude-3.5")
  const getDisplayModelName = (name) => {
    if (name === '-') return name;
    if (name.includes('gpt')) {
      const match = name.match(/(gpt-[^-]+)/i);
      return match ? match[1] : name;
    }
    if (name.includes('claude')) {
      const match = name.match(/claude-(\d+-\d+)/i);
      return match ? `claude-${match[1].replace('-', '.')}` : 'claude';
    }
    // Generic: take first 10 chars
    return name.length > 10 ? name.substring(0, 10) : name;
  };

  const displayModel = getDisplayModelName(modelName);

  return (
    <div>
      <div className={`relative w-[160px] h-[60px] rounded-xl bg-gradient-to-br from-violet-600/30 to-fuchsia-600/30 backdrop-blur-xl flex items-center gap-3 px-3 border shadow-xl shadow-violet-500/30 ${
        selected ? "border-violet-400 ring-2 ring-violet-400/50" : "border-violet-500/50"
      }`}>
        {hasWarning && !executionState && (
          <div className="absolute -top-1 -right-1 z-10">
            <div className="w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center">
              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        )}
        {executionState && (
          <div className="absolute top-1 right-1">
            {executionState.status === 'running' && (
              <svg className="w-3.5 h-3.5 text-amber-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {executionState.status === 'completed' && (
              <svg className="w-3.5 h-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {executionState.status === 'failed' && (
              <svg className="w-3.5 h-3.5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </div>
        )}
        {data.isPinned && <PinBadge />}
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 grid place-items-center shrink-0 shadow-lg shadow-violet-500/50 overflow-hidden">
          {avatarUrl ? (
            <img src={avatarUrl} alt="Agent" className="w-full h-full object-cover" />
          ) : (
            <Headset className="w-5 h-5 text-white" strokeWidth={2} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold leading-tight text-white truncate">{data.label || "AI Agent"}</div>
          <div className="text-[11px] text-violet-200/70 leading-tight truncate">{data.sub || "AI agent"}</div>
        </div>
        <Handle type="target" position={Position.Left} style={{ background: "#8b5cf6", width: 10, height: 10 }}/>
        <Handle type="source" position={Position.Right} style={{ background: "#8b5cf6", width: 10, height: 10 }}/>
        {/* Invisible handle — only used for edge routing to satellite nodes */}
        <Handle type="source" position={Position.Bottom} id="satellites" style={{ opacity: 0, width: 1, height: 1, minWidth: 'unset', minHeight: 'unset', bottom: 0 }}/>
      </div>
      <div className="flex flex-col items-center mt-1 gap-0.5">
        {(!hasSatellites || !isExpanded) && (
          <div className="grid grid-cols-3 gap-1 w-[160px]">
            <div className="text-center text-[9px] text-zinc-500 truncate">Avatar</div>
            <div className="text-center text-[9px] text-zinc-500 truncate">Model</div>
            <div className="text-center text-[9px] text-zinc-500 truncate">Skills</div>
          </div>
        )}
        {hasSatellites && (
          <button
            onClick={toggleSatellites}
            className="w-5 h-5 rounded-full bg-violet-700 hover:bg-primary border border-violet-400/70 flex items-center justify-center transition-all nodrag shadow-md shadow-violet-500/40 mt-0.5"
            title={isExpanded ? "Collapse satellites" : "Expand satellites"}
          >
            {isExpanded ? (
              <ChevronDown className="w-3 h-3 text-white" />
            ) : (
              <ChevronUp className="w-3 h-3 text-white" />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const TriggerNode = ({ data, selected }) => {
  const executionState = data.executionState;
  const hasWarning = hasIncompleteRequiredFields(data);
  
  return (
    <div className={`relative w-[160px] h-[60px] flex items-center gap-2.5 px-3 border rounded-l-xl rounded-r-3xl bg-gradient-to-r from-orange-500/20 to-amber-500/20 backdrop-blur-xl shadow-xl shadow-orange-500/30 text-orange-100 ${
      selected ? "border-orange-400 ring-2 ring-orange-400/50" : "border-orange-500/50"
    }`}>
      {hasWarning && !executionState && (
        <div className="absolute -top-1 -right-1 z-10">
          <div className="w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center">
            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      )}
      {executionState && (
        <div className="absolute top-1 right-1">
          {executionState.status === 'running' && (
            <svg className="w-3.5 h-3.5 text-amber-400 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          {executionState.status === 'completed' && (
            <svg className="w-3.5 h-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
          {executionState.status === 'failed' && (
            <svg className="w-3.5 h-3.5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
        </div>
      )}
      {data.isPinned && <PinBadge />}
      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 grid place-items-center shrink-0 shadow-lg shadow-orange-500/50">
        <StepIcon step={{ data, type: 'trigger' }} size={26} colorOverride="text-white" />
      </div>
      <div className="leading-tight min-w-0 flex-1">
        <div className="text-sm font-semibold truncate">{data.label || "On 'create user'"}</div>
        <div className="text-[10px] text-orange-200/60 truncate">{data.sub || "form submission"}</div>
      </div>
      <Handle type="source" position={Position.Right} style={{ background: "#f97316", width: 10, height: 10 }}/>
    </div>
  );
};

const ConditionNode = ({ data, selected }) => {
  const executionState = data.executionState;
  const hasWarning = hasIncompleteRequiredFields(data);
  
  return (
    <div className="relative">
      {hasWarning && !executionState && (
        <div className="absolute -top-2 -right-2 z-10">
          <div className="w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center">
            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      )}
      {data.isPinned && <PinBadge />}
      <div
        className={`relative w-[160px] h-[60px] bg-gradient-to-br from-amber-500/20 to-yellow-500/20 backdrop-blur-xl flex items-center justify-center text-amber-100 border shadow-xl shadow-amber-500/30 rounded-xl ${
          selected ? "border-amber-400 ring-2 ring-amber-400/50" : "border-amber-500/50"
        }`}
      >
        {executionState && (
          <div className="absolute top-1 right-1 z-10">
            {executionState.status === 'running' && (
              <svg className="w-3.5 h-3.5 text-amber-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {executionState.status === 'completed' && (
              <svg className="w-3.5 h-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {executionState.status === 'failed' && (
              <svg className="w-3.5 h-3.5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </div>
        )}
        <div className="flex flex-col items-center gap-1 text-sm font-semibold">
          <StepIcon step={{ data: { ...data, step_type: data.step_type || 'condition' }, type: 'condition' }} size={26} colorOverride="text-amber-100" />
          <span className="text-xs">{data.label || "if/else"}</span>
        </div>
        <Handle type="target" position={Position.Left} style={{ background: "#f59e0b", width: 10, height: 10 }}/>
        <Handle type="source" position={Position.Right} id="true" style={{ top: "30%", background: "#10b981", width: 10, height: 10 }}/>
        <Handle type="source" position={Position.Right} id="false" style={{ top: "70%", background: "#ef4444", width: 10, height: 10 }}/>
      </div>
      <div className="absolute -right-12 top-[22%] text-[9px] text-green-400 font-bold">TRUE</div>
      <div className="absolute -right-12 top-[62%] text-[9px] text-red-400 font-bold">FALSE</div>
    </div>
  );
};

const ActionNode = ({ data, selected }) => {
  const executionState = data.executionState;
  const hasWarning = hasIncompleteRequiredFields(data);

  const getIcon = () => <StepIcon step={{ data, type: 'action' }} size={36} />;

  return (
    <div>
      <div className={`relative w-[90px] h-[70px] rounded-xl bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 backdrop-blur-xl border shadow-lg ${
        selected ? "border-blue-400 ring-2 ring-blue-400/50 shadow-blue-500/30" : "border-zinc-700/50 shadow-zinc-900/50"
      } grid place-items-center ${
        data.color || "text-blue-300"
      }`}>
        {hasWarning && !executionState && (
          <div className="absolute -top-1 -right-1 z-10">
            <div className="w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center">
              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        )}
        {data.isPinned && <PinBadge />}
        {executionState && (
          <div className="absolute top-1 right-1">
            {executionState.status === 'running' && (
              <svg className="w-3 h-3 text-amber-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {executionState.status === 'completed' && (
              <svg className="w-3 h-3 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {executionState.status === 'failed' && (
              <svg className="w-3 h-3 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </div>
        )}
        {getIcon()}
        <Handle type="target" position={Position.Left} style={{ background: "#3b82f6", width: 10, height: 10 }}/>
        <Handle type="source" position={Position.Right} style={{ background: "#3b82f6", width: 10, height: 10 }}/>
      </div>
      <div className="text-[11px] mt-1.5 w-[100px] text-center -ml-1">
        <div className="text-zinc-100 leading-tight truncate font-semibold">{data.label || "Action"}</div>
        <div className="text-zinc-400 leading-tight truncate">{data.sub || ""}</div>
      </div>
    </div>
  );
};

const SatelliteNode = ({ data }) => {
  const isAvatar = data.slot === "Avatar";
  const isModel = data.slot === "Model";
  
  const getIcon = () => {
    // Si es Avatar y tiene avatarUrl, mostrar la imagen
    if (isAvatar && data.avatarUrl) {
      return (
        <img 
          src={data.avatarUrl} 
          alt={data.label}
          className="w-full h-full object-cover"
        />
      );
    }
    // NO mostrar imagen del assistant en el nodo Model
    if (data.icon === "✨") return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5z"/>
        <path d="M19 14l.7 2.3L22 17l-2.3.7L19 20l-.7-2.3L16 17l2.3-.7z"/>
      </svg>
    );
    if (data.icon === "🧠") return (
      <img src={ZenitLogo} alt="Zenit" className="w-8 h-8 object-contain" />
    );
    if (data.icon === "🔧") return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/>
      </svg>
    );
    if (data.icon === "🌐") return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9"/>
        <path d="M3 12h18"/>
        <path d="M12 3a14 14 0 0 1 0 18"/>
        <path d="M12 3a14 14 0 0 0 0 18"/>
      </svg>
    );
    if (data.icon === "💬") return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12z"/>
      </svg>
    );
    return <span className="text-xl">{data.icon}</span>;
  };
  
  const shape = isAvatar ? "rounded-full" : "rounded-lg";
  const frame = isAvatar
    ? "bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 border-violet-500/30 text-violet-200"
    : isModel
      ? "bg-zinc-900 border-violet-500/30 text-violet-300 hover:border-violet-500/60"
      : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700";
  
  return (
    <div>
      <div className={`w-[80px] h-[60px] border grid place-items-center transition cursor-pointer overflow-hidden ${shape} ${frame}`}>
        {getIcon()}
      </div>
      <div className="text-[11px] mt-1.5 w-24 text-center -ml-1.5">
        <div className="text-zinc-300 leading-tight truncate">{data.label || ""}</div>
        <div className="text-zinc-500 leading-tight truncate">{data.sub || ""}</div>
      </div>
      <Handle type="target" position={Position.Top} style={{ background: "#8b5cf6", width: 10, height: 10 }}/>
    </div>
  );
};

// Edge that routes below nodes when the connection goes backward (right→left)
const SmartEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style = {}, markerEnd }) => {
  const isSatellite = id?.includes('satellite-');
  const isBackward = !isSatellite && targetX < sourceX - 50;
  let edgePath;
  if (isBackward) {
    const r = 16; // corner radius
    const bottomY = Math.max(sourceY, targetY) + 60;
    edgePath = [
      `M ${sourceX},${sourceY}`,
      `L ${sourceX},${bottomY - r}`,
      `Q ${sourceX},${bottomY} ${sourceX - r},${bottomY}`,
      `L ${targetX + r},${bottomY}`,
      `Q ${targetX},${bottomY} ${targetX},${bottomY - r}`,
      `L ${targetX},${targetY}`,
    ].join(' ');
  } else {
    [edgePath] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  }
  return <BaseEdge id={id} path={edgePath} style={style} markerEnd={markerEnd} />;
};

const edgeTypes = { default: SmartEdge };

const nodeTypes = {
  trigger: TriggerNode,
  agent: AgentNode,
  condition: ConditionNode,
  action: ActionNode,
  satellite: SatelliteNode,
};

const BACKEND_STEP_TYPES = new Set([
  'ai_agent',
  'llm_call',
  'api_call',
  'webhook',
  'database',
  'database_query',
  'file_operation',
  'shopify_update_order',
  'drive_upload',
  'drive_download_file',
  'drive_list_files',
  'drive_share_file',
  'sheets_append_row',
  'sheets_read_range',
  'sheets_write_range',
  'sheets_create_spreadsheet',
  'docs_create_document',
  'docs_get_document',
  'docs_batch_update',
  'calendar_create_event',
  'calendar_list_events',
  'gmail_send',
  'gmail_search',
  'notification',
  'slack_notification',
  'discord_notification',
  'gmail',
  'whatsapp_notification',
  'instagram_notification',
  'workflow',
  'wait',
  'wait_duration',
  'wait_condition',
  'wait_event',
  'wait_user_input',
  'wait_webhook',
  'wait_for_human_action',
  'execute_workflow',
  'executable_by_workflow',
  'condition',
  'data_transform',
  'code_execution',
  'delay',
  'log',
  'metric',
  'error_handler',
]);

const generateUUID = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const resolveBackendStepType = (node) => {
  const explicitStepType = String(node?.data?.step_type || '').trim();
  if (explicitStepType && BACKEND_STEP_TYPES.has(explicitStepType)) {
    return explicitStepType;
  }

  if (node?.type === 'agent') return 'ai_agent';
  if (node?.type === 'condition') return 'condition';
  if (node?.type === 'trigger' || node?.type === 'satellite') return null;

  if (node?.type === 'action') {
    const hint = `${node?.data?.label || ''} ${node?.data?.sub || ''}`.toLowerCase();
    if (hint.includes('api')) return 'api_call';
    if (hint.includes('database') || hint.includes('save data')) return 'database';
    if (hint.includes('python') || hint.includes('code')) return 'code_execution';
    if (hint.includes('whatsapp')) return 'whatsapp_notification';
    if (hint.includes('instagram')) return 'instagram_notification';
    if (hint.includes('email') || hint.includes('gmail')) return 'gmail';
  }

  return null;
};

const serializeWorkflowStep = (node) => {
  const stepType = resolveBackendStepType(node);
  if (!stepType) return null;

  const rawConfiguration = node?.data?.configuration;
  const safeConfiguration =
    rawConfiguration && typeof rawConfiguration === 'object' && !Array.isArray(rawConfiguration)
      ? rawConfiguration
      : {};

  const stepName =
    (typeof node?.data?.stepName === 'string' && node.data.stepName.trim()) ||
    (typeof node?.data?.label === 'string' && node.data.label.trim()) ||
    null;

  const stepDescription =
    (typeof node?.data?.stepDescription === 'string' && node.data.stepDescription.trim()) ||
    (typeof node?.data?.description === 'string' && node.data.description.trim()) ||
    (typeof node?.data?.sub === 'string' && node.data.sub.trim()) ||
    '';

  const existingStepId =
    typeof node?.data?.step_id === 'string' && WorkflowService.isValidUUID(node.data.step_id)
      ? node.data.step_id
      : null;

  const position = node?.position && typeof node.position.x === 'number' && typeof node.position.y === 'number'
    ? { x: node.position.x, y: node.position.y }
    : null;

  if (stepType === 'ai_agent') {
    const assistantId =
      node?.data?.assistant_id ||
      safeConfiguration?.assistant_id ||
      safeConfiguration?.assistantId ||
      null;

    const tasks =
      Array.isArray(node?.data?.tasks) && node.data.tasks.length > 0
        ? node.data.tasks
        : null;

    return {
      ...(existingStepId ? { id: existingStepId } : {}),
      ...(stepName ? { name: stepName } : {}),
      ...(stepDescription ? { description: stepDescription } : {}),
      ...safeConfiguration,
      step_type: 'ai_agent',
      ...(assistantId ? { assistant_id: assistantId } : {}),
      ...(tasks ? { tasks } : {}),
      ...(position ? { position } : {}),
    };
  }

  if (stepType === 'condition') {
    const left = safeConfiguration.left || '';
    const operator = safeConfiguration.operator || 'eq';
    const right = safeConfiguration.right || '';
    
    // Si left está vacío, el condition no está configurado - no serializar
    if (!left.trim()) {
      console.warn('Condition node is not configured (missing left value), skipping serialization');
      return null;
    }

    // Según la documentación, left/operator/right van directamente en parameters, no en parameters.condition
    return {
      ...(existingStepId ? { id: existingStepId } : {}),
      ...(stepName ? { name: stepName } : {}),
      ...(stepDescription ? { description: stepDescription } : {}),
      step_type: 'condition',
      parameters: {
        step_type: 'condition',
        version: 1,
        left: left.trim(),
        operator: operator,
        right: right || null,
        on_true: safeConfiguration.on_true || null,
        on_false: safeConfiguration.on_false || null,
        max_iterations: safeConfiguration.max_iterations || 100,
        on_max_iterations: safeConfiguration.on_max_iterations || null,
        ...(stepDescription ? { description: stepDescription } : {})
      },
      ...(position ? { position } : {})
    };
  }

  if (stepType === 'execute_workflow') {
    // workflow_id (target) lives in parameters; spreading it at the root would
    // collide with the parent workflow_id and get stripped by the normalizer.
    return {
      ...(existingStepId ? { id: existingStepId } : {}),
      ...(stepName ? { name: stepName } : {}),
      ...(stepDescription ? { description: stepDescription } : {}),
      step_type: 'execute_workflow',
      parameters: {
        step_type: 'execute_workflow',
        version: 1,
        workflow_id: safeConfiguration.workflow_id || null,
        inputs: safeConfiguration.inputs || {},
        wait_for_completion: safeConfiguration.wait_for_completion !== false,
        continue_on_failure: safeConfiguration.continue_on_failure === true,
        ...(safeConfiguration.timeout_seconds !== undefined
          ? { timeout_seconds: safeConfiguration.timeout_seconds }
          : {})
      },
      ...(position ? { position } : {})
    };
  }

  if (stepType === 'code_execution') {
    const rawInputs = safeConfiguration.inputs;
    const inputs = Array.isArray(rawInputs)
      ? rawInputs.reduce((acc, { key, value }) => {
          if (key) acc[key] = value ?? '';
          return acc;
        }, {})
      : (isPlainObject(rawInputs) ? rawInputs : {});
    return {
      ...(existingStepId ? { id: existingStepId } : {}),
      ...(stepName ? { name: stepName } : {}),
      ...(stepDescription ? { description: stepDescription } : {}),
      step_type: 'code_execution',
      parameters: {
        step_type: 'code_execution',
        version: 1,
        code: safeConfiguration.code || '',
        inputs,
        timeout: safeConfiguration.timeout || 30,
      },
      ...(position ? { position } : {})
    };
  }

  // For sheets steps, ensure 'values' is always a parsed 2D array, not a raw string
  const configForSpread = { ...safeConfiguration };
  if (
    (stepType === 'sheets_write_range' || stepType === 'sheets_append_row') &&
    typeof configForSpread.values === 'string'
  ) {
    try {
      configForSpread.values = JSON.parse(configForSpread.values);
    } catch {
      // leave as-is; backend will reject with a clear error
    }
  }

  // La configuración del nodo es siempre parameters-level (se cargó desde
  // step.parameters). Anidarla bajo `parameters` en vez de aplanarla a la raíz
  // evita que campos como `timeout_seconds` (duración de espera de un wait) se
  // confundan con el `timeout_seconds` de nivel step (timeout de ejecución).
  // El timeout de nivel step se preserva por passthrough en el normalizador.
  return {
    ...(existingStepId ? { id: existingStepId } : {}),
    ...(stepName ? { name: stepName } : {}),
    ...(stepDescription ? { description: stepDescription } : {}),
    step_type: stepType,
    parameters: {
      ...configForSpread,
      step_type: stepType,
    },
    ...(position ? { position } : {})
  };
};

const sanitizeTriggerConfiguration = (triggerType, configuration) => {
  const sourceConfiguration = isPlainObject(configuration) ? configuration : {};
  const uiOnlyKeys = new Set([
    'name',
    'label',
    'sub',
    'trigger_id',
    'id',
    'workflow_id',
    'account_id',
    'project_id',
    'is_active'
  ]);

  if (triggerType === 'manual') {
    return { trigger_type: 'manual' };
  }

  const sanitized = {};
  Object.entries(sourceConfiguration).forEach(([key, value]) => {
    if (uiOnlyKeys.has(key)) return;
    if (value === undefined) return;
    sanitized[key] = value;
  });

  if (!sanitized.trigger_type) {
    sanitized.trigger_type = triggerType || 'manual';
  }

  return sanitized;
};

const normalizeWorkflowTriggerForRequest = (triggerNode, index = 0) => {
  const triggerData = isPlainObject(triggerNode?.data) ? triggerNode.data : {};
  const triggerConfiguration = isPlainObject(triggerData.configuration) ? triggerData.configuration : {};
  const resolvedTriggerType =
    (typeof triggerData.trigger_type === 'string' && triggerData.trigger_type.trim()) ||
    (typeof triggerConfiguration.trigger_type === 'string' && triggerConfiguration.trigger_type.trim()) ||
    'manual';

  if (resolvedTriggerType === 'manual') {
    return { trigger_type: 'manual' };
  }

  const sanitizedConfiguration = sanitizeTriggerConfiguration(
    resolvedTriggerType,
    triggerConfiguration
  );

  // Ensure required API fields are present with defaults
  return {
    trigger_type: resolvedTriggerType,
    enabled: sanitizedConfiguration.enabled !== false,
    ...sanitizedConfiguration,
    metadata: sanitizedConfiguration.metadata || {}
  };
};

const buildTriggerNodeFromApi = (trigger, index = 0) => {
  const safeTrigger = isPlainObject(trigger) ? trigger : {};
  // The API may return either `configuration` or `config_summary` depending on the endpoint.
  const safeConfiguration = isPlainObject(safeTrigger.configuration)
    ? safeTrigger.configuration
    : isPlainObject(safeTrigger.config_summary)
      ? safeTrigger.config_summary
      : {};
  const resolvedType =
    (typeof safeTrigger.trigger_type === 'string' && safeTrigger.trigger_type) ||
    (typeof safeConfiguration.trigger_type === 'string' && safeConfiguration.trigger_type) ||
    'manual';
  const triggerLabels = {
    webhook: 'On webhook',
    scheduled: 'On schedule',
    manual: 'On manual',
    shopify_event: 'On Shopify event',
    whatsapp_inbound: 'On WhatsApp message'
  };
  const triggerSubLabels = {
    webhook: 'HTTP POST',
    scheduled: 'cron expression',
    manual: 'user trigger',
    shopify_event: 'store event',
    whatsapp_inbound: 'inbound message'
  };

  return {
    id: `trigger-${safeTrigger.id || index}`,
    type: 'trigger',
    position: safeTrigger.position || { x: 100, y: 100 + (index * 150) },
    data: {
      label: safeTrigger.name || triggerLabels[resolvedType] || 'Trigger',
      sub: triggerSubLabels[resolvedType] || '',
      trigger_id: safeTrigger.id,
      trigger_type: resolvedType,
      is_active: safeTrigger.is_active,
      configuration: safeConfiguration
    }
  };
};

// Normalizar config_override del backend (camelCase) a frontend (snake_case)
const normalizeConfigOverride = (configOverride) => {
  if (!configOverride || typeof configOverride !== 'object') return null;
  
  const normalized = {};
  if (configOverride.llmId) normalized.llm_id = configOverride.llmId;
  if (configOverride.llm_id) normalized.llm_id = configOverride.llm_id; // Por si acaso el backend usa snake_case
  if (Array.isArray(configOverride.skills)) normalized.skills = configOverride.skills;
  if (typeof configOverride.temperature === 'number') normalized.temperature = configOverride.temperature;
  if (configOverride.maxTokens !== null && configOverride.maxTokens !== undefined) normalized.max_tokens = configOverride.maxTokens;
  if (configOverride.max_tokens !== null && configOverride.max_tokens !== undefined) normalized.max_tokens = configOverride.max_tokens;
  
  return Object.keys(normalized).length > 0 ? normalized : null;
};

const getSkillCategory = (skillName) => {
  if (skillName.startsWith('gmail_')) return 'Google';
  if (skillName.startsWith('web_')) return 'Utility';
  if (skillName === 'memory') return 'Memory';
  return 'Tool';
};

const getSkillIcon = (skillName) => {
  if (skillName.startsWith('web_')) return '🌐';
  if (skillName.startsWith('gmail_')) return '💬';
  return '🔧';
};

const buildAgentSatelliteGraph = ({
  parentNodeId,
  parentPosition,
  assistant,
  fallbackLabel = 'AI Agent',
  llmModels = []
}) => {
  if (!parentNodeId || !parentPosition) {
    return { nodes: [], edges: [] };
  }

  const assistantData = isPlainObject(assistant) ? assistant : {};
  const assistantConfig = isPlainObject(assistantData.config) ? assistantData.config : {};
  const assistantName = assistantData.name || fallbackLabel;
  
  // Get real model name from llmModels registry
  const llmId = assistantConfig.llmId || assistantConfig.llm_id;
  const modelFromRegistry = Array.isArray(llmModels) && llmModels.find(m => m.id === llmId);
  const modelLabel = modelFromRegistry?.name || llmId || 'Model';
  const rawSkills = Array.isArray(assistantData.skills)
    ? assistantData.skills
    : (Array.isArray(assistantConfig.skills) ? assistantConfig.skills : []);

  const nodes = [];
  const edges = [];
  const baseX = parentPosition.x;
  const baseY = parentPosition.y + 160;
  let xOffset = -110;

  const assistantAvatarUrl = assistantData.avatar || assistantData.avatar_url || assistantData.profile_image_url || null;
  const avatarTemplateUrl = assistantConfig.assistantTemplate?.avatar || assistantConfig.assistant_template?.avatar || null;

  const avatarNode = {
    id: `satellite-${parentNodeId}-avatar`,
    type: 'satellite',
    position: { x: baseX + xOffset, y: baseY },
    data: {
      slot: 'Avatar',
      label: assistantName || 'Avatar',
      sub: 'Avatar',
      icon: '✨',
      avatarUrl: avatarTemplateUrl,
      parentId: parentNodeId
    }
  };
  nodes.push(avatarNode);
  edges.push({
    id: `satellite-${parentNodeId}-avatar-edge`,
    source: parentNodeId,
    target: avatarNode.id,
    sourceHandle: 'satellites',
    type: 'default',
    style: { stroke: '#8b5cf6', strokeWidth: 2 }
  });
  xOffset += 100;

  const modelNode = {
    id: `satellite-${parentNodeId}-model`,
    type: 'satellite',
    position: { x: baseX + xOffset, y: baseY },
    data: {
      slot: 'Model',
      label: modelLabel,
      sub: 'Chat Model',
      icon: '🧠',
      parentId: parentNodeId
    }
  };
  nodes.push(modelNode);
  edges.push({
    id: `satellite-${parentNodeId}-model-edge`,
    source: parentNodeId,
    target: modelNode.id,
    sourceHandle: 'satellites',
    type: 'default',
    style: { stroke: '#8b5cf6', strokeWidth: 2 }
  });
  xOffset += 100;

  rawSkills.forEach((skill, index) => {
    const skillName = typeof skill === 'string' ? skill : (skill?.name || 'Skill');
    const skillCategory = getSkillCategory(skillName);
    const skillIcon = getSkillIcon(skillName);

    const skillNode = {
      id: `satellite-${parentNodeId}-skill-${index}`,
      type: 'satellite',
      position: { x: baseX + xOffset, y: baseY },
      data: {
        slot: 'Skill',
        label: skillName,
        sub: `Skill · ${skillCategory}`,
        icon: skillIcon,
        parentId: parentNodeId
      }
    };
    nodes.push(skillNode);
    edges.push({
      id: `satellite-${parentNodeId}-skill-${index}-edge`,
      source: parentNodeId,
      target: skillNode.id,
      sourceHandle: 'satellites',
      type: 'default',
      style: { stroke: '#8b5cf6', strokeWidth: 2 }
    });
    xOffset += 100;
  });

  return { nodes, edges };
};

const StepPaletteItem = ({ step }) => (
  <div
    draggable
    onDragStart={(e) => e.dataTransfer.setData("application/reactflow", JSON.stringify(step))}
    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:bg-white/5 hover:text-white cursor-grab active:cursor-grabbing transition-all group"
  >
    <div className="w-7 h-7 rounded-lg bg-white/5 ring-1 ring-white/5 flex items-center justify-center group-hover:bg-white/10 group-hover:ring-white/10 transition-all">
      <StepIcon step={step} size={16} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-medium truncate">{step.label}</div>
      <div className="text-[11px] text-gray-500 truncate">{step.description}</div>
    </div>
  </div>
);

const StepPaletteCategory = ({ title, steps, groups, expanded, onToggle }) => {
  return (
    <div className="border-b border-white/5">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest hover:text-white hover:bg-white/5 transition-all"
      >
        {title}
        {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>

      {expanded && (
        <div className="px-2 pb-3 space-y-1">
          {Array.isArray(groups) && groups.length > 0
            ? groups.map((group) => (
                <div key={group.key} className="space-y-1">
                  {group.label && (
                    <div className="flex items-center gap-2 px-3 pt-2 pb-1 text-[9px] font-semibold text-gray-500 uppercase tracking-wider">
                      <ServiceGroupIcon groupKey={group.key} size={14} />
                      <span>{group.label}</span>
                    </div>
                  )}
                  {group.steps.map((step, idx) => (
                    <StepPaletteItem key={`${group.key}-${idx}`} step={step} />
                  ))}
                </div>
              ))
            : steps.map((step, idx) => (
                <StepPaletteItem key={idx} step={step} />
              ))}
        </div>
      )}
    </div>
  );
};

// Group HTTP & Integrations steps by service prefix (gmail_*, drive_*, etc.)
const SERVICE_GROUPS = [
  { key: 'gmail',    label: 'Gmail',         icon: '📧', match: (t) => t.startsWith('gmail_') },
  { key: 'drive',    label: 'Google Drive',  icon: '📁', match: (t) => t.startsWith('drive_') },
  { key: 'sheets',   label: 'Google Sheets', icon: '📊', match: (t) => t.startsWith('sheets_') },
  { key: 'calendar', label: 'Calendar',      icon: '📅', match: (t) => t.startsWith('calendar_') || t.startsWith('gcal_') },
  { key: 'slack',    label: 'Slack',         icon: '💬', match: (t) => t.startsWith('slack_') },
  { key: 'discord',  label: 'Discord',       icon: '🎮', match: (t) => t.startsWith('discord_') },
  { key: 'telegram', label: 'Telegram',      icon: '✈️', match: (t) => t.startsWith('telegram_') },
  { key: 'whatsapp', label: 'WhatsApp',      icon: '💚', match: (t) => t.startsWith('whatsapp_') },
  { key: 'shopify',  label: 'Shopify',       icon: '🛍️', match: (t) => t.startsWith('shopify_') },
  { key: 'http',     label: 'HTTP',          icon: '🌐', match: (t) => t === 'http_request' || t.startsWith('http_') || t.startsWith('webhook_call_') },
];

const groupStepsByService = (steps) => {
  const buckets = SERVICE_GROUPS.map((g) => ({ ...g, steps: [] }));
  const other = { key: 'other', label: 'Other', icon: '🔌', steps: [] };
  steps.forEach((step) => {
    const stepType = step?.data?.step_type || '';
    const bucket = buckets.find((b) => b.match(stepType));
    if (bucket) bucket.steps.push(step);
    else other.steps.push(step);
  });
  const result = buckets.filter((b) => b.steps.length > 0).map(({ match, ...rest }) => rest);
  if (other.steps.length > 0) result.push(other);
  return result;
};

const isHttpIntegrationsCategory = (category) => {
  const key = (category?.key || '').toLowerCase();
  const title = (category?.title || '').toLowerCase();
  return key.includes('http') || key.includes('integration')
    || title.includes('http') || title.includes('integration');
};

const StepInspector = ({ selectedNode, currentAccount, projectId, setNodes, setEdges, llmModels, allNodes = [] }) => {
  const [assistants, setAssistants] = useState([]);
  const [loadingAssistants, setLoadingAssistants] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const resolveLabel = useCallback(
    (token) => resolveTokenLabel(token, allNodes),
    [allNodes]
  );
  
  // Google Sheets state
  const [spreadsheetMetadata, setSpreadsheetMetadata] = useState(null);
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [sheetsError, setSheetsError] = useState(null);

  // Callable workflows (for execute_workflow step)
  const [callableWorkflows, setCallableWorkflows] = useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);

  useEffect(() => {
    if (selectedNode?.type === 'agent' && currentAccount?.id) {
      loadAssistants();
    }
  }, [selectedNode?.type, currentAccount?.id]);

  // Load callable workflows when an execute_workflow step is selected
  useEffect(() => {
    const stepType = selectedNode?.data?.step_type;
    if (stepType !== 'execute_workflow' || !currentAccount?.id) return;
    setLoadingWorkflows(true);
    WorkflowService.listWorkflows({ account_id: currentAccount.id, project_id: projectId, limit: 200 })
      .then((result) => {
        if (result.success) {
          setCallableWorkflows(result.data.workflows || []);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingWorkflows(false));
  }, [selectedNode?.data?.step_type, currentAccount?.id, projectId]);
  
  // Load metadata when a Sheets node is selected and has spreadsheet_id
  useEffect(() => {
    const stepType = selectedNode?.data?.step_type;
    const isSheetsNode = stepType?.startsWith('sheets_');
    
    if (!isSheetsNode) {
      setSheetsError(null);
      setSpreadsheetMetadata(null);
    }
  }, [selectedNode?.data?.step_type]);

  // Load spreadsheet metadata when a spreadsheet is selected
  useEffect(() => {
    const spreadsheetId = selectedNode?.data?.configuration?.spreadsheet_id;
    if (spreadsheetId && spreadsheetId !== spreadsheetMetadata?.spreadsheetId) {
      loadSpreadsheetMetadata(spreadsheetId);
    }
  }, [selectedNode?.data?.configuration?.spreadsheet_id, spreadsheetMetadata?.spreadsheetId]);

  useEffect(() => {
    if (selectedNode?.data?.assistant_id && assistants.length > 0) {
      const assistant = assistants.find((item) => item.id === selectedNode.data.assistant_id);
      if (assistant) {
        setSelectedAssistant(assistant);
        
        // Update node with assistant info if not already present
        if (!selectedNode.data.avatar_url && !selectedNode.data.llm_id) {
          setNodes((nds) => nds.map((node) => {
            if (node.id === selectedNode.id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  avatar_url: assistant.avatar || assistant.avatar_url || assistant.profile_image_url || null,
                  llm_id: assistant.llm_id,
                  model_name: assistant.model_name,
                  skills_count: assistant.skills?.length || 0
                }
              };
            }
            return node;
          }));
        }
      }
      return;
    }

    setSelectedAssistant(null);
  }, [selectedNode?.data?.assistant_id, assistants, selectedNode?.data?.avatar_url, selectedNode?.data?.llm_id, selectedNode?.id, setNodes]);

  const loadAssistants = async () => {
    setLoadingAssistants(true);
    try {
      const result = await AssistantService.listAssistants({
        accountId: currentAccount.id,
        projectId: projectId,
        limit: 100
      });
      if (result.success && result.data) {
        setAssistants(result.data);
      }
    } catch (error) {
      console.error('Error loading assistants:', error);
    } finally {
      setLoadingAssistants(false);
    }
  };
  
  const loadSpreadsheetMetadata = async (spreadsheetId) => {
    if (!spreadsheetId) return;
    
    setLoadingMetadata(true);
    setSheetsError(null);
    
    try {
      const result = await GoogleService.getSpreadsheet(spreadsheetId, { includeGridData: false });
      
      if (result.success && result.data) {
        // Parse Lambda response format
        const responseData = result.data.body ? 
          (typeof result.data.body === 'string' ? JSON.parse(result.data.body) : result.data.body) : 
          result.data;
        
        const metadata = responseData.data || responseData;
        
        console.log('Spreadsheet metadata loaded:', metadata);
        setSpreadsheetMetadata(metadata);
      } else {
        console.error('Failed to load spreadsheet metadata:', result.error);
        setSheetsError('Failed to load spreadsheet metadata.');
      }
    } catch (error) {
      console.error('Error loading spreadsheet metadata:', error);
      setSpreadsheetMetadata(null);
      setSheetsError('Unable to load spreadsheet metadata.');
    } finally {
      setLoadingMetadata(false);
    }
  };
  
  const handleSpreadsheetIdChange = (spreadsheetId) => {
    if (!spreadsheetId) return;
    
    // Update node configuration with spreadsheet ID
    setNodes((nds) => nds.map((node) => {
      if (node.id === selectedNode.id) {
        return {
          ...node,
          data: {
            ...node.data,
            configuration: {
              ...node.data.configuration,
              spreadsheet_id: spreadsheetId
            }
          }
        };
      }
      return node;
    }));
    
    // Load metadata if it's a valid ID format
    if (spreadsheetId.length > 20) {
      loadSpreadsheetMetadata(spreadsheetId);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setNodes((nds) => nds.map((node) => {
      if (node.id === selectedNode.id) {
        return {
          ...node,
          data: {
            ...node.data,
            configuration: {
              ...node.data.configuration,
              [fieldName]: value
            }
          }
        };
      }
      return node;
    }));
  };

  const renderSatelliteNodes = (assistant) => {
    if (!selectedNode || !assistant) return;

    setNodes((nds) => nds.filter((node) =>
      !(node.type === 'satellite' && node.data.parentId === selectedNode.id)
    ));
    setEdges((eds) => eds.filter((edge) =>
      !edge.id.startsWith(`satellite-${selectedNode.id}`)
    ));

    const satelliteGraph = buildAgentSatelliteGraph({
      parentNodeId: selectedNode.id,
      parentPosition: selectedNode.position,
      assistant,
      fallbackLabel: assistant.name || selectedNode.data?.label || 'AI Agent',
      llmModels
    });

    // Mark parent agent node as expanded so toggle button appears
    setNodes((nds) => [
      ...nds.map(n => n.id === selectedNode.id
        ? { ...n, data: { ...n.data, satellitesExpanded: true } }
        : n
      ),
      ...satelliteGraph.nodes
    ]);
    setEdges((eds) => [...eds, ...satelliteGraph.edges]);
  };

  const handleAssistantChange = (e) => {
    const assistantId = e.target.value;
    if (!assistantId) return;

    const assistant = assistants.find((item) => item.id === assistantId);
    if (!assistant) return;

    setSelectedAssistant(assistant);

    setNodes((nds) => nds.map((node) => {
      if (node.id === selectedNode.id) {
        return {
          ...node,
          data: {
            ...node.data,
            assistant_id: assistant.id,
            label: assistant.name,
            sub: assistant.description || 'AI Agent',
            avatar_url: assistant.avatar || assistant.avatar_url || assistant.profile_image_url || null,
            llm_id: assistant.llm_id,
            model_name: assistant.model_name,
            skills_count: assistant.skills?.length || 0
          }
        };
      }
      return node;
    }));

    renderSatelliteNodes(assistant);
  };

  if (!selectedNode) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8">
        <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
          <span className="text-3xl opacity-40">🎯</span>
        </div>
        <div className="text-sm font-semibold text-gray-400">Select a step to configure</div>
        <p className="text-xs text-gray-600 mt-2 max-w-xs">
          Click on any node in the canvas to view and edit its configuration
        </p>
      </div>
    );
  }

  // Helper: Get step type description
  const getStepTypeDescription = () => {
    const stepType = selectedNode.data?.step_type || selectedNode.type;
    const descriptions = {
      ai_agent: "Runs agent tasks against a pre-configured agent",
      api_call: "HTTP API Request",
      drive_upload: "Google Drive File Upload",
      shopify_update_order: "Shopify Order Update",
      database: "Database CRUD Operation",
      database_query: "Raw Database Query",
      whatsapp_notification: "WhatsApp Message",
      instagram_notification: "Instagram DM",
      condition: "Conditional Branch",
      wait_condition: "Wait Until Condition",
      wait_duration: "Fixed Delay",
      wait_event: "Wait for System Event",
      wait_user_input: "Wait for User Input",
      wait_for_human_action: "Wait for Human Action (Email)",
      execute_workflow: "Execute Another Workflow",
      executable_by_workflow: "Callable by Another Workflow",
      wait_webhook: "Wait for Webhook",
      log: "Log Message",
      code_execution: "Python Code Execution",
      trigger: "Workflow Trigger",
      action: "Generic Action",
      satellite: "Tool Configuration",
      agent: "AI Agent Configuration",
      sheets_append_row: "Append Row to Google Sheet",
      calendar_create_event: "Create Google Calendar Event",
      docs_create_document: "Create Google Docs Document",
      sheets_create_spreadsheet: "Create Google Spreadsheet",
      drive_download_file: "Download Google Drive File",
      docs_get_document: "Get Google Docs Document",
      calendar_list_events: "List Google Calendar Events",
      drive_list_files: "List Google Drive Files",
      sheets_read_range: "Read Google Sheets Range",
      gmail_search: "Search Gmail Messages",
      gmail_send: "Send Email via Gmail",
      drive_share_file: "Share Google Drive File",
      docs_batch_update: "Update Google Docs Document",
      sheets_write_range: "Write to Google Sheets Range",
    };
    return descriptions[stepType] || "Step Configuration";
  };

  const stepType = selectedNode.data?.step_type || selectedNode.type;
  const config = selectedNode.data?.configuration || {};

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-bold text-white mb-0.5">{selectedNode.data.label || selectedNode.type}</h3>
          <p className="text-[10px] text-gray-400">{getStepTypeDescription()}</p>
        </div>
      </div>

      {/* AI Agent */}
      {(selectedNode.type === "agent" || stepType === "ai_agent") && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Agent <span className="text-red-400">*</span>
            </label>
            {loadingAssistants ? (
              <div className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-gray-400 flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                Loading...
              </div>
            ) : (
              <select
                className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:border-violet-500/50"
                value={selectedAssistant?.id || ''}
                onChange={handleAssistantChange}
              >
                <option value="">Select agent...</option>
                {assistants.map((assistant) => (
                  <option key={assistant.id} value={assistant.id}>{assistant.name}</option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Execution Mode</label>
            <select
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:border-violet-500/50"
              defaultValue={config.execution_mode || 'sequential'}
            >
              <option value="sequential">Sequential - Tasks run one after another, can reference earlier outputs</option>
              <option value="parallel">Parallel - Tasks run concurrently, must be independent</option>
            </select>
          </div>
        </div>
      )}

      {/* Schedule Trigger */}
      {(selectedNode.type === "trigger" && selectedNode.data?.trigger_type === "scheduled") && (
        <div className="space-y-3">
          {(() => {
            const cronExpression = config.cron_expression || '0 9 * * *';
            const currentTimezone = config.timezone || 'UTC';

            return (
              <>
                {/* Quick info */}
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-500/20 grid place-items-center shrink-0">
                      <span className="text-xl">⏰</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-400 mb-1">Current schedule:</p>
                      <p className="text-sm font-semibold text-orange-300 mb-2">{describeCron(cronExpression)}</p>
                      <p className="text-xs text-gray-500 font-mono">Cron: {cronExpression}</p>
                      <p className="text-xs text-gray-500 mt-1">Timezone: {currentTimezone}</p>
                    </div>
                  </div>
                </div>

                {/* Double click hint */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-400 mb-1">💡 Tip</p>
                  <p className="text-xs text-gray-300">
                    <span className="font-semibold">Double-click</span> this trigger to open the schedule editor
                  </p>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Webhook Trigger */}
      {(selectedNode.type === "trigger" && selectedNode.data?.trigger_type === "webhook") && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Path <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20"
              placeholder="https://app.zenit/webhooks/mi-workflow"
              value={config.path || ''}
              onChange={(e) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        configuration: {
                          ...node.data.configuration,
                          path: e.target.value
                        }
                      }
                    };
                  }
                  return node;
                }));
              }}
            />
            <p className="text-xs text-gray-500 mt-2">URL path for the webhook endpoint</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">HTTP Method</label>
            <select
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20"
              value={config.method || 'POST'}
              onChange={(e) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        configuration: {
                          ...node.data.configuration,
                          method: e.target.value
                        }
                      }
                    };
                  }
                  return node;
                }));
              }}
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Content Type</label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20"
              placeholder="application/json"
              value={config.content_type || ''}
              onChange={(e) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        configuration: {
                          ...node.data.configuration,
                          content_type: e.target.value
                        }
                      }
                    };
                  }
                  return node;
                }));
              }}
            />
            <p className="text-xs text-gray-500 mt-2">Expected Content-Type header (default: application/json)</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Authentication</label>
            <select
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20"
              value={config.auth?.type || 'none'}
              onChange={(e) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    const authType = e.target.value;
                    const authConfig = authType === 'none' ? undefined : { type: authType };
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        configuration: {
                          ...node.data.configuration,
                          auth: authConfig
                        }
                      }
                    };
                  }
                  return node;
                }));
              }}
            >
              <option value="none">None</option>
              <option value="signature">Signature</option>
              <option value="api_key">API Key</option>
              <option value="bearer">Bearer Token</option>
              <option value="basic">Basic Auth</option>
            </select>
            <p className="text-xs text-gray-500 mt-2">Authentication scheme for the endpoint</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Rate Limit (per minute)</label>
            <input
              type="number"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20"
              placeholder="100"
              min="0"
              value={config.rate_limit_per_minute || ''}
              onChange={(e) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        configuration: {
                          ...node.data.configuration,
                          rate_limit_per_minute: e.target.value ? parseInt(e.target.value) : undefined
                        }
                      }
                    };
                  }
                  return node;
                }));
              }}
            />
            <p className="text-xs text-gray-500 mt-2">Maximum requests per minute (optional)</p>
          </div>

          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 grid place-items-center shrink-0">
                <span className="text-xl">🔗</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-400 mb-1">Webhook URL:</p>
                <p className="text-sm font-semibold text-orange-300 font-mono break-all">
                  {config.path ? `https://api.example.com${config.path}` : 'Configure path above'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Executable by Workflow Trigger */}
      {(selectedNode.type === "trigger" && selectedNode.data?.trigger_type === "executable_by_workflow") && (
        <div className="space-y-4">
          {/* Info banner */}
          <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/20 grid place-items-center shrink-0">
                <span className="text-xl">🔁</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-indigo-300 mb-1">Callable by Another Workflow</p>
                <p className="text-xs text-gray-400">
                  This workflow can be invoked by an <span className="text-indigo-300 font-mono">execute_workflow</span> step in another workflow.
                  Define the inputs it expects below.
                </p>
              </div>
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Display Name (optional)</label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/20"
              placeholder="e.g. Send Welcome Email"
              value={config.name || ''}
              onChange={(e) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    return { ...node, data: { ...node.data, configuration: { ...node.data.configuration, name: e.target.value } } };
                  }
                  return node;
                }));
              }}
            />
            <p className="text-xs text-gray-500 mt-2">Human-readable name shown in the Execute Workflow step picker</p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Description (optional)</label>
            <textarea
              rows={2}
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/20 resize-none"
              placeholder="What does this workflow do?"
              value={config.description || ''}
              onChange={(e) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    return { ...node, data: { ...node.data, configuration: { ...node.data.configuration, description: e.target.value } } };
                  }
                  return node;
                }));
              }}
            />
          </div>

          {/* Inputs array editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-white">Input Parameters</label>
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                onClick={() => {
                  const newInput = { name: '', type: 'text', required: false, description: '' };
                  setNodes((nds) => nds.map((node) => {
                    if (node.id === selectedNode.id) {
                      const inputs = [...(node.data.configuration?.inputs || []), newInput];
                      return { ...node, data: { ...node.data, configuration: { ...node.data.configuration, inputs } } };
                    }
                    return node;
                  }));
                }}
              >
                <Plus className="w-3.5 h-3.5" /> Add Input
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Define the variables this workflow expects when called by another workflow. Each input is available as{' '}
              <code className="text-indigo-400">{'{{trigger:input_name}}'}</code>.
            </p>

            {(!config.inputs || config.inputs.length === 0) ? (
              <div className="border border-dashed border-white/10 rounded-xl p-4 text-center">
                <p className="text-xs text-gray-500">No inputs defined — this workflow can be called without arguments.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {config.inputs.map((input, idx) => (
                  <div key={idx} className="bg-black/30 border border-white/10 rounded-xl p-3 space-y-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-gray-400">Input {idx + 1}</span>
                      <button
                        type="button"
                        className="text-red-400/60 hover:text-red-400 transition-colors"
                        onClick={() => {
                          setNodes((nds) => nds.map((node) => {
                            if (node.id === selectedNode.id) {
                              const inputs = node.data.configuration.inputs.filter((_, i) => i !== idx);
                              return { ...node, data: { ...node.data, configuration: { ...node.data.configuration, inputs } } };
                            }
                            return node;
                          }));
                        }}
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-1">Name <span className="text-red-400">*</span></label>
                        <input
                          type="text"
                          className="w-full px-2 py-1.5 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none focus:border-indigo-500/50"
                          placeholder="customer_id"
                          value={input.name || ''}
                          onChange={(e) => {
                            setNodes((nds) => nds.map((node) => {
                              if (node.id === selectedNode.id) {
                                const inputs = node.data.configuration.inputs.map((inp, i) =>
                                  i === idx ? { ...inp, name: e.target.value } : inp
                                );
                                return { ...node, data: { ...node.data, configuration: { ...node.data.configuration, inputs } } };
                              }
                              return node;
                            }));
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-1">Type</label>
                        <select
                          className="w-full px-2 py-1.5 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:border-indigo-500/50"
                          value={input.type || 'text'}
                          onChange={(e) => {
                            setNodes((nds) => nds.map((node) => {
                              if (node.id === selectedNode.id) {
                                const inputs = node.data.configuration.inputs.map((inp, i) =>
                                  i === idx ? { ...inp, type: e.target.value } : inp
                                );
                                return { ...node, data: { ...node.data, configuration: { ...node.data.configuration, inputs } } };
                              }
                              return node;
                            }));
                          }}
                        >
                          <option value="text">text</option>
                          <option value="number">number</option>
                          <option value="boolean">boolean</option>
                          <option value="date">date</option>
                          <option value="list">list</option>
                          <option value="object">object</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-1">Description (optional)</label>
                      <input
                        type="text"
                        className="w-full px-2 py-1.5 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:border-indigo-500/50"
                        placeholder="What this input is used for"
                        value={input.description || ''}
                        onChange={(e) => {
                          setNodes((nds) => nds.map((node) => {
                            if (node.id === selectedNode.id) {
                              const inputs = node.data.configuration.inputs.map((inp, i) =>
                                i === idx ? { ...inp, description: e.target.value } : inp
                              );
                              return { ...node, data: { ...node.data, configuration: { ...node.data.configuration, inputs } } };
                            }
                            return node;
                          }));
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`input-required-${idx}`}
                        checked={input.required || false}
                        className="w-3 h-3 accent-indigo-500"
                        onChange={(e) => {
                          setNodes((nds) => nds.map((node) => {
                            if (node.id === selectedNode.id) {
                              const inputs = node.data.configuration.inputs.map((inp, i) =>
                                i === idx ? { ...inp, required: e.target.checked } : inp
                              );
                              return { ...node, data: { ...node.data, configuration: { ...node.data.configuration, inputs } } };
                            }
                            return node;
                          }));
                        }}
                      />
                      <label htmlFor={`input-required-${idx}`} className="text-xs text-gray-400 select-none">Required</label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* API Call */}
      {stepType === "api_call" && (
        <ApiCallFields
          key={selectedNode.id}
          compact
          config={config}
          onFieldChange={handleFieldChange}
        />
      )}

      {/* Database CRUD */}
      {stepType === "database" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Operation</label>
              <select className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" defaultValue={config.operation || 'select'}>
                <option>select</option>
                <option>insert</option>
                <option>update</option>
                <option>delete</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Connection</label>
              <select className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" defaultValue={config.connection_mode || 'zenit'}>
                <option value="zenit">Zenit DB</option>
                <option value="external">External</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Table Name</label>
            <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" placeholder="users" defaultValue={config.table_name || ''} />
          </div>
        </div>
      )}

      {/* WhatsApp Notification */}
      {stepType === "whatsapp_notification" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Recipient <span className="text-red-400">*</span>
            </label>
            <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" placeholder="+1234567890" defaultValue={config.recipient || ''} />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Message</label>
            <textarea className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" rows={3} placeholder="Your message here..." defaultValue={config.message_content || ''} />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Type</label>
            <select className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" defaultValue={config.message_type || 'text'}>
              <option>text</option>
              <option>template</option>
              <option>image</option>
              <option>document</option>
            </select>
          </div>
        </div>
      )}

      {/* Condition */}
      {(selectedNode.type === "condition" || stepType === "condition") && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Left Value <span className="text-red-400">*</span>
            </label>
            <input 
              type="text" 
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" 
              placeholder="{{context:step_3.result}}" 
              value={config.left || ''} 
              onChange={(e) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        configuration: {
                          ...node.data.configuration,
                          left: e.target.value
                        }
                      }
                    };
                  }
                  return node;
                }));
              }}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Operator <span className="text-red-400">*</span>
            </label>
            <select 
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" 
              value={config.operator || 'eq'}
              onChange={(e) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        configuration: {
                          ...node.data.configuration,
                          operator: e.target.value
                        }
                      }
                    };
                  }
                  return node;
                }));
              }}
            >
              <option value="eq">equals</option>
              <option value="ne">not equals</option>
              <option value="gt">greater than</option>
              <option value="lt">less than</option>
              <option value="gte">greater than or equal</option>
              <option value="lte">less than or equal</option>
              <option value="contains">contains</option>
              <option value="not_contains">not contains</option>
              <option value="starts_with">starts with</option>
              <option value="ends_with">ends with</option>
              <option value="is_true">is true</option>
              <option value="is_false">is false</option>
              <option value="is_empty">is empty</option>
              <option value="not_empty">not empty</option>
              <option value="exists">exists</option>
              <option value="not_exists">not exists</option>
              <option value="is_null">is null</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Right Value</label>
            <input 
              type="text" 
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" 
              placeholder="expected_value" 
              value={config.right || ''} 
              onChange={(e) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        configuration: {
                          ...node.data.configuration,
                          right: e.target.value
                        }
                      }
                    };
                  }
                  return node;
                }));
              }}
            />
            <p className="text-[9px] text-gray-500 mt-1">Leave empty for IS_TRUE/IS_FALSE operators</p>
          </div>
        </div>
      )}

      {/* Wait Steps */}
      {stepType === "wait_duration" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Duration (seconds)</label>
            <input type="number" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" defaultValue={config.duration_seconds || 60} />
          </div>
        </div>
      )}

      {stepType === "wait_condition" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Condition</label>
            <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" placeholder="status == 'completed'" defaultValue={config.condition || ''} />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Timeout (s)</label>
            <input type="number" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" defaultValue={config.timeout_seconds || 300} />
          </div>
        </div>
      )}

      {stepType === "wait_webhook" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Webhook Path</label>
            <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" placeholder="https://app.zenit/webhooks/mi-workflow" defaultValue={config.webhook_path || ''} />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Match Type</label>
            <select className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" defaultValue={config.match_type || 'webhook'}>
              <option>webhook</option>
              <option>whatsapp</option>
              <option>email</option>
              <option>custom</option>
            </select>
          </div>
        </div>
      )}

      {/* Log */}
      {stepType === "log" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Message</label>
            <textarea className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" rows={3} placeholder="{{context:result}}" defaultValue={config.message || ''} />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Level</label>
            <select className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" defaultValue={config.level || 'info'}>
              <option>debug</option>
              <option>info</option>
              <option>warning</option>
              <option>error</option>
            </select>
          </div>
        </div>
      )}

      {/* Code Execution */}
      {stepType === "code_execution" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Python Code</label>
            <textarea className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" rows={5} placeholder="def run(inputs):\n    return {'result': inputs['value'] * 2}" defaultValue={config.code || ''} />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Timeout (s)</label>
            <input type="number" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" defaultValue={config.timeout || 30} />
          </div>
        </div>
      )}

      {/* Google Drive Upload */}
      {stepType === "drive_upload" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              File Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none"
              placeholder="report.pdf"
              value={config.file_name || ''}
              onChange={(e) => handleFieldChange('file_name', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">File Content</label>
            <textarea
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none"
              rows={3}
              placeholder="{{context:report_data}}"
              value={config.file_content || ''}
              onChange={(e) => handleFieldChange('file_content', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">MIME Type</label>
            <input
              type="text"
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none"
              placeholder="text/markdown"
              value={config.mime_type || 'text/markdown'}
              onChange={(e) => handleFieldChange('mime_type', e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Shopify Update Order */}
      {stepType === "shopify_update_order" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Order ID <span className="text-red-400">*</span>
            </label>
            <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" placeholder="{{trigger.order_id}}" defaultValue={config.order_id || ''} />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Tags</label>
            <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" placeholder="processed, urgent" defaultValue={config.tags || ''} />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Note</label>
            <textarea className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" rows={2} placeholder="Order processed successfully" defaultValue={config.note || ''} />
          </div>
        </div>
      )}

      {/* Database Query */}
      {stepType === "database_query" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Connection</label>
            <select className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" defaultValue={config.connection_mode || 'zenit'}>
              <option value="zenit">Zenit DB</option>
              <option value="external">External</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Query <span className="text-red-400">*</span>
            </label>
            <textarea className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" rows={4} placeholder="SELECT * FROM users WHERE active = true" defaultValue={config.query || ''} />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Timeout (s)</label>
            <input type="number" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" defaultValue={config.timeout || 30} />
          </div>
        </div>
      )}

      {/* Wait Event */}
      {stepType === "wait_event" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Event Type <span className="text-red-400">*</span>
            </label>
            <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" placeholder="file.uploaded" defaultValue={config.event_type || ''} />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Timeout (s)</label>
            <input type="number" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" defaultValue={config.timeout_seconds || 300} />
          </div>
        </div>
      )}

      {/* Wait User Input */}
      {stepType === "wait_user_input" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Input Schema</label>
            <textarea className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" rows={4} placeholder='{"type": "object", "properties": {...}}' defaultValue={config.input_schema ? JSON.stringify(config.input_schema, null, 2) : ''} />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Timeout (s)</label>
            <input type="number" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" defaultValue={config.timeout_seconds || 3600} />
          </div>
        </div>
      )}

      {/* Google Sheets - Append Row */}
      {stepType === "sheets_append_row" && (
        <div className="space-y-4">
          {/* Spreadsheet ID Input */}
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Spreadsheet ID <span className="text-red-400">*</span>
            </label>
            <input 
              type="text" 
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none focus:border-violet-500/50" 
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms" 
              value={config.spreadsheet_id || ''} 
              onChange={(e) => handleSpreadsheetIdChange(e.target.value)}
            />
            <p className="text-[9px] text-gray-500 mt-1">From Google Sheets URL or use Create Spreadsheet node first</p>
            {sheetsError && (
              <div className="mt-2 w-full px-3 py-2 text-xs bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400">
                ⚠️ {sheetsError}
              </div>
            )}
          </div>
          
          {/* Sheet/Tab Selector (from metadata) */}
          {spreadsheetMetadata?.sheets && (
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                Sheet/Tab <span className="text-red-400">*</span>
              </label>
              <select
                className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:border-violet-500/50"
                value={config.range || ''}
                onChange={(e) => {
                  setNodes((nds) => nds.map((node) => {
                    if (node.id === selectedNode.id) {
                      return {
                        ...node,
                        data: {
                          ...node.data,
                          configuration: {
                            ...node.data.configuration,
                            range: e.target.value
                          }
                        }
                      };
                    }
                    return node;
                  }));
                }}
              >
                <option value="">Select a sheet...</option>
                {spreadsheetMetadata.sheets.map((sheet) => (
                  <option key={sheet.sheetId || sheet.properties?.sheetId} value={sheet.title || sheet.properties?.title}>
                    {sheet.title || sheet.properties?.title}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Range <span className="text-red-400">*</span>
            </label>
            <input 
              type="text" 
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" 
              placeholder="Sheet1 or Sheet1!A1:C10" 
              value={config.range || ''}
              onChange={(e) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        configuration: {
                          ...node.data.configuration,
                          range: e.target.value
                        }
                      }
                    };
                  }
                  return node;
                }));
              }}
            />
            <p className="text-[9px] text-gray-500 mt-1">Anchor range in A1 notation (e.g. 'Sheet1' or 'Sheet1!A1:C10')</p>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Values <span className="text-red-400">*</span>
            </label>
            <textarea 
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" 
              rows={3} 
              placeholder='[["value1", "value2", "value3"]]' 
              value={config.values ? JSON.stringify(config.values, null, 2) : ''}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  setNodes((nds) => nds.map((node) => {
                    if (node.id === selectedNode.id) {
                      return {
                        ...node,
                        data: {
                          ...node.data,
                          configuration: {
                            ...node.data.configuration,
                            values: parsed
                          }
                        }
                      };
                    }
                    return node;
                  }));
                } catch (err) {
                  // Invalid JSON, keep as string
                }
              }}
            />
            <p className="text-[9px] text-gray-500 mt-1">2D array: one row per inner list</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Value Input Option</label>
              <select 
                className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" 
                value={config.value_input_option || 'USER_ENTERED'}
                onChange={(e) => {
                  setNodes((nds) => nds.map((node) => {
                    if (node.id === selectedNode.id) {
                      return {
                        ...node,
                        data: {
                          ...node.data,
                          configuration: {
                            ...node.data.configuration,
                            value_input_option: e.target.value
                          }
                        }
                      };
                    }
                    return node;
                  }));
                }}
              >
                <option>USER_ENTERED</option>
                <option>RAW</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Insert Data Option</label>
              <select className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" defaultValue={config.insert_data_option || 'INSERT_ROWS'}>
                <option>INSERT_ROWS</option>
                <option>OVERWRITE</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Google Calendar - Create Event */}
      {stepType === "calendar_create_event" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Summary <span className="text-red-400">*</span>
            </label>
            <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" placeholder="Team Meeting" defaultValue={config.summary || ''} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                Start DateTime <span className="text-red-400">*</span>
              </label>
              <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" placeholder="2026-05-01T10:00:00-05:00" defaultValue={config.start_datetime || ''} />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                End DateTime <span className="text-red-400">*</span>
              </label>
              <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" placeholder="2026-05-01T11:00:00-05:00" defaultValue={config.end_datetime || ''} />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Description</label>
            <textarea className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" rows={2} placeholder="Event description..." defaultValue={config.description || ''} />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Location</label>
            <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" placeholder="Conference Room A" defaultValue={config.location || ''} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Calendar ID</label>
              <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" placeholder="primary" defaultValue={config.calendar_id || 'primary'} />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Timezone</label>
              <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" placeholder="UTC" defaultValue={config.timezone || 'UTC'} />
            </div>
          </div>
        </div>
      )}

      {/* Google Docs - Create Document */}
      {stepType === "docs_create_document" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none"
              placeholder="New Document"
              value={config.title || ''}
              onChange={(e) => handleFieldChange('title', e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Google Sheets - Create Spreadsheet */}
      {stepType === "sheets_create_spreadsheet" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Title <span className="text-red-400">*</span>
            </label>
            <input 
              type="text" 
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" 
              placeholder="New Spreadsheet" 
              value={config.title || ''} 
              onChange={(e) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        configuration: {
                          ...node.data.configuration,
                          title: e.target.value
                        }
                      }
                    };
                  }
                  return node;
                }));
              }}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Sheet Titles</label>
            <textarea 
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" 
              rows={2} 
              placeholder='["Sheet1", "Sheet2"]' 
              value={config.sheet_titles ? JSON.stringify(config.sheet_titles) : ''} 
              onChange={(e) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    let parsedSheets = null;
                    try {
                      if (e.target.value.trim()) {
                        parsedSheets = JSON.parse(e.target.value);
                      }
                    } catch (error) {
                      // Invalid JSON, keep as string for now
                      parsedSheets = e.target.value;
                    }
                    
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        configuration: {
                          ...node.data.configuration,
                          sheet_titles: parsedSheets
                        }
                      }
                    };
                  }
                  return node;
                }));
              }}
            />
            <p className="text-[9px] text-gray-500 mt-1">Optional list of tab titles (defaults to 'Sheet1')</p>
          </div>
        </div>
      )}

      {/* Google Drive - Download File */}
      {stepType === "drive_download_file" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              File ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none"
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
              value={config.file_id || ''}
              onChange={(e) => handleFieldChange('file_id', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Export Format</label>
            <input
              type="text"
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none"
              placeholder="application/pdf"
              value={config.export_format || ''}
              onChange={(e) => handleFieldChange('export_format', e.target.value)}
            />
            <p className="text-[9px] text-gray-500 mt-1">Target MIME type for Google Workspace files (e.g. 'application/pdf', 'text/csv')</p>
          </div>
        </div>
      )}

      {/* Google Docs - Get Document */}
      {stepType === "docs_get_document" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Document ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none"
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
              value={config.document_id || ''}
              onChange={(e) => handleFieldChange('document_id', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Suggestions View Mode</label>
            <select
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none"
              value={config.suggestions_view_mode || 'DEFAULT_FOR_CURRENT_ACCESS'}
              onChange={(e) => handleFieldChange('suggestions_view_mode', e.target.value)}
            >
              <option>DEFAULT_FOR_CURRENT_ACCESS</option>
              <option>SUGGESTIONS_INLINE</option>
              <option>PREVIEW_SUGGESTIONS_ACCEPTED</option>
              <option>PREVIEW_WITHOUT_SUGGESTIONS</option>
            </select>
          </div>
        </div>
      )}

      {/* Google Calendar - List Events */}
      {stepType === "calendar_list_events" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Calendar ID</label>
            <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" placeholder="primary" defaultValue={config.calendar_id || 'primary'} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Time Min</label>
              <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" placeholder="2026-05-01T00:00:00Z" defaultValue={config.time_min || ''} />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Time Max</label>
              <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" placeholder="2026-05-31T23:59:59Z" defaultValue={config.time_max || ''} />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Search Query</label>
            <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" placeholder="meeting" defaultValue={config.query || ''} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Max Results</label>
              <input type="number" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" defaultValue={config.max_results || 10} />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Order By</label>
              <select className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" defaultValue={config.order_by || 'startTime'}>
                <option>startTime</option>
                <option>updated</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Google Drive - List Files */}
      {stepType === "drive_list_files" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Query</label>
            <input
              type="text"
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none"
              placeholder="mimeType='application/pdf'"
              value={config.query || ''}
              onChange={(e) => handleFieldChange('query', e.target.value)}
            />
            <p className="text-[9px] text-gray-500 mt-1">Drive search query (Google's native syntax)</p>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Parent Folder ID</label>
            <input
              type="text"
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none"
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
              value={config.parent_folder_id || ''}
              onChange={(e) => handleFieldChange('parent_folder_id', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Max Results</label>
              <input
                type="number"
                className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none"
                value={config.max_results || 10}
                onChange={(e) => handleFieldChange('max_results', parseInt(e.target.value))}
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Order By</label>
              <input
                type="text"
                className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none"
                placeholder="modifiedTime desc"
                value={config.order_by || 'modifiedTime desc'}
                onChange={(e) => handleFieldChange('order_by', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Google Sheets - Read Range */}
      {stepType === "sheets_read_range" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Spreadsheet ID <span className="text-red-400">*</span>
            </label>
            <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms" defaultValue={config.spreadsheet_id || ''} />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Range <span className="text-red-400">*</span>
            </label>
            <input type="text" className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" placeholder="Sheet1!A1:D10" defaultValue={config.range || ''} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Value Render Option</label>
              <select className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" defaultValue={config.value_render_option || 'FORMATTED_VALUE'}>
                <option>FORMATTED_VALUE</option>
                <option>UNFORMATTED_VALUE</option>
                <option>FORMULA</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Major Dimension</label>
              <select className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" defaultValue={config.major_dimension || 'ROWS'}>
                <option>ROWS</option>
                <option>COLUMNS</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Gmail - Search */}
      {stepType === "gmail_search" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Query <span className="text-red-400">*</span>
            </label>
            <input 
              type="text" 
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none" 
              placeholder="from:invoice@x.com newer_than:7d" 
              value={config.query || ''} 
              onChange={(e) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        configuration: {
                          ...node.data.configuration,
                          query: e.target.value
                        }
                      }
                    };
                  }
                  return node;
                }));
              }}
            />
            <p className="text-[9px] text-gray-500 mt-1">Gmail search query syntax (e.g., from:, to:, subject:, newer_than:)</p>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Max Results</label>
            <input 
              type="number" 
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none" 
              value={config.max_results || 10} 
              onChange={(e) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        configuration: {
                          ...node.data.configuration,
                          max_results: parseInt(e.target.value) || 10
                        }
                      }
                    };
                  }
                  return node;
                }));
              }}
            />
          </div>
        </div>
      )}

      {/* Gmail - Send */}
      {stepType === "gmail_send" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              To <span className="text-red-400">*</span>
            </label>
            <TokenField
              resolveLabel={resolveLabel}
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none"
              placeholder="user@example.com, user2@example.com"
              value={config.to ? (Array.isArray(config.to) ? config.to.join(', ') : config.to) : ''}
              onChange={(val) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    const toValue = val.split(',').map(email => email.trim()).filter(Boolean);
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        configuration: {
                          ...node.data.configuration,
                          to: toValue
                        }
                      }
                    };
                  }
                  return node;
                }));
              }}
            />
            <p className="text-[9px] text-gray-500 mt-1">Comma-separated email addresses</p>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Subject <span className="text-red-400">*</span>
            </label>
            <TokenField
              resolveLabel={resolveLabel}
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none"
              placeholder="Email subject"
              value={config.subject || ''}
              onChange={(val) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        configuration: {
                          ...node.data.configuration,
                          subject: val
                        }
                      }
                    };
                  }
                  return node;
                }));
              }}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Body (Text) <span className="text-red-400">*</span>
            </label>
            <TokenField
              resolveLabel={resolveLabel}
              multiline
              rows={3}
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none"
              placeholder="Plain text email body..."
              value={config.body_text || ''}
              onChange={(val) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        configuration: {
                          ...node.data.configuration,
                          body_text: val
                        }
                      }
                    };
                  }
                  return node;
                }));
              }}
            />
            <p className="text-[9px] text-gray-500 mt-1">Required if Body (HTML) is empty</p>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Body (HTML)</label>
            <TokenField
              resolveLabel={resolveLabel}
              multiline
              rows={3}
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none"
              placeholder="<p>HTML email body...</p>"
              value={config.body_html || ''}
              onChange={(val) => {
                setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        configuration: {
                          ...node.data.configuration,
                          body_html: val
                        }
                      }
                    };
                  }
                  return node;
                }));
              }}
            />
            <p className="text-[9px] text-gray-500 mt-1">Optional, but at least one body type is required</p>
          </div>
        </div>
      )}

      {/* Google Drive - Share File */}
      {stepType === "drive_share_file" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              File ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none"
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
              value={config.file_id || ''}
              onChange={(e) => handleFieldChange('file_id', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Email <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none"
              placeholder="user@example.com"
              value={config.email || ''}
              onChange={(e) => handleFieldChange('email', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Role</label>
            <select
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none"
              value={config.role || 'reader'}
              onChange={(e) => handleFieldChange('role', e.target.value)}
            >
              <option>reader</option>
              <option>writer</option>
              <option>commenter</option>
              <option>owner</option>
            </select>
          </div>
        </div>
      )}

      {/* Google Docs - Batch Update */}
      {stepType === "docs_batch_update" && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Document ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none"
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
              value={config.document_id || ''}
              onChange={(e) => handleFieldChange('document_id', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Replace Text</label>
            <textarea
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none"
              rows={3}
              placeholder='{"{{placeholder}}": "replacement"}'
              value={config.replace_text ? JSON.stringify(config.replace_text, null, 2) : ''}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  handleFieldChange('replace_text', parsed);
                } catch {
                  handleFieldChange('replace_text', e.target.value);
                }
              }}
            />
            <p className="text-[9px] text-gray-500 mt-1">Mapping of placeholder text to replacement text</p>
          </div>
        </div>
      )}

      {/* Google Sheets - Write Range */}
      {stepType === "sheets_write_range" && (() => {
        const updateField = (field, value) => {
          setNodes((nds) => nds.map((node) => {
            if (node.id === selectedNode.id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  configuration: {
                    ...node.data.configuration,
                    [field]: value
                  }
                }
              };
            }
            return node;
          }));
        };
        const valuesText = config.values !== undefined && config.values !== null
          ? (typeof config.values === 'string' ? config.values : JSON.stringify(config.values, null, 2))
          : '';
        return (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Spreadsheet ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none"
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
              value={config.spreadsheet_id || ''}
              onChange={(e) => updateField('spreadsheet_id', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Range <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none"
              placeholder="Sheet1!A1:D10"
              value={config.range || ''}
              onChange={(e) => updateField('range', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Values <span className="text-red-400">*</span>
            </label>
            <textarea
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none"
              rows={3}
              placeholder='[["value1", "value2"], ["value3", "value4"]]'
              value={valuesText}
              onChange={(e) => {
                const raw = e.target.value;
                try {
                  updateField('values', JSON.parse(raw));
                } catch {
                  updateField('values', raw);
                }
              }}
            />
            <p className="text-[9px] text-gray-500 mt-1">2D array: outer list = rows, inner list = columns</p>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Value Input Option</label>
            <select
              className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none"
              value={config.value_input_option || 'USER_ENTERED'}
              onChange={(e) => updateField('value_input_option', e.target.value)}
            >
              <option>USER_ENTERED</option>
              <option>RAW</option>
            </select>
          </div>
        </div>
        );
      })()}

      {/* Execute Workflow - inline config */}
      {stepType === "execute_workflow" && (
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Target Workflow <span className="text-red-400">*</span>
            </label>
            {loadingWorkflows ? (
              <div className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-gray-400 flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                Loading workflows...
              </div>
            ) : (
              <select
                className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:border-violet-500/50"
                value={config.workflow_id || ''}
                onChange={(e) => handleFieldChange('workflow_id', e.target.value)}
              >
                <option value="">Select workflow...</option>
                {callableWorkflows.map((wf) => (
                  <option key={wf.id} value={wf.id}>
                    {wf.name || wf.id}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="exec-wf-wait"
              checked={config.wait_for_completion !== false}
              className="w-3 h-3 accent-violet-500"
              onChange={(e) => handleFieldChange('wait_for_completion', e.target.checked)}
            />
            <label htmlFor="exec-wf-wait" className="text-xs text-gray-400 select-none">Wait for completion (sync)</label>
          </div>
          <p className="text-[10px] text-gray-500">Open step editor to configure inputs and advanced options.</p>
        </div>
      )}

      {/* Wait for Human Action - inline preview */}
      {stepType === "wait_for_human_action" && (
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Recipient</label>
            <p className="text-xs text-white font-mono truncate">{config.recipient || <span className="text-gray-500 italic">not set</span>}</p>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Subject</label>
            <p className="text-xs text-white truncate">{config.subject || <span className="text-gray-500 italic">not set</span>}</p>
          </div>
          {config.actions?.length > 0 && (
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Actions</label>
              <div className="flex flex-wrap gap-1">
                {config.actions.map((a, i) => (
                  <span key={i} className="px-2 py-0.5 bg-orange-500/20 border border-orange-500/30 rounded text-orange-300 text-[10px]">
                    {a.label || a.id}
                  </span>
                ))}
              </div>
            </div>
          )}
          <p className="text-[10px] text-gray-500">Open step editor to configure all fields.</p>
        </div>
      )}

      {/* Fallback for other types */}
      {!["agent", "ai_agent", "api_call", "database", "database_query", "drive_upload", "shopify_update_order", "whatsapp_notification", "condition", "wait_duration", "wait_condition", "wait_event", "wait_user_input", "wait_for_human_action", "execute_workflow", "wait_webhook", "log", "code_execution", "sheets_append_row", "calendar_create_event", "docs_create_document", "sheets_create_spreadsheet", "drive_download_file", "docs_get_document", "calendar_list_events", "drive_list_files", "sheets_read_range", "gmail_search", "gmail_send", "drive_share_file", "docs_batch_update", "sheets_write_range"].includes(stepType) && !(selectedNode.type === "trigger" && (selectedNode.data?.trigger_type === "scheduled" || selectedNode.data?.trigger_type === "webhook" || selectedNode.data?.trigger_type === "executable_by_workflow")) && (
        <div className="text-xs text-gray-400">
          Configuration for <code className="text-violet-400">{stepType}</code> coming soon.
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  STEP EDITOR MODAL (UNIFIED FOR ALL NODE TYPES)                     */
/* ------------------------------------------------------------------ */
// Step types whose Advanced tab is a curated hand-coded form (keep as-is).
const CURATED_ADVANCED = ['api_call', 'database', 'database_query', 'code_execution', 'condition'];

// wait_webhook: which resume/match fields apply to each match_type. These render
// in the Basic form (conditionally) instead of all-at-once. Fields common to
// every match_type (timeout_*, response_mapping, resume_data_key) stay in Advanced.
const WAIT_WEBHOOK_FIELDS_BY_MATCH = {
  webhook: ['webhook_path', 'webhook_method'],
  whatsapp: ['match_field', 'match_value_template', 'wait_for_more_messages_seconds'],
  email: ['match_field', 'match_value_template'],
  custom: ['webhook_path', 'webhook_method', 'match_field', 'match_value_template', 'match_criteria'],
};
// Every match-specific key — excluded from the generic Advanced tab so those
// fields are never shown regardless of the selected match_type.
const WAIT_WEBHOOK_BASIC_KEYS = [
  'match_type', 'webhook_path', 'webhook_method', 'match_field',
  'match_value_template', 'match_criteria', 'wait_for_more_messages_seconds',
];

// llm_call: keep tuning knobs out of Basic — they belong in Advanced.
const LLM_CALL_ADVANCED_KEYS = ['temperature', 'max_tokens'];

// instagram_comment_reply: integration_id + instagram_business_account_id are
// already covered by the hand-coded Basic panel's integration dropdown —
// don't repeat them as free-text fields in a generic Advanced tab.
const INSTAGRAM_COMMENT_REPLY_ADVANCED_EXCLUDE = ['integration_id', 'instagram_business_account_id'];

const StepEditorModal = ({
  node,
  onBack,
  onSave,
  llmModels = [],
  allNodes = [],
  stepResults = {},
  readOnly = false,
  schema = null
}) => {
  const stepType = node?.data?.step_type || node?.type;
  const config = node?.data?.configuration || {};
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState(config);
  const { currentAccount } = useOrganizations();
  
  // Assistants state (for agent nodes)
  const [assistants, setAssistants] = useState([]);
  const [loadingAssistants, setLoadingAssistants] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  
  // Google Sheets state
  const [spreadsheetMetadata, setSpreadsheetMetadata] = useState(null);
  const [sheetsError, setSheetsError] = useState(null);

  // Google integrations state (for wait_for_human_action)
  const [googleIntegrations, setGoogleIntegrations] = useState([]);
  const [loadingGoogleIntegrations, setLoadingGoogleIntegrations] = useState(false);

  // Meta/Instagram integrations state (for instagram trigger)
  const [metaIntegrations, setMetaIntegrations] = useState([]);
  const [loadingMetaIntegrations, setLoadingMetaIntegrations] = useState(false);

  // Meta/WhatsApp integrations state (for whatsapp_notification step)
  const [whatsappIntegrations, setWhatsappIntegrations] = useState([]);
  const [loadingWhatsappIntegrations, setLoadingWhatsappIntegrations] = useState(false);

  // Zenit-hosted DB tables (for database / database_query steps in zenit mode)
  const [zenitTables, setZenitTables] = useState([]);
  const [loadingZenitTables, setLoadingZenitTables] = useState(false);

  // Callable workflows state (for execute_workflow)
  const [callableWorkflows, setCallableWorkflows] = useState([]);
  const [loadingCallableWorkflows, setLoadingCallableWorkflows] = useState(false);

  // Data selector visibility
  const [dataSelectorVisible, setDataSelectorVisible] = useState(false);
  const lastInputRef = useRef(null);

  // Resolves friendly chip labels for already-saved {{...}} tokens
  const resolveLabel = useCallback(
    (token) => resolveTokenLabel(token, allNodes),
    [allNodes]
  );

  // Load assistants if needed for agent nodes
  useEffect(() => {
    if (stepType === 'agent' || stepType === 'ai_agent') {
      loadAssistantsList();
    }
  }, [stepType, currentAccount?.id]);

  // Load Google integrations for steps that act through a Google account
  // (wait_for_human_action plus the gmail_/drive_/sheets_/docs_/calendar_ steps).
  useEffect(() => {
    const needsGoogle = stepType === 'wait_for_human_action' || isGoogleIntegrationStep(stepType);
    if (!needsGoogle || !currentAccount?.id) return;
    setLoadingGoogleIntegrations(true);
    IntegrationService.listAllIntegrations(currentAccount.id)
      .then((result) => {
        if (result.success && result.data?.google) {
          setGoogleIntegrations(result.data.google);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingGoogleIntegrations(false));
  }, [stepType, currentAccount?.id]);

  // Load Meta integrations (Instagram-enabled) for the Instagram trigger and
  // for the instagram_notification step (DM picker).
  useEffect(() => {
    const isInstagramTrigger = stepType === 'trigger' &&
      (node?.data?.trigger_type === 'instagram_webhook' || node?.data?.trigger_type === 'instagram');
    const isInstagram = isInstagramTrigger || stepType === 'instagram_notification';
    if (!isInstagram || !currentAccount?.id) return;
    setLoadingMetaIntegrations(true);
    IntegrationService.listAllIntegrations(currentAccount.id)
      .then((result) => {
        if (result.success && Array.isArray(result.data?.meta)) {
          setMetaIntegrations(result.data.meta.filter(i => i.platforms_enabled?.instagram === true));
        }
      })
      .catch(() => {})
      .finally(() => setLoadingMetaIntegrations(false));
  }, [stepType, node?.data?.trigger_type, currentAccount?.id]);

  // Load Meta integrations (WhatsApp-enabled) for the WhatsApp notification step
  useEffect(() => {
    if (stepType !== 'whatsapp_notification' || !currentAccount?.id) return;
    setLoadingWhatsappIntegrations(true);
    IntegrationService.listAllIntegrations(currentAccount.id)
      .then((result) => {
        if (result.success && Array.isArray(result.data?.meta)) {
          setWhatsappIntegrations(
            result.data.meta.filter(i => i.platforms_enabled?.whatsapp === true || i.whatsapp_info)
          );
        }
      })
      .catch(() => {})
      .finally(() => setLoadingWhatsappIntegrations(false));
  }, [stepType, currentAccount?.id]);

  // Load Zenit-hosted DB tables for the database / database_query steps.
  // Pulls both SQL and NoSQL so the picker can offer any table the account
  // created in the Database view; each option carries its own database_type.
  useEffect(() => {
    const needsTables = stepType === 'database' || stepType === 'database_query';
    if (!needsTables || !currentAccount?.id) return;
    setLoadingZenitTables(true);
    Promise.all([
      DatabaseService.listTables(currentAccount.id, { databaseType: 'SQL', pageSize: 100 }),
      DatabaseService.listTables(currentAccount.id, { databaseType: 'NOSQL', pageSize: 100 }),
    ])
      .then(([sql, nosql]) => {
        const merged = [
          ...(sql.success ? sql.data?.tables ?? [] : []),
          ...(nosql.success ? nosql.data?.tables ?? [] : []),
        ];
        setZenitTables(merged);
      })
      .catch(() => {})
      .finally(() => setLoadingZenitTables(false));
  }, [stepType, currentAccount?.id]);

  // Load callable workflows for execute_workflow
  useEffect(() => {
    if (stepType !== 'execute_workflow' || !currentAccount?.id) return;
    setLoadingCallableWorkflows(true);
    WorkflowService.listWorkflows({ account_id: currentAccount.id, project_id: node?.data?.project_id, limit: 200 })
      .then((result) => {
        if (result.success) setCallableWorkflows(result.data.workflows || []);
      })
      .catch(() => {})
      .finally(() => setLoadingCallableWorkflows(false));
  }, [stepType, currentAccount?.id]);

  const loadAssistantsList = async () => {
    if (!currentAccount?.id) return;
    
    setLoadingAssistants(true);
    try {
      const result = await AssistantService.getAssistants({ accountId: currentAccount.id });
      if (result.success && Array.isArray(result.data)) {
        setAssistants(result.data);
        // Set selected assistant if node already has one
        if (config.assistant_id) {
          const assistant = result.data.find(a => a.id === config.assistant_id);
          setSelectedAssistant(assistant || null);
        }
      }
    } catch (error) {
      console.error('Error loading assistants:', error);
    } finally {
      setLoadingAssistants(false);
    }
  };

  const handleAssistantChange = (e) => {
    const assistantId = e.target.value;
    const assistant = assistants.find(a => a.id === assistantId);
    setSelectedAssistant(assistant || null);
    handleFieldChange('assistant_id', assistantId);
  };

  const getNodeIcon = () => {
    const icons = {
      agent: '🤖',
      ai_agent: '🤖',
      condition: '🔀',
      api_call: '🌐',
      database: '🗄️',
      database_query: '🔍',
      whatsapp_notification: '💬',
      instagram_notification: '📸',
      wait_duration: '⏱️',
      wait_condition: '⏳',
      wait_webhook: '🪝',
      wait_event: '📡',
      wait_user_input: '👤',
      wait_for_human_action: '🙋',
      execute_workflow: '🔁',
      executable_by_workflow: '🔁',
      log: '📝',
      code_execution: '💻',
      drive_upload: '📤',
      shopify_update_order: '🛒',
      sheets_append_row: '📊',
      sheets_write_range: '📝',
      sheets_create_spreadsheet: '📄',
      calendar_create_event: '📅',
      docs_create_document: '📄',
      drive_create_folder: '📁',
      gmail_send_email: '📧'
    };
    return icons[stepType] || '⚙️';
  };

  const getNodeTitle = () => {
    return node?.data?.label || stepType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getTabs = () => {
    const tabs = [{ id: 'basic', label: 'Basic', icon: '⚙️' }];

    // Add Advanced tab for nodes with a curated advanced form, or any node whose
    // schema exposes optional parameters not shown in the Basic form.
    if (CURATED_ADVANCED.includes(stepType)) {
      tabs.push({ id: 'advanced', label: 'Advanced', icon: '🔧' });
    } else if (getSchemaFields(schema, {
      onlyOptional: true,
      excludeKeys: stepType === 'instagram_comment_reply' ? INSTAGRAM_COMMENT_REPLY_ADVANCED_EXCLUDE : undefined,
    }).length > 0) {
      tabs.push({ id: 'advanced', label: 'Advanced', icon: '🔧' });
    }

    // Add Testing tab for nodes that can be tested
    if (['api_call', 'database_query', 'code_execution'].includes(stepType)) {
      tabs.push({ id: 'testing', label: 'Testing', icon: '🧪' });
    }

    return tabs;
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave({ ...node, data: { ...node.data, configuration: formData } });
  };

  const handleSpreadsheetIdChange = async (spreadsheetId) => {
    handleFieldChange('spreadsheet_id', spreadsheetId);
    
    if (!spreadsheetId || !currentAccount?.id) {
      setSpreadsheetMetadata(null);
      return;
    }

    try {
      setSheetsError(null);
      const result = await GoogleService.getSpreadsheet(spreadsheetId, { includeGridData: false });
      
      if (result.success && result.data) {
        setSpreadsheetMetadata(result.data);
      } else {
        setSheetsError(result.error || 'Failed to load spreadsheet metadata');
      }
    } catch (error) {
      console.error('Error loading spreadsheet metadata:', error);
      setSheetsError('Unable to connect to Google Sheets');
    }
  };

  const tabs = getTabs();

  return (
    <motion.div
      className="absolute inset-0 z-50 bg-[#09090b] flex flex-col rounded-xl overflow-hidden"
      initial={{ opacity: 0, scale: 0.97, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97, y: 12 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Header */}
      <div className="h-11 shrink-0 border-b border-white/10 flex items-center gap-3 px-4">
        <button
          onClick={onBack}
          className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4 text-gray-400" />
        </button>
        <div className="h-4 w-px bg-white/10" />
        <div className="w-5 h-5 rounded bg-gradient-to-br from-violet-500 to-blue-500 grid place-items-center shrink-0">
          <span className="text-sm">{getNodeIcon()}</span>
        </div>
        <span className="text-sm font-semibold text-white">{getNodeTitle()}</span>
        <span className="text-xs text-gray-500">/ Configuration</span>
        <div className="flex-1" />
        {readOnly ? (
          <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded-lg">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/></svg>
            View only
          </span>
        ) : (
          <button
            onClick={handleSave}
            className="px-4 py-1.5 text-xs font-semibold bg-primary hover:bg-violet-500 text-white rounded-lg transition-colors"
          >
            Save changes
          </button>
        )}
      </div>

      {/* Body */}
      <div
        className="flex-1 flex min-h-0"
        onFocusCapture={(e) => {
          const isTokenField = e.target.dataset?.tokenfield === 'true';
          if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || isTokenField) {
            if (!e.target.closest('[data-dataselector]')) {
              lastInputRef.current = e.target;
            }
            setDataSelectorVisible(true);
          }
        }}
        onBlurCapture={(e) => {
          if (e.relatedTarget?.closest?.('[data-dataselector]')) return;
          if (!e.currentTarget.contains(e.relatedTarget)) {
            setDataSelectorVisible(false);
          }
        }}
      >
        {/* Floating Data Selector — appears as a separate window when an input is focused */}
        <DataSelector
          nodes={allNodes}
          selectedNodeId={node?.id}
          visible={dataSelectorVisible}
          stepResults={stepResults}
          lastInputRef={lastInputRef}
          onClose={() => setDataSelectorVisible(false)}
        />

        {/* Read-only overlay banner */}
        {readOnly && (
          <div className="absolute top-11 inset-x-0 z-10 pointer-events-none">
            <div className="mx-4 mt-3 px-3 py-2 bg-amber-400/10 border border-amber-400/20 rounded-lg flex items-center gap-2">
              <svg className="w-3.5 h-3.5 text-amber-400 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/></svg>
              <span className="text-xs text-amber-400">Published version — create a draft to make changes</span>
            </div>
          </div>
        )}

        {/* Tabs sidebar */}
        {tabs.length > 1 && (
          <div className="w-52 shrink-0 border-r border-white/10 flex flex-col">
            <div className="px-4 pt-4 pb-2 text-[10px] font-bold text-gray-500 uppercase tracking-widest">
              Sections
            </div>
            <div className="flex-1 overflow-y-auto px-2 space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-xs transition-colors ${
                    activeTab === tab.id
                      ? 'bg-violet-600/20 border border-violet-500/30 text-violet-200'
                      : 'border border-transparent text-gray-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span>{tab.icon}</span>
                    <span className="font-medium">{tab.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <fieldset disabled={readOnly} className={readOnly ? 'opacity-70 pointer-events-none select-text' : ''}>
          {activeTab === 'basic' && (
            <StepBasicConfig
              stepType={stepType}
              config={formData}
              node={node}
              onFieldChange={handleFieldChange}
              assistants={assistants}
              loadingAssistants={loadingAssistants}
              onAssistantChange={handleAssistantChange}
              selectedAssistant={selectedAssistant}
              spreadsheetMetadata={spreadsheetMetadata}
              sheetsError={sheetsError}
              onSpreadsheetIdChange={handleSpreadsheetIdChange}
              currentAccount={currentAccount}
              resolveLabel={resolveLabel}
              googleIntegrations={googleIntegrations}
              loadingGoogleIntegrations={loadingGoogleIntegrations}
              callableWorkflows={callableWorkflows}
              loadingCallableWorkflows={loadingCallableWorkflows}
              metaIntegrations={metaIntegrations}
              loadingMetaIntegrations={loadingMetaIntegrations}
              whatsappIntegrations={whatsappIntegrations}
              loadingWhatsappIntegrations={loadingWhatsappIntegrations}
              zenitTables={zenitTables}
              loadingZenitTables={loadingZenitTables}
              llmModels={llmModels}
              schema={schema}
            />
          )}

          {activeTab === 'advanced' && (
            CURATED_ADVANCED.includes(stepType) ? (
              <StepAdvancedConfig
                stepType={stepType}
                config={formData}
                onFieldChange={handleFieldChange}
              />
            ) : (
              <SchemaForm
                schema={schema}
                config={formData}
                onFieldChange={handleFieldChange}
                onlyOptional
                includeKeys={stepType === 'llm_call' ? LLM_CALL_ADVANCED_KEYS : undefined}
                excludeKeys={
                  stepType === 'wait_webhook' ? WAIT_WEBHOOK_BASIC_KEYS :
                  stepType === 'instagram_comment_reply' ? INSTAGRAM_COMMENT_REPLY_ADVANCED_EXCLUDE :
                  undefined
                }
              />
            )
          )}
          
          {activeTab === 'testing' && (
            <StepTestingConfig
              stepType={stepType}
              config={formData}
              node={node}
            />
          )}
          </fieldset>
        </div>
      </div>
    </motion.div>
  );
};

/* ------------------------------------------------------------------ */
/*  STEP BASIC CONFIG TAB                                              */
/* ------------------------------------------------------------------ */
// Google action steps run through a connected Google account. Map each step's
// prefix to the service_type used to filter which integrations to offer.
const GOOGLE_SERVICE_LABEL = {
  gmail: 'Gmail',
  drive: 'Google Drive',
  sheets: 'Google Sheets',
  docs: 'Google Docs',
  calendar: 'Google Calendar',
};

const googleServiceForStep = (stepType = '') => {
  if (stepType.startsWith('gmail_')) return 'gmail';
  if (stepType.startsWith('drive_')) return 'drive';
  if (stepType.startsWith('sheets_')) return 'sheets';
  if (stepType.startsWith('docs_')) return 'docs';
  if (stepType.startsWith('calendar_')) return 'calendar';
  return null;
};

const isGoogleIntegrationStep = (stepType) => googleServiceForStep(stepType) !== null;

// Dropdown to pick a Zenit-hosted table for database / database_query steps.
// Mirrors the Google integration picker: lists the tables the account created
// in the Database view, and writes both `table_id` and `database_type` (the
// step schema expects lowercase `sql`/`nosql`) when one is selected.
const ZenitTablePicker = ({ config, onFieldChange, tables = [], loading = false }) => {
  return (
    <div>
      <label className="block text-sm font-semibold text-white mb-2">
        Table <span className="text-red-400">*</span>
      </label>
      {loading ? (
        <div className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-gray-400 flex items-center gap-2 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading tables...
        </div>
      ) : tables.length > 0 ? (
        <StyledSelect
          value={config.table_id || ''}
          placeholder="Select a table..."
          onChange={(selectedId) => {
            onFieldChange('table_id', selectedId || undefined);
            const selected = tables.find((t) => t.table_id === selectedId);
            if (selected?.database_type) {
              onFieldChange('database_type', String(selected.database_type).toLowerCase());
            }
          }}
          options={tables.map((t) => ({
            value: t.table_id,
            label: `${t.name} (${String(t.database_type || 'sql').toUpperCase()})`,
          }))}
        />
      ) : (
        <>
          <input
            type="text"
            className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
            placeholder="Table ID (UUID)"
            value={config.table_id || ''}
            onChange={(e) => onFieldChange('table_id', e.target.value || undefined)}
          />
          <p className="text-xs text-amber-400 mt-2">
            No tables found. Create one in the Database view.
          </p>
        </>
      )}
      <p className="text-xs text-gray-500 mt-2">
        Zenit-hosted table this step will operate on.
      </p>
    </div>
  );
};

// Coerce a free-text DB value into a JS value: valid JSON (number, bool,
// object, array) is parsed; anything else (incl. {{template}} tokens) stays a
// string. Lets users type 42, true, ["a","b"] or {{context:step_1.id}}.
const coerceDbValue = (raw) => {
  const t = String(raw ?? '').trim();
  if (t === '') return '';
  try {
    return JSON.parse(t);
  } catch {
    return raw;
  }
};

// Column→value editor for insert/update operations. Writes `config.values`
// as an object. Local row state keeps key order while typing.
const DbValuesEditor = ({ config, onFieldChange }) => {
  const [rows, setRows] = useState(() => {
    const v = config.values;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const entries = Object.entries(v);
      if (entries.length) {
        return entries.map(([k, val]) => ({
          k,
          v: typeof val === 'string' ? val : JSON.stringify(val),
        }));
      }
    }
    return [{ k: '', v: '' }];
  });

  const sync = (next) => {
    setRows(next);
    const obj = {};
    next.forEach(({ k, v }) => {
      if (String(k).trim() === '') return;
      obj[String(k).trim()] = coerceDbValue(v);
    });
    onFieldChange('values', Object.keys(obj).length ? obj : undefined);
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-white mb-2">
        Values <span className="text-red-400">*</span>
      </label>
      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={i} className="flex gap-2">
            <input
              type="text"
              className="flex-1 px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-violet-500/50"
              placeholder="column"
              value={row.k}
              onChange={(e) => sync(rows.map((r, idx) => (idx === i ? { ...r, k: e.target.value } : r)))}
            />
            <input
              type="text"
              className="flex-[2] px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-violet-500/50"
              placeholder="value or {{context:step_1.field}}"
              value={row.v}
              onChange={(e) => sync(rows.map((r, idx) => (idx === i ? { ...r, v: e.target.value } : r)))}
            />
            <button
              type="button"
              className="px-2 text-gray-500 hover:text-red-400"
              onClick={() => {
                const next = rows.filter((_, idx) => idx !== i);
                sync(next.length ? next : [{ k: '', v: '' }]);
              }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="mt-2 text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1"
        onClick={() => sync([...rows, { k: '', v: '' }])}
      >
        <Plus className="w-3 h-3" /> Add column
      </button>
      <p className="text-xs text-gray-500 mt-2">
        Column-value pairs to write. Values accept template variables.
      </p>
    </div>
  );
};

const DB_FILTER_OPERATORS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'like', 'in', 'is_null', 'is_not_null'];
const DB_FILTER_NO_VALUE = new Set(['is_null', 'is_not_null']);

// WHERE-condition editor for select/update/delete. Writes `config.filters`
// (array of {column, operator, value}) and `config.filter_logic`.
const DbFiltersEditor = ({ config, onFieldChange, required = false }) => {
  const filters = Array.isArray(config.filters) ? config.filters : [];
  const setFilters = (next) => onFieldChange('filters', next.length ? next : undefined);
  const updateRow = (i, patch) => setFilters(filters.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));

  return (
    <div>
      <label className="block text-sm font-semibold text-white mb-2">
        Filters (WHERE) {required && <span className="text-red-400">*</span>}
      </label>
      {filters.length === 0 ? (
        <p className="text-xs text-gray-500 mb-2">No conditions yet.</p>
      ) : (
        <div className="space-y-2">
          {filters.map((f, i) => {
            const noValue = DB_FILTER_NO_VALUE.has(f.operator);
            return (
              <div key={i} className="flex gap-2">
                <input
                  type="text"
                  className="flex-1 px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-violet-500/50"
                  placeholder="column"
                  value={f.column || ''}
                  onChange={(e) => updateRow(i, { column: e.target.value })}
                />
                <StyledSelect
                  triggerClassName="w-auto min-w-[7rem] px-2 py-2 rounded-lg"
                  value={f.operator || 'eq'}
                  onChange={(op) => {
                    const patch = { operator: op };
                    if (DB_FILTER_NO_VALUE.has(op)) patch.value = null;
                    updateRow(i, patch);
                  }}
                  options={DB_FILTER_OPERATORS}
                />
                <input
                  type="text"
                  className="flex-[2] px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 disabled:opacity-30"
                  placeholder={noValue ? '—' : 'value or {{context:...}}'}
                  disabled={noValue}
                  value={noValue ? '' : (f.value ?? '')}
                  onChange={(e) => updateRow(i, { value: coerceDbValue(e.target.value) })}
                />
                <button
                  type="button"
                  className="px-2 text-gray-500 hover:text-red-400"
                  onClick={() => setFilters(filters.filter((_, idx) => idx !== i))}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex items-center gap-3 mt-2">
        <button
          type="button"
          className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1"
          onClick={() => setFilters([...filters, { column: '', operator: 'eq', value: '' }])}
        >
          <Plus className="w-3 h-3" /> Add condition
        </button>
        {filters.length > 1 && (
          <StyledSelect
            triggerClassName="w-auto min-w-[10rem] px-2 py-1 rounded-lg text-xs"
            value={config.filter_logic || 'and'}
            onChange={(v) => onFieldChange('filter_logic', v)}
            options={[
              { value: 'and', label: 'Match ALL (AND)' },
              { value: 'or', label: 'Match ANY (OR)' },
            ]}
          />
        )}
      </div>
      {required && filters.length === 0 && (
        <p className="text-xs text-amber-400 mt-2">
          At least one filter is required to prevent affecting every row.
        </p>
      )}
    </div>
  );
};

// Extra options shown only for the select operation.
const DbSelectOptions = ({ config, onFieldChange }) => {
  const columnsText = Array.isArray(config.columns)
    ? config.columns.filter((c) => c !== '*').join(', ')
    : '';
  return (
    <>
      <div>
        <label className="block text-sm font-semibold text-white mb-2">Columns</label>
        <input
          type="text"
          className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
          placeholder="id, name, email  (leave empty for all)"
          value={columnsText}
          onChange={(e) => {
            const cols = e.target.value.split(',').map((c) => c.trim()).filter(Boolean);
            onFieldChange('columns', cols.length ? cols : undefined);
          }}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-white mb-2">Sort By</label>
          <input
            type="text"
            className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
            placeholder="created_at"
            value={config.sort_by || ''}
            onChange={(e) => onFieldChange('sort_by', e.target.value || undefined)}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-white mb-2">Direction</label>
          <StyledSelect
            value={config.sort_direction || 'asc'}
            onChange={(v) => onFieldChange('sort_direction', v)}
            options={['asc', 'desc']}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-white mb-2">Limit</label>
          <input
            type="number"
            className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
            placeholder="100"
            value={config.limit ?? ''}
            onChange={(e) => onFieldChange('limit', e.target.value === '' ? undefined : parseInt(e.target.value, 10))}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-white mb-2">Offset</label>
          <input
            type="number"
            className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
            placeholder="0"
            value={config.offset ?? ''}
            onChange={(e) => onFieldChange('offset', e.target.value === '' ? undefined : parseInt(e.target.value, 10))}
          />
        </div>
      </div>
    </>
  );
};

const StepBasicConfig = ({
  stepType,
  config,
  node,
  onFieldChange,
  assistants,
  loadingAssistants,
  onAssistantChange,
  selectedAssistant,
  spreadsheetMetadata,
  sheetsError,
  onSpreadsheetIdChange,
  currentAccount,
  resolveLabel,
  googleIntegrations = [],
  loadingGoogleIntegrations = false,
  callableWorkflows = [],
  loadingCallableWorkflows = false,
  metaIntegrations = [],
  loadingMetaIntegrations = false,
  whatsappIntegrations = [],
  loadingWhatsappIntegrations = false,
  zenitTables = [],
  loadingZenitTables = false,
  llmModels = [],
  schema = null
}) => {
  return (
    <div className="space-y-6">
      {/* AI Agent */}
      {(stepType === 'agent' || stepType === 'ai_agent') && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Agent <span className="text-red-400">*</span>
            </label>
            {loadingAssistants ? (
              <div className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-gray-400 flex items-center gap-2 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </div>
            ) : (
              <StyledSelect
                value={selectedAssistant?.id || ''}
                placeholder="Select agent..."
                onChange={(v) => onAssistantChange({ target: { value: v } })}
                options={assistants.map((a) => ({ value: a.id, label: a.name }))}
              />
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Execution Mode</label>
            <StyledSelect
              value={config.execution_mode || 'sequential'}
              onChange={(v) => onFieldChange('execution_mode', v)}
              options={[
                { value: 'sequential', label: 'Sequential - Tasks run one after another' },
                { value: 'parallel', label: 'Parallel - Tasks run concurrently' },
              ]}
            />
          </div>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <p className="text-sm text-blue-300">
              💡 Double-click the agent node to configure tasks
            </p>
          </div>
        </>
      )}

      {/* Webhook Trigger */}
      {stepType === 'trigger' && node?.data?.trigger_type === 'webhook' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Path <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20"
              placeholder="https://app.zenit/webhooks/mi-workflow"
              value={config.path || ''}
              onChange={(e) => onFieldChange('path', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">URL path for the webhook endpoint</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">HTTP Method</label>
            <StyledSelect
              value={config.method || 'POST'}
              onChange={(v) => onFieldChange('method', v)}
              options={['GET', 'POST', 'PUT', 'PATCH', 'DELETE']}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Content Type</label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20"
              placeholder="application/json"
              value={config.content_type || ''}
              onChange={(e) => onFieldChange('content_type', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">Expected Content-Type header (default: application/json)</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Authentication</label>
            <StyledSelect
              value={config.auth?.type || 'none'}
              onChange={(authType) => {
                const authConfig = authType === 'none' ? undefined : { type: authType };
                onFieldChange('auth', authConfig);
              }}
              options={[
                { value: 'none', label: 'None' },
                { value: 'signature', label: 'Signature' },
                { value: 'api_key', label: 'API Key' },
                { value: 'bearer', label: 'Bearer Token' },
                { value: 'basic', label: 'Basic Auth' },
              ]}
            />
            <p className="text-xs text-gray-500 mt-2">Authentication scheme for the endpoint</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Rate Limit (per minute)</label>
            <input
              type="number"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20"
              placeholder="100"
              min="0"
              value={config.rate_limit_per_minute || ''}
              onChange={(e) => onFieldChange('rate_limit_per_minute', e.target.value ? parseInt(e.target.value) : undefined)}
            />
            <p className="text-xs text-gray-500 mt-2">Maximum requests per minute (optional)</p>
          </div>

          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 grid place-items-center shrink-0">
                <span className="text-xl">🔗</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-400 mb-1">Webhook URL:</p>
                <p className="text-sm font-semibold text-orange-300 font-mono break-all">
                  {config.path ? `https://api.example.com${config.path}` : 'Configure path above'}
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Instagram Trigger */}
      {stepType === 'trigger' && (node?.data?.trigger_type === 'instagram_webhook' || node?.data?.trigger_type === 'instagram') && (
        <>
          <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-pink-500/20 grid place-items-center shrink-0">
                <span className="text-xl">📷</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-pink-300 mb-1">Instagram Messaging</p>
                <p className="text-xs text-gray-400">
                  Fires on incoming DMs, reactions, and other Instagram Messaging events.
                  Path, method and signature verification are handled by the platform.
                </p>
              </div>
            </div>
          </div>

          {/* Instagram integration selector */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Instagram Integration <span className="text-red-400">*</span>
            </label>
            {loadingMetaIntegrations ? (
              <div className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-gray-400 flex items-center gap-2 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading Instagram integrations...
              </div>
            ) : metaIntegrations.length > 0 ? (
              <StyledSelect
                value={config.integration_id || ''}
                placeholder="Select Instagram integration..."
                onChange={(selectedId) => {
                  onFieldChange('integration_id', selectedId);
                  const selected = metaIntegrations.find(i => i.id === selectedId);
                  const igAccountId = selected?.instagram_info?.account_ids?.[0];
                  if (igAccountId) onFieldChange('instagram_business_account_id', igAccountId);
                }}
                options={metaIntegrations.map((integration) => {
                  const igInfo = integration.instagram_info || {};
                  const displayName = igInfo.username
                    ? `@${igInfo.username}`
                    : (igInfo.account_ids?.[0]
                        ? `Instagram · ${igInfo.account_ids[0]}`
                        : (integration.business_name || integration.name || integration.id));
                  return { value: integration.id, label: displayName };
                })}
              />
            ) : (
              <>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-pink-500/50 focus:ring-2 focus:ring-pink-500/20"
                  placeholder="Meta integration ID"
                  value={config.integration_id || ''}
                  onChange={(e) => onFieldChange('integration_id', e.target.value)}
                />
                <p className="text-xs text-amber-400 mt-2">No Instagram integrations found. Connect one in Integrations → Meta.</p>
              </>
            )}
            <p className="text-xs text-gray-500 mt-2">Meta integration used to retrieve the access token.</p>
          </div>

          {/* Instagram Business Account ID */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Instagram Business Account ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-pink-500/50 focus:ring-2 focus:ring-pink-500/20"
              placeholder="17841416557284103"
              value={config.instagram_business_account_id || ''}
              onChange={(e) => onFieldChange('instagram_business_account_id', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">Instagram Business Account ID from Meta Graph API.</p>
          </div>

          {/* Event types */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Event Types</label>
            <div className="space-y-2">
              {[
                { value: 'messages', label: 'Messages (Incoming DMs)' },
                { value: 'comments', label: 'Comments (Public post/reel comments)' },
                { value: 'messaging_postbacks', label: 'Postbacks (Button / Icebreaker taps)' },
                { value: 'message_reactions', label: 'Message Reactions' },
                { value: 'messaging_seen', label: 'Message Seen' },
                { value: 'messaging_referral', label: 'Referrals' },
              ].map((opt) => {
                const selected = Array.isArray(config.event_types) ? config.event_types : [];
                const checked = selected.includes(opt.value);
                return (
                  <label key={opt.value} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = checked
                          ? selected.filter(v => v !== opt.value)
                          : [...selected, opt.value];
                        onFieldChange('event_types', next);
                      }}
                      className="accent-pink-500"
                    />
                    {opt.label}
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-2">Leave all unchecked to default to incoming messages.</p>
          </div>
        </>
      )}

      {/* Condition */}
      {stepType === 'condition' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Left Value <span className="text-red-400">*</span>
            </label>
            <input 
              type="text" 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="{{context:step_3.result}}" 
              value={config.left || ''} 
              onChange={(e) => onFieldChange('left', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">Use {'{{context:step_name.field}}'} to reference previous step outputs</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Operator <span className="text-red-400">*</span>
            </label>
            <StyledSelect
              value={config.operator || 'eq'}
              onChange={(v) => onFieldChange('operator', v)}
              options={[
                { value: 'eq', label: 'equals' },
                { value: 'ne', label: 'not equals' },
                { value: 'gt', label: 'greater than' },
                { value: 'lt', label: 'less than' },
                { value: 'gte', label: 'greater than or equal' },
                { value: 'lte', label: 'less than or equal' },
                { value: 'contains', label: 'contains' },
                { value: 'not_contains', label: 'not contains' },
                { value: 'starts_with', label: 'starts with' },
                { value: 'ends_with', label: 'ends with' },
                { value: 'is_true', label: 'is true' },
                { value: 'is_false', label: 'is false' },
                { value: 'is_empty', label: 'is empty' },
                { value: 'not_empty', label: 'not empty' },
                { value: 'exists', label: 'exists' },
                { value: 'not_exists', label: 'not exists' },
                { value: 'is_null', label: 'is null' },
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Right Value</label>
            <input 
              type="text" 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="expected_value" 
              value={config.right || ''} 
              onChange={(e) => onFieldChange('right', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">Leave empty for is_true/is_false/is_null operators</p>
          </div>
        </>
      )}

      {/* API Call */}
      {stepType === 'api_call' && (
        <ApiCallFields key={node?.id} config={config} onFieldChange={onFieldChange} />
      )}

      {/* Database */}
      {stepType === 'database' && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Operation</label>
              <StyledSelect
                value={config.operation || 'select'}
                onChange={(v) => onFieldChange('operation', v)}
                options={['select', 'insert', 'update', 'delete']}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Connection</label>
              <StyledSelect
                value={config.connection_mode || 'zenit'}
                onChange={(v) => onFieldChange('connection_mode', v)}
                options={[
                  { value: 'zenit', label: 'Zenit DB' },
                  { value: 'external', label: 'External' },
                ]}
              />
            </div>
          </div>
          {(config.connection_mode || 'zenit') === 'zenit' ? (
            <ZenitTablePicker
              config={config}
              onFieldChange={onFieldChange}
              tables={zenitTables}
              loading={loadingZenitTables}
            />
          ) : (
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Table Name</label>
              <input
                type="text"
                className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
                placeholder="users"
                value={config.table_name || ''}
                onChange={(e) => onFieldChange('table_name', e.target.value)}
              />
            </div>
          )}

          {/* Operation-specific fields. select → query options + optional
              filters; insert → values; update → values + required filters;
              delete → required filters. */}
          {(config.operation || 'select') === 'select' && (
            <>
              <DbSelectOptions config={config} onFieldChange={onFieldChange} />
              <DbFiltersEditor config={config} onFieldChange={onFieldChange} />
            </>
          )}
          {(config.operation || 'select') === 'insert' && (
            <DbValuesEditor config={config} onFieldChange={onFieldChange} />
          )}
          {(config.operation || 'select') === 'update' && (
            <>
              <DbValuesEditor config={config} onFieldChange={onFieldChange} />
              <DbFiltersEditor config={config} onFieldChange={onFieldChange} required />
            </>
          )}
          {(config.operation || 'select') === 'delete' && (
            <DbFiltersEditor config={config} onFieldChange={onFieldChange} required />
          )}
        </>
      )}

      {/* Instagram Notification (DM) */}
      {stepType === 'instagram_notification' && (
        <>
          {/* Instagram integration selector — dropdown only when there are
              Instagram-enabled Meta integrations; otherwise a manual ID input. */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Instagram Integration <span className="text-red-400">*</span>
            </label>
            {loadingMetaIntegrations ? (
              <div className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-gray-400 flex items-center gap-2 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading Instagram integrations...
              </div>
            ) : metaIntegrations.length > 0 ? (
              <StyledSelect
                value={config.integration_id || ''}
                placeholder="Select Instagram integration..."
                onChange={(selectedId) => {
                  onFieldChange('integration_id', selectedId);
                  // Auto-fill the sender's IG business account from the chosen integration.
                  const selected = metaIntegrations.find(i => i.id === selectedId);
                  const igAccountId = selected?.instagram_info?.account_ids?.[0];
                  if (igAccountId) onFieldChange('instagram_business_account_id', igAccountId);
                }}
                options={metaIntegrations.map((integration) => {
                  const igInfo = integration.instagram_info || {};
                  const displayName = igInfo.username
                    ? `@${igInfo.username}`
                    : (igInfo.account_ids?.[0]
                        ? `Instagram · ${igInfo.account_ids[0]}`
                        : (integration.business_name || integration.name || integration.id));
                  return { value: integration.id, label: displayName };
                })}
              />
            ) : (
              <>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-pink-500/50 focus:ring-2 focus:ring-pink-500/20"
                  placeholder="Meta integration ID"
                  value={config.integration_id || ''}
                  onChange={(e) => onFieldChange('integration_id', e.target.value)}
                />
                <p className="text-xs text-amber-400 mt-2">No Instagram integrations found. Connect one in Integrations → Meta.</p>
              </>
            )}
            <p className="text-xs text-gray-500 mt-2">Meta integration used to send the DM.</p>
          </div>

          {/* Recipient IG user ID */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Recipient IG User ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-pink-500/50 focus:ring-2 focus:ring-pink-500/20"
              placeholder="17841405822304914 or {{trigger:sender_id}}"
              value={config.recipient_ig_user_id || ''}
              onChange={(e) => onFieldChange('recipient_ig_user_id', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">Instagram-scoped ID of the user receiving the DM.</p>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Message</label>
            <textarea
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-pink-500/50 focus:ring-2 focus:ring-pink-500/20"
              rows={4}
              placeholder="Your message here..."
              value={config.message_content || ''}
              onChange={(e) => onFieldChange('message_content', e.target.value)}
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Type</label>
            <StyledSelect
              value={config.message_type || 'text'}
              onChange={(v) => onFieldChange('message_type', v)}
              options={['text', 'reaction', 'read']}
            />
            <p className="text-xs text-gray-500 mt-2">For image/video DMs, set Media URL + Media Type in the Advanced tab.</p>
          </div>
        </>
      )}

      {/* Instagram Comment Reply */}
      {stepType === 'instagram_comment_reply' && (
        <>
          {/* Instagram integration selector — dropdown only when there are
              Instagram-enabled Meta integrations; otherwise a manual ID input. */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Instagram Integration <span className="text-red-400">*</span>
            </label>
            {loadingMetaIntegrations ? (
              <div className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-gray-400 flex items-center gap-2 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading Instagram integrations...
              </div>
            ) : metaIntegrations.length > 0 ? (
              <StyledSelect
                value={config.integration_id || ''}
                placeholder="Select Instagram integration..."
                onChange={(selectedId) => {
                  onFieldChange('integration_id', selectedId);
                  // Auto-fill the sender's IG business account from the chosen integration.
                  const selected = metaIntegrations.find(i => i.id === selectedId);
                  const igAccountId = selected?.instagram_info?.account_ids?.[0];
                  if (igAccountId) onFieldChange('instagram_business_account_id', igAccountId);
                }}
                options={metaIntegrations.map((integration) => {
                  const igInfo = integration.instagram_info || {};
                  const displayName = igInfo.username
                    ? `@${igInfo.username}`
                    : (igInfo.account_ids?.[0]
                        ? `Instagram · ${igInfo.account_ids[0]}`
                        : (integration.business_name || integration.name || integration.id));
                  return { value: integration.id, label: displayName };
                })}
              />
            ) : (
              <>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-pink-500/50 focus:ring-2 focus:ring-pink-500/20"
                  placeholder="Meta integration ID"
                  value={config.integration_id || ''}
                  onChange={(e) => onFieldChange('integration_id', e.target.value)}
                />
                <p className="text-xs text-amber-400 mt-2">No Instagram integrations found. Connect one in Integrations → Meta.</p>
              </>
            )}
            <p className="text-xs text-gray-500 mt-2">Meta integration used to publish the reply.</p>
          </div>

          {/* Comment ID */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Comment ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-pink-500/50 focus:ring-2 focus:ring-pink-500/20"
              placeholder="{{trigger:entry.0.changes.0.value.id}}"
              value={config.comment_id || ''}
              onChange={(e) => onFieldChange('comment_id', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">
              ID of the Instagram comment to reply to — from the comments webhook event upstream in this workflow.
            </p>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Reply Message <span className="text-red-400">*</span>
            </label>
            <textarea
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-pink-500/50 focus:ring-2 focus:ring-pink-500/20"
              rows={4}
              placeholder="Thanks for your comment! {{context:responder_con_ai.response}}"
              value={config.message || ''}
              onChange={(e) => onFieldChange('message', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">Public reply text — max 2200 characters (Instagram's comment limit).</p>
          </div>
        </>
      )}

      {/* WhatsApp Notification */}
      {stepType === 'whatsapp_notification' && (
        <>
          {/* WhatsApp integration selector */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              WhatsApp Integration <span className="text-red-400">*</span>
            </label>
            {loadingWhatsappIntegrations ? (
              <div className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-gray-400 flex items-center gap-2 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading WhatsApp integrations...
              </div>
            ) : whatsappIntegrations.length > 0 ? (
              <StyledSelect
                value={config.integration_id || ''}
                placeholder="Select WhatsApp integration..."
                onChange={(selectedId) => {
                  onFieldChange('integration_id', selectedId);
                  // Auto-rellenar el phone_number_id remitente desde la integración elegida.
                  const selected = whatsappIntegrations.find(i => i.id === selectedId);
                  const phoneId = selected?.whatsapp_info?.phone_number_id;
                  if (phoneId) onFieldChange('phone_number_id', phoneId);
                }}
                options={whatsappIntegrations.map((integration) => {
                  const info = integration.whatsapp_info || {};
                  const displayName = info.verified_name || integration.business_name || integration.name || integration.id;
                  const phone = info.phone_number;
                  return { value: integration.id, label: phone ? `${displayName} (+${phone})` : displayName };
                })}
              />
            ) : (
              <>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20"
                  placeholder="Meta integration ID"
                  value={config.integration_id || ''}
                  onChange={(e) => onFieldChange('integration_id', e.target.value)}
                />
                <p className="text-xs text-amber-400 mt-2">No WhatsApp integrations found. Connect one in Integrations → Meta.</p>
              </>
            )}
            <p className="text-xs text-gray-500 mt-2">Meta integration used to send the message.</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Recipient <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="+1234567890"
              value={config.recipient || ''}
              onChange={(e) => onFieldChange('recipient', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Message</label>
            <textarea
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              rows={4}
              placeholder="Your message here..."
              value={config.message_content || ''}
              onChange={(e) => onFieldChange('message_content', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Type</label>
            <StyledSelect
              value={config.message_type || 'text'}
              onChange={(v) => onFieldChange('message_type', v)}
              options={['text', 'template', 'image', 'document']}
            />
          </div>

          {/* Campos de plantilla — solo cuando el tipo es "template" */}
          {config.message_type === 'template' && (
            <>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Template Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
                  placeholder="order_confirmation"
                  value={config.template_name || ''}
                  onChange={(e) => onFieldChange('template_name', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Template Language</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
                  placeholder="en"
                  value={config.template_language || ''}
                  onChange={(e) => onFieldChange('template_language', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Header Image URL</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
                  placeholder="https://example.com/banner.jpg or {{input:...}}"
                  value={config.header_image_url || ''}
                  onChange={(e) => onFieldChange('header_image_url', e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-2">Solo si la plantilla tiene un header de tipo imagen.</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Dynamic URL Button</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20"
                  placeholder="order/12345 or {{input:webhook_payload.order_id}}"
                  value={
                    Array.isArray(config.template_button_parameters)
                      ? (config.template_button_parameters[0]?.value ?? '')
                      : (typeof config.template_button_parameters === 'string' ? config.template_button_parameters : '')
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    // Solo el sufijo dinámico; se guarda con la forma amigable que espera
                    // el backend: [{ sub_type, index, value }] (sub_type/index con defaults).
                    onFieldChange(
                      'template_button_parameters',
                      v.trim() ? [{ sub_type: 'url', index: '0', value: v }] : null
                    );
                  }}
                />
                <p className="text-xs text-gray-500 mt-2">
                  Valor de la variable del botón URL: solo el sufijo que reemplaza el {'{{1}}'} del botón (no la URL completa).
                </p>
              </div>
            </>
          )}
        </>
      )}

      {/* Wait Duration */}
      {stepType === 'wait_duration' && (
        <div>
          <label className="block text-sm font-semibold text-white mb-2">Duration (seconds)</label>
          <input 
            type="number" 
            className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
            value={config.duration_seconds || 60}
            onChange={(e) => onFieldChange('duration_seconds', parseInt(e.target.value))}
          />
        </div>
      )}

      {/* Wait Condition */}
      {stepType === 'wait_condition' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Condition</label>
            <input 
              type="text" 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="status == 'completed'"
              value={config.condition || ''}
              onChange={(e) => onFieldChange('condition', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Timeout (s)</label>
            <input 
              type="number" 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              value={config.timeout_seconds || 300}
              onChange={(e) => onFieldChange('timeout_seconds', parseInt(e.target.value))}
            />
          </div>
        </>
      )}

      {/* Wait Webhook */}
      {stepType === 'wait_webhook' && (() => {
        const matchType = config.match_type || 'webhook';
        const fields = WAIT_WEBHOOK_FIELDS_BY_MATCH[matchType] || [];
        return (
          <>
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Match Type</label>
              <StyledSelect
                value={matchType}
                onChange={(v) => onFieldChange('match_type', v)}
                options={['webhook', 'whatsapp', 'email', 'custom']}
              />
              <p className="text-xs text-gray-500 mt-2">
                Determines which resume/match fields apply below.
              </p>
            </div>

            {/* Fields specific to the selected match_type (driven by the catalog schema) */}
            <SchemaForm
              schema={schema}
              config={config}
              onFieldChange={onFieldChange}
              includeKeys={fields}
              emptyHint="This match type needs no extra fields."
            />
          </>
        );
      })()}

      {/* Log */}
      {stepType === 'log' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Message</label>
            <textarea 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              rows={4}
              placeholder="{{context:result}}"
              value={config.message || ''}
              onChange={(e) => onFieldChange('message', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Level</label>
            <StyledSelect
              value={config.level || 'info'}
              onChange={(v) => onFieldChange('level', v)}
              options={['debug', 'info', 'warning', 'error']}
            />
          </div>
        </>
      )}

      {/* Code Execution */}
      {stepType === 'code_execution' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Python Code</label>
            <textarea
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              rows={8}
              placeholder="def run(inputs):\n    return {'result': inputs['value'] * 2}"
              value={config.code || ''}
              onChange={(e) => onFieldChange('code', e.target.value)}
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-white">Inputs</label>
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300 transition-colors"
                onClick={() => {
                  const current = Array.isArray(config.inputs) ? config.inputs : Object.entries(config.inputs || {}).map(([k, v]) => ({ key: k, value: v }));
                  onFieldChange('inputs', [...current, { key: '', value: '' }]);
                }}
              >
                <Plus className="w-3.5 h-3.5" /> Add Input
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Named inputs for <code className="text-violet-400">run(inputs)</code>. Values support <code className="text-violet-400">{'{{context:step_name.field}}'}</code>.
            </p>
            {(() => {
              const inputPairs = Array.isArray(config.inputs)
                ? config.inputs
                : Object.entries(config.inputs || {}).map(([k, v]) => ({ key: k, value: v }));
              if (inputPairs.length === 0) {
                return (
                  <div className="border border-dashed border-white/10 rounded-xl p-4 text-center">
                    <p className="text-xs text-gray-500">No inputs — <code className="text-gray-400">inputs</code> will be an empty dict.</p>
                  </div>
                );
              }
              return (
                <div className="space-y-2">
                  {inputPairs.map((pair, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="text"
                        className="flex-1 px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-xl text-white font-mono focus:outline-none focus:border-violet-500/50"
                        placeholder="key"
                        value={pair.key || ''}
                        onChange={(e) => {
                          const updated = inputPairs.map((p, i) => i === idx ? { ...p, key: e.target.value } : p);
                          onFieldChange('inputs', updated);
                        }}
                      />
                      <span className="text-gray-600 text-xs">→</span>
                      <input
                        type="text"
                        className="flex-1 px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-xl text-white font-mono focus:outline-none focus:border-violet-500/50"
                        placeholder="value or {{context:step_name.field}}"
                        value={pair.value || ''}
                        onChange={(e) => {
                          const updated = inputPairs.map((p, i) => i === idx ? { ...p, value: e.target.value } : p);
                          onFieldChange('inputs', updated);
                        }}
                      />
                      <button
                        type="button"
                        className="text-red-400/60 hover:text-red-400 transition-colors flex-shrink-0"
                        onClick={() => {
                          const updated = inputPairs.filter((_, i) => i !== idx);
                          onFieldChange('inputs', updated);
                        }}
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Timeout (s)</label>
            <input
              type="number"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              value={config.timeout || 30}
              onChange={(e) => onFieldChange('timeout', parseInt(e.target.value))}
            />
          </div>
        </>
      )}

      {/* Database Query */}
      {stepType === 'database_query' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Connection</label>
            <StyledSelect
              value={config.connection_mode || 'zenit'}
              onChange={(v) => onFieldChange('connection_mode', v)}
              options={[
                { value: 'zenit', label: 'Zenit DB' },
                { value: 'external', label: 'External' },
              ]}
            />
          </div>
          {(config.connection_mode || 'zenit') === 'zenit' && (
            <ZenitTablePicker
              config={config}
              onFieldChange={onFieldChange}
              tables={zenitTables}
              loading={loadingZenitTables}
            />
          )}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Query <span className="text-red-400">*</span>
            </label>
            <textarea 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              rows={6}
              placeholder="SELECT * FROM users WHERE active = true"
              value={config.query || ''}
              onChange={(e) => onFieldChange('query', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Timeout (s)</label>
            <input 
              type="number" 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              value={config.timeout || 30}
              onChange={(e) => onFieldChange('timeout', parseInt(e.target.value))}
            />
          </div>
        </>
      )}

      {/* Google integration picker — shared by every gmail_/drive_/sheets_/docs_/calendar_ step */}
      {isGoogleIntegrationStep(stepType) && (() => {
        const service = googleServiceForStep(stepType);
        const serviceLabel = GOOGLE_SERVICE_LABEL[service] || 'Google';
        const options = googleIntegrations.filter(
          (i) => String(i.service_type || i.service || '').toLowerCase() === service
        );
        return (
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              {serviceLabel} Integration <span className="text-red-400">*</span>
            </label>
            {loadingGoogleIntegrations ? (
              <div className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-gray-400 flex items-center gap-2 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading integrations...
              </div>
            ) : options.length > 0 ? (
              <StyledSelect
                value={config.integration_id || ''}
                placeholder={`Select ${serviceLabel} account...`}
                onChange={(v) => onFieldChange('integration_id', v)}
                options={options.map((intg) => ({
                  value: intg.id,
                  label: intg.google_account_email || intg.name || intg.email || intg.id,
                }))}
              />
            ) : (
              <>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
                  placeholder="Enter Google integration ID"
                  value={config.integration_id || ''}
                  onChange={(e) => onFieldChange('integration_id', e.target.value)}
                />
                <p className="text-xs text-amber-400 mt-2">
                  No {serviceLabel} integration found. Connect one in Integrations → Google.
                </p>
              </>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Google account this step will run as.
            </p>
          </div>
        );
      })()}

      {/* Drive Upload */}
      {stepType === 'drive_upload' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              File Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="report.pdf"
              value={config.file_name || ''}
              onChange={(e) => onFieldChange('file_name', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">File Content</label>
            <textarea
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              rows={4}
              placeholder="{{context:report_data}}"
              value={config.file_content || ''}
              onChange={(e) => onFieldChange('file_content', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">MIME Type</label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="text/markdown"
              value={config.mime_type || 'text/markdown'}
              onChange={(e) => onFieldChange('mime_type', e.target.value)}
            />
          </div>
        </>
      )}

      {/* Shopify Update Order */}
      {stepType === 'shopify_update_order' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Order ID <span className="text-red-400">*</span>
            </label>
            <input 
              type="text" 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="{{trigger.order_id}}"
              value={config.order_id || ''}
              onChange={(e) => onFieldChange('order_id', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Tags</label>
            <input 
              type="text" 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="processed, urgent"
              value={config.tags || ''}
              onChange={(e) => onFieldChange('tags', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Note</label>
            <textarea 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              rows={3}
              placeholder="Order processed successfully"
              value={config.note || ''}
              onChange={(e) => onFieldChange('note', e.target.value)}
            />
          </div>
        </>
      )}

      {/* Wait Event */}
      {stepType === 'wait_event' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Event Type <span className="text-red-400">*</span>
            </label>
            <input 
              type="text" 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="file.uploaded"
              value={config.event_type || ''}
              onChange={(e) => onFieldChange('event_type', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Timeout (s)</label>
            <input 
              type="number" 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              value={config.timeout_seconds || 300}
              onChange={(e) => onFieldChange('timeout_seconds', parseInt(e.target.value))}
            />
          </div>
        </>
      )}

      {/* Wait User Input */}
      {stepType === 'wait_user_input' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Input Schema</label>
            <textarea 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              rows={6}
              placeholder='{"type": "object", "properties": {...}}'
              value={config.input_schema ? JSON.stringify(config.input_schema, null, 2) : ''}
              onChange={(e) => {
                try {
                  onFieldChange('input_schema', JSON.parse(e.target.value));
                } catch {
                  onFieldChange('input_schema', e.target.value);
                }
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Timeout (s)</label>
            <input 
              type="number" 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              value={config.timeout_seconds || 3600}
              onChange={(e) => onFieldChange('timeout_seconds', parseInt(e.target.value))}
            />
          </div>
        </>
      )}

      {/* Google Sheets - Append Row */}
      {stepType === 'sheets_append_row' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Spreadsheet ID <span className="text-red-400">*</span>
            </label>
            <input 
              type="text" 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
              value={config.spreadsheet_id || ''}
              onChange={(e) => onSpreadsheetIdChange(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">From Google Sheets URL</p>
            {sheetsError && (
              <div className="mt-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-xs">
                ⚠️ {sheetsError}
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Range <span className="text-red-400">*</span>
            </label>
            <input 
              type="text" 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="Sheet1!A1:D1"
              value={config.range || ''}
              onChange={(e) => onFieldChange('range', e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Values <span className="text-red-400">*</span>
            </label>
            <textarea 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              rows={4}
              placeholder='[["value1", "value2", "value3"]]'
              value={config.values ? JSON.stringify(config.values, null, 2) : ''}
              onChange={(e) => {
                try {
                  onFieldChange('values', JSON.parse(e.target.value));
                } catch {
                  onFieldChange('values', e.target.value);
                }
              }}
            />
            <p className="text-xs text-gray-500 mt-2">2D array: outer list = rows, inner list = columns</p>
          </div>
        </>
      )}

      {/* Google Sheets - Write Range */}
      {stepType === 'sheets_write_range' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Spreadsheet ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
              value={config.spreadsheet_id || ''}
              onChange={(e) => onSpreadsheetIdChange(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">From Google Sheets URL</p>
            {sheetsError && (
              <div className="mt-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-xs">
                ⚠️ {sheetsError}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Range <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="Sheet1!A1:D10"
              value={config.range || ''}
              onChange={(e) => onFieldChange('range', e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Values <span className="text-red-400">*</span>
            </label>
            <textarea
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              rows={6}
              placeholder='[["value1", "value2"], ["value3", "value4"]]'
              value={
                config.values !== undefined && config.values !== null
                  ? (typeof config.values === 'string'
                      ? config.values
                      : JSON.stringify(config.values, null, 2))
                  : ''
              }
              onChange={(e) => {
                const raw = e.target.value;
                try {
                  onFieldChange('values', JSON.parse(raw));
                } catch {
                  onFieldChange('values', raw);
                }
              }}
            />
            <p className="text-xs text-gray-500 mt-2">2D array: outer list = rows, inner list = columns</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Value Input Option</label>
            <StyledSelect
              value={config.value_input_option || 'USER_ENTERED'}
              onChange={(v) => onFieldChange('value_input_option', v)}
              options={['USER_ENTERED', 'RAW']}
            />
          </div>
        </>
      )}

      {/* Gmail Send */}
      {stepType === 'gmail_send' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              To <span className="text-red-400">*</span>
            </label>
            <TokenField
              resolveLabel={resolveLabel}
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="user@example.com, user2@example.com"
              value={config.to ? (Array.isArray(config.to) ? config.to.join(', ') : config.to) : ''}
              onChange={(val) => {
                const toValue = val.split(',').map(email => email.trim()).filter(Boolean);
                onFieldChange('to', toValue);
              }}
            />
            <p className="text-xs text-gray-500 mt-2">Comma-separated email addresses</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Subject <span className="text-red-400">*</span>
            </label>
            <TokenField
              resolveLabel={resolveLabel}
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="Email subject"
              value={config.subject || ''}
              onChange={(val) => onFieldChange('subject', val)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Body (Text) <span className="text-red-400">*</span>
            </label>
            <TokenField
              resolveLabel={resolveLabel}
              multiline
              rows={6}
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="Plain text email body..."
              value={config.body_text || ''}
              onChange={(val) => onFieldChange('body_text', val)}
            />
            <p className="text-xs text-gray-500 mt-2">Required if Body (HTML) is empty</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Body (HTML)</label>
            <TokenField
              resolveLabel={resolveLabel}
              multiline
              rows={6}
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="<p>HTML email body...</p>"
              value={config.body_html || ''}
              onChange={(val) => onFieldChange('body_html', val)}
            />
            <p className="text-xs text-gray-500 mt-2">Optional, but at least one body type is required</p>
          </div>
        </>
      )}

      {/* Gmail Search */}
      {stepType === 'gmail_search' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Query <span className="text-red-400">*</span>
            </label>
            <input 
              type="text" 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="from:sender@example.com subject:invoice"
              value={config.query || ''}
              onChange={(e) => onFieldChange('query', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">Gmail search query (e.g., from:user@example.com is:unread)</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Max Results</label>
            <input 
              type="number" 
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="10"
              value={config.max_results || 10}
              onChange={(e) => onFieldChange('max_results', parseInt(e.target.value))}
            />
          </div>
        </>
      )}

      {/* Google Docs - Create Document */}
      {stepType === 'docs_create_document' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="New Document"
              value={config.title || ''}
              onChange={(e) => onFieldChange('title', e.target.value)}
            />
          </div>
        </>
      )}

      {/* Google Docs - Get Document */}
      {stepType === 'docs_get_document' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Document ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
              value={config.document_id || ''}
              onChange={(e) => onFieldChange('document_id', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">From Google Docs URL</p>
          </div>
        </>
      )}

      {/* Google Docs - Batch Update */}
      {stepType === 'docs_batch_update' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Document ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
              value={config.document_id || ''}
              onChange={(e) => onFieldChange('document_id', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Requests <span className="text-red-400">*</span>
            </label>
            <textarea
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              rows={8}
              placeholder='[{"insertText": {"location": {"index": 1}, "text": "Hello World"}}]'
              value={config.requests ? JSON.stringify(config.requests, null, 2) : ''}
              onChange={(e) => {
                try {
                  onFieldChange('requests', JSON.parse(e.target.value));
                } catch {
                  onFieldChange('requests', e.target.value);
                }
              }}
            />
            <p className="text-xs text-gray-500 mt-2">Array of Google Docs API requests</p>
          </div>
        </>
      )}

      {/* Google Sheets - Create Spreadsheet */}
      {stepType === 'sheets_create_spreadsheet' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="New Spreadsheet"
              value={config.title || ''}
              onChange={(e) => onFieldChange('title', e.target.value)}
            />
          </div>
        </>
      )}

      {/* Google Sheets - Read Range */}
      {stepType === 'sheets_read_range' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Spreadsheet ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
              value={config.spreadsheet_id || ''}
              onChange={(e) => onSpreadsheetIdChange(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">From Google Sheets URL</p>
            {sheetsError && (
              <div className="mt-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-xs">
                ⚠️ {sheetsError}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Range <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="Sheet1!A1:D10"
              value={config.range || ''}
              onChange={(e) => onFieldChange('range', e.target.value)}
            />
          </div>
        </>
      )}

      {/* Google Calendar - Create Event */}
      {stepType === 'calendar_create_event' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Summary <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="Team Meeting"
              value={config.summary || ''}
              onChange={(e) => onFieldChange('summary', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Description</label>
            <textarea
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              rows={3}
              placeholder="Meeting agenda and details..."
              value={config.description || ''}
              onChange={(e) => onFieldChange('description', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-white mb-2">
                Start Time <span className="text-red-400">*</span>
              </label>
              <input
                type="datetime-local"
                className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
                value={config.start_datetime || ''}
                onChange={(e) => onFieldChange('start_datetime', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-white mb-2">
                End Time <span className="text-red-400">*</span>
              </label>
              <input
                type="datetime-local"
                className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
                value={config.end_datetime || ''}
                onChange={(e) => onFieldChange('end_datetime', e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Attendees</label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="user1@example.com, user2@example.com"
              value={config.attendees ? (Array.isArray(config.attendees) ? config.attendees.join(', ') : config.attendees) : ''}
              onChange={(e) => {
                const attendeesList = e.target.value.split(',').map(email => email.trim()).filter(Boolean);
                onFieldChange('attendees', attendeesList);
              }}
            />
            <p className="text-xs text-gray-500 mt-2">Comma-separated email addresses</p>
          </div>
        </>
      )}

      {/* Google Calendar - List Events */}
      {stepType === 'calendar_list_events' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Time Min</label>
            <input
              type="datetime-local"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              value={config.time_min || ''}
              onChange={(e) => onFieldChange('time_min', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">Start date for search range</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Time Max</label>
            <input
              type="datetime-local"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              value={config.time_max || ''}
              onChange={(e) => onFieldChange('time_max', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">End date for search range</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Max Results</label>
            <input
              type="number"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="10"
              value={config.max_results || 10}
              onChange={(e) => onFieldChange('max_results', parseInt(e.target.value))}
            />
          </div>
        </>
      )}

      {/* Google Drive - Download File */}
      {stepType === 'drive_download_file' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              File ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
              value={config.file_id || ''}
              onChange={(e) => onFieldChange('file_id', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">From Google Drive URL</p>
          </div>
        </>
      )}

      {/* Google Drive - List Files */}
      {stepType === 'drive_list_files' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Query</label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="name contains 'report'"
              value={config.query || ''}
              onChange={(e) => onFieldChange('query', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">Google Drive search query</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Max Results</label>
            <input
              type="number"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="10"
              value={config.max_results || 10}
              onChange={(e) => onFieldChange('max_results', parseInt(e.target.value))}
            />
          </div>
        </>
      )}

      {/* Google Drive - Share File */}
      {stepType === 'drive_share_file' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              File ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
              value={config.file_id || ''}
              onChange={(e) => onFieldChange('file_id', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Email <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="user@example.com"
              value={config.email || ''}
              onChange={(e) => onFieldChange('email', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Role</label>
            <StyledSelect
              value={config.role || 'reader'}
              onChange={(v) => onFieldChange('role', v)}
              options={[
                { value: 'reader', label: 'Reader' },
                { value: 'writer', label: 'Writer' },
                { value: 'commenter', label: 'Commenter' },
              ]}
            />
          </div>
        </>
      )}

      {/* Wait for Human Action */}
      {stepType === 'wait_for_human_action' && (
        <>
          {/* Integration (Gmail account) */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Email Integration (Gmail) <span className="text-red-400">*</span>
            </label>
            {loadingGoogleIntegrations ? (
              <div className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-gray-400 flex items-center gap-2 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading integrations...
              </div>
            ) : googleIntegrations.length > 0 ? (
              <StyledSelect
                value={config.integration_id || ''}
                placeholder="Select Gmail account..."
                onChange={(v) => onFieldChange('integration_id', v)}
                options={googleIntegrations.map((intg) => ({
                  value: intg.id,
                  label: intg.name || intg.email || intg.id,
                }))}
              />
            ) : (
              <input
                type="text"
                className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
                placeholder="Enter Google integration ID"
                value={config.integration_id || ''}
                onChange={(e) => onFieldChange('integration_id', e.target.value)}
              />
            )}
            {!loadingGoogleIntegrations && googleIntegrations.length === 0 && (
              <p className="text-xs text-amber-400 mt-2">No Gmail integrations found. Connect one in Integrations → Google.</p>
            )}
          </div>

          {/* Recipient */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Recipient Email <span className="text-red-400">*</span>
            </label>
            <TokenField
              resolveLabel={resolveLabel}
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="approver@example.com or {{trigger:contact_email}}"
              value={config.recipient || ''}
              onChange={(val) => onFieldChange('recipient', val)}
            />
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Subject <span className="text-red-400">*</span>
            </label>
            <TokenField
              resolveLabel={resolveLabel}
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="Approval required for {{context:step_name.order_id}}"
              value={config.subject || ''}
              onChange={(val) => onFieldChange('subject', val)}
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Message <span className="text-red-400">*</span>
            </label>
            <TokenField
              resolveLabel={resolveLabel}
              multiline
              rows={5}
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder={"Please review and take action.\n\n**Order:** {{context:step_name.order_id}}"}
              value={config.message || ''}
              onChange={(val) => onFieldChange('message', val)}
            />
            <p className="text-xs text-gray-500 mt-2">Supports templates and basic Markdown (bold, italic, lists).</p>
          </div>

          {/* Actions */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Actions <span className="text-red-400">*</span>
            </label>
            <p className="text-xs text-gray-500 mb-3">Buttons shown on the decision page. At least 1, at most 10.</p>
            <div className="space-y-2">
              {(config.actions || []).map((action, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <input
                    type="text"
                    className="flex-1 px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-violet-500/50"
                    placeholder="action_id (e.g. approved)"
                    value={action.id || ''}
                    onChange={(e) => {
                      const updated = [...(config.actions || [])];
                      updated[idx] = { ...updated[idx], id: e.target.value };
                      onFieldChange('actions', updated);
                    }}
                  />
                  <input
                    type="text"
                    className="flex-1 px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500/50"
                    placeholder="Button Label (e.g. Approve)"
                    value={action.label || ''}
                    onChange={(e) => {
                      const updated = [...(config.actions || [])];
                      updated[idx] = { ...updated[idx], label: e.target.value };
                      onFieldChange('actions', updated);
                    }}
                  />
                  <button
                    onClick={() => {
                      const updated = (config.actions || []).filter((_, i) => i !== idx);
                      onFieldChange('actions', updated);
                    }}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                    title="Remove action"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            {(config.actions?.length || 0) < 10 && (
              <button
                onClick={() => {
                  const updated = [...(config.actions || []), { id: '', label: '' }];
                  onFieldChange('actions', updated);
                }}
                className="mt-2 w-full px-3 py-2 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 rounded-lg text-orange-400 text-sm font-medium transition-colors"
              >
                + Add Action
              </button>
            )}
          </div>

          {/* Timeout & Language row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Timeout (seconds)</label>
              <input
                type="number"
                min={60}
                className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
                placeholder="86400"
                value={config.timeout_seconds ?? 86400}
                onChange={(e) => onFieldChange('timeout_seconds', parseInt(e.target.value))}
              />
              <p className="text-xs text-gray-500 mt-1">Default 24h (86400 s)</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Language</label>
              <StyledSelect
                value={config.language || 'en'}
                onChange={(v) => onFieldChange('language', v)}
                options={[
                  { value: 'en', label: 'English' },
                  { value: 'es', label: 'Spanish' },
                  { value: 'fr', label: 'French' },
                  { value: 'pt', label: 'Portuguese' },
                  { value: 'de', label: 'German' },
                ]}
              />
            </div>
          </div>

          {/* Timeout action */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Timeout Action</label>
            <StyledSelect
              value={config.timeout_action || 'fail'}
              onChange={(v) => onFieldChange('timeout_action', v)}
              options={[
                { value: 'fail', label: 'Fail — workflow fails if nobody responds' },
                { value: 'continue', label: 'Continue — resumes with action_id="timeout"' },
              ]}
            />
          </div>
        </>
      )}

      {/* Execute Workflow */}
      {stepType === 'execute_workflow' && (
        <>
          {/* Info */}
          <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-500/20 grid place-items-center shrink-0">
                <span className="text-xl">🔁</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-violet-300 mb-1">Execute Another Workflow</p>
                <p className="text-xs text-gray-400">
                  Calls a workflow that exposes an active <span className="font-mono text-violet-300">executable_by_workflow</span> trigger.
                  In sync mode the current workflow waits for the child to finish.
                </p>
              </div>
            </div>
          </div>

          {/* Target workflow */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Target Workflow <span className="text-red-400">*</span>
            </label>
            {loadingCallableWorkflows ? (
              <div className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-gray-400 flex items-center gap-2 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading workflows...
              </div>
            ) : callableWorkflows.length > 0 ? (
              <StyledSelect
                value={config.workflow_id || ''}
                placeholder="Select workflow..."
                onChange={(v) => onFieldChange('workflow_id', v)}
                options={callableWorkflows.map((wf) => ({ value: wf.id, label: wf.name || wf.id }))}
              />
            ) : (
              <input
                type="text"
                className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
                placeholder="Workflow ID (UUID)"
                value={config.workflow_id || ''}
                onChange={(e) => onFieldChange('workflow_id', e.target.value)}
              />
            )}
            <p className="text-xs text-gray-500 mt-2">
              Only workflows with an active <span className="font-mono text-gray-400">executable_by_workflow</span> trigger are valid targets.
            </p>
          </div>

          {/* Inputs (key-value pairs to send to the child workflow) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-white">Inputs</label>
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300 transition-colors"
                onClick={() => {
                  const currentInputs = Array.isArray(config.inputs) ? config.inputs : Object.entries(config.inputs || {}).map(([k,v]) => ({ key: k, value: v }));
                  onFieldChange('inputs', [...currentInputs, { key: '', value: '' }]);
                }}
              >
                <Plus className="w-3.5 h-3.5" /> Add Input
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Key-value pairs sent to the child workflow. Values support{' '}
              <code className="text-violet-400">{'{{context:...}}'}</code> tokens.
            </p>

            {(() => {
              const inputPairs = Array.isArray(config.inputs)
                ? config.inputs
                : Object.entries(config.inputs || {}).map(([k, v]) => ({ key: k, value: v }));

              if (inputPairs.length === 0) {
                return (
                  <div className="border border-dashed border-white/10 rounded-xl p-4 text-center">
                    <p className="text-xs text-gray-500">No inputs — child workflow will receive an empty object.</p>
                  </div>
                );
              }

              return (
                <div className="space-y-2">
                  {inputPairs.map((pair, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="text"
                        className="flex-1 px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none focus:border-violet-500/50"
                        placeholder="key"
                        value={pair.key || ''}
                        onChange={(e) => {
                          const updated = inputPairs.map((p, i) => i === idx ? { ...p, key: e.target.value } : p);
                          onFieldChange('inputs', updated);
                        }}
                      />
                      <span className="text-gray-600 text-xs">→</span>
                      <input
                        type="text"
                        className="flex-1 px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none focus:border-violet-500/50"
                        placeholder="value or {{context:step_1.field}}"
                        value={pair.value || ''}
                        onChange={(e) => {
                          const updated = inputPairs.map((p, i) => i === idx ? { ...p, value: e.target.value } : p);
                          onFieldChange('inputs', updated);
                        }}
                      />
                      <button
                        type="button"
                        className="text-red-400/60 hover:text-red-400 transition-colors flex-shrink-0"
                        onClick={() => {
                          const updated = inputPairs.filter((_, i) => i !== idx);
                          onFieldChange('inputs', updated);
                        }}
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Wait for completion */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-white">Execution Mode</label>
            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="wait_for_completion"
                  className="mt-0.5 accent-violet-500"
                  checked={config.wait_for_completion !== false}
                  onChange={() => onFieldChange('wait_for_completion', true)}
                />
                <div>
                  <p className="text-sm text-white font-medium">Sync (wait)</p>
                  <p className="text-xs text-gray-500">Wait for the child workflow to finish, then continue. Outputs from the child are available as step outputs.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="wait_for_completion"
                  className="mt-0.5 accent-violet-500"
                  checked={config.wait_for_completion === false}
                  onChange={() => onFieldChange('wait_for_completion', false)}
                />
                <div>
                  <p className="text-sm text-white font-medium">Async (fire and forget)</p>
                  <p className="text-xs text-gray-500">Start the child workflow and continue immediately. Only the <code className="text-violet-400">execution_id</code> is returned.</p>
                </div>
              </label>
            </div>
          </div>

          {/* Timeout + continue_on_failure — only relevant in sync mode */}
          {config.wait_for_completion !== false && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Timeout (seconds)</label>
                <input
                  type="number"
                  min={1}
                  className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
                  placeholder="300"
                  value={config.timeout_seconds ?? 300}
                  onChange={(e) => onFieldChange('timeout_seconds', parseInt(e.target.value) || 300)}
                />
                <p className="text-xs text-gray-500 mt-1">Max wait time (default 5 min)</p>
              </div>
              <div className="flex flex-col justify-center gap-2 pt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-3.5 h-3.5 accent-violet-500"
                    checked={config.continue_on_failure || false}
                    onChange={(e) => onFieldChange('continue_on_failure', e.target.checked)}
                  />
                  <span className="text-xs text-gray-300">Continue on failure</span>
                </label>
                <p className="text-[10px] text-gray-500">If checked, this workflow continues even if the child fails.</p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Executable by Workflow Trigger */}
      {stepType === 'trigger' && node?.data?.trigger_type === 'executable_by_workflow' && (
        <>
          {/* Info banner */}
          <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/20 grid place-items-center shrink-0">
                <span className="text-xl">🔁</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-indigo-300 mb-1">Callable by Another Workflow</p>
                <p className="text-xs text-gray-400">
                  This workflow can be invoked by an <span className="font-mono text-indigo-300">execute_workflow</span> step.
                  Define the inputs it expects from the calling workflow.
                </p>
              </div>
            </div>
          </div>

          {/* Display name */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Display Name</label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/20"
              placeholder="e.g. Send Welcome Email"
              value={config.name || ''}
              onChange={(e) => onFieldChange('name', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">Human-readable name shown in the Execute Workflow step picker</p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Description</label>
            <textarea
              rows={2}
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm resize-none focus:outline-none focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/20"
              placeholder="What does this workflow do?"
              value={config.description || ''}
              onChange={(e) => onFieldChange('description', e.target.value)}
            />
          </div>

          {/* Input Parameters */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-white">Input Parameters</label>
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                onClick={() => {
                  const inputs = [...(config.inputs || []), { name: '', type: 'text', required: false, description: '' }];
                  onFieldChange('inputs', inputs);
                }}
              >
                <Plus className="w-3.5 h-3.5" /> Add Input
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Variables passed in by the calling workflow, available as{' '}
              <code className="text-indigo-400">{'{{trigger:input_name}}'}</code>.
            </p>

            {(!config.inputs || config.inputs.length === 0) ? (
              <div className="border border-dashed border-white/10 rounded-xl p-4 text-center">
                <p className="text-xs text-gray-500">No inputs defined — callable without arguments.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {config.inputs.map((input, idx) => (
                  <div key={idx} className="bg-black/30 border border-white/10 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-400">Input {idx + 1}</span>
                      <button
                        type="button"
                        className="text-red-400/60 hover:text-red-400 transition-colors"
                        onClick={() => {
                          const inputs = config.inputs.filter((_, i) => i !== idx);
                          onFieldChange('inputs', inputs);
                        }}
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Name <span className="text-red-400">*</span></label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none focus:border-indigo-500/50"
                          placeholder="customer_id"
                          value={input.name || ''}
                          onChange={(e) => {
                            const inputs = config.inputs.map((inp, i) => i === idx ? { ...inp, name: e.target.value } : inp);
                            onFieldChange('inputs', inputs);
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Type</label>
                        <StyledSelect
                          triggerClassName="px-3 py-2 text-xs rounded-lg"
                          value={input.type || 'text'}
                          onChange={(v) => {
                            const inputs = config.inputs.map((inp, i) => i === idx ? { ...inp, type: v } : inp);
                            onFieldChange('inputs', inputs);
                          }}
                          options={['text', 'number', 'boolean', 'date', 'list', 'object']}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Description</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:border-indigo-500/50"
                        placeholder="What this input is used for"
                        value={input.description || ''}
                        onChange={(e) => {
                          const inputs = config.inputs.map((inp, i) => i === idx ? { ...inp, description: e.target.value } : inp);
                          onFieldChange('inputs', inputs);
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`modal-input-required-${idx}`}
                        checked={input.required || false}
                        className="w-3.5 h-3.5 accent-indigo-500"
                        onChange={(e) => {
                          const inputs = config.inputs.map((inp, i) => i === idx ? { ...inp, required: e.target.checked } : inp);
                          onFieldChange('inputs', inputs);
                        }}
                      />
                      <label htmlFor={`modal-input-required-${idx}`} className="text-xs text-gray-400 select-none">Required</label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Generic fallback: render the full backend schema for any step type that
          doesn't have a dedicated hand-coded form above. */}
      {!['agent', 'ai_agent', 'condition', 'api_call', 'database', 'whatsapp_notification', 'instagram_notification', 'instagram_comment_reply', 'wait_duration', 'wait_condition', 'wait_webhook', 'log', 'code_execution', 'database_query', 'drive_upload', 'shopify_update_order', 'wait_event', 'wait_user_input', 'wait_for_human_action', 'execute_workflow', 'sheets_append_row', 'sheets_write_range', 'gmail_send', 'gmail_search', 'docs_create_document', 'docs_get_document', 'docs_batch_update', 'sheets_create_spreadsheet', 'sheets_read_range', 'calendar_create_event', 'calendar_list_events', 'drive_download_file', 'drive_list_files', 'drive_share_file', 'trigger', 'executable_by_workflow'].includes(stepType) && (
        getSchemaFields(schema).length > 0 ? (
          <SchemaForm schema={schema} config={config} onFieldChange={onFieldChange} llmModels={llmModels} excludeKeys={stepType === 'llm_call' ? LLM_CALL_ADVANCED_KEYS : undefined} />
        ) : (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 text-center">
            <p className="text-amber-400 font-medium">Configuration for {stepType} coming soon</p>
          </div>
        )
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  STEP ADVANCED CONFIG TAB                                           */
/* ------------------------------------------------------------------ */
const StepAdvancedConfig = ({ stepType, config, onFieldChange }) => {
  return (
    <div className="space-y-6">
      {stepType === 'api_call' && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Body Type</label>
              <StyledSelect
                value={config.body_type || 'json'}
                onChange={(v) => onFieldChange('body_type', v)}
                options={['json', 'form', 'raw', 'none', 'multipart']}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Response Type</label>
              <StyledSelect
                value={config.response_type || 'json'}
                onChange={(v) => onFieldChange('response_type', v)}
                options={['json', 'text', 'binary']}
              />
            </div>
          </div>
        </>
      )}

      {stepType === 'condition' && (
        <div>
          <label className="block text-sm font-semibold text-white mb-2">Max Iterations</label>
          <input 
            type="number" 
            className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
            value={config.max_iterations || 100}
            onChange={(e) => onFieldChange('max_iterations', parseInt(e.target.value))}
          />
          <p className="text-xs text-gray-500 mt-2">Maximum loop iterations to prevent infinite loops</p>
        </div>
      )}

      {stepType === 'database' && (
        <>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Schema Name</label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="public"
              value={config.schema_name || ''}
              onChange={(e) => onFieldChange('schema_name', e.target.value || undefined)}
            />
            <p className="text-xs text-gray-500 mt-2">Database schema (PostgreSQL / SQL Server). Defaults to public.</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Timeout (s)</label>
            <input
              type="number"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
              placeholder="30"
              value={config.timeout ?? ''}
              onChange={(e) => onFieldChange('timeout', e.target.value === '' ? undefined : parseInt(e.target.value, 10))}
            />
          </div>
        </>
      )}

      {stepType === 'database_query' && (
        <div>
          <label className="block text-sm font-semibold text-white mb-2">Query Params</label>
          <textarea
            className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
            rows={4}
            placeholder='{"$1": "{{context:step_1.id}}", "status": "active"}'
            value={config.query_params ? JSON.stringify(config.query_params, null, 2) : ''}
            onChange={(e) => {
              const t = e.target.value;
              if (t.trim() === '') { onFieldChange('query_params', undefined); return; }
              try { onFieldChange('query_params', JSON.parse(t)); } catch { /* keep typing */ }
            }}
          />
          <p className="text-xs text-gray-500 mt-2">
            Parameterized values ($1, $2 or :named) to prevent SQL injection.
          </p>
        </div>
      )}

      {stepType === 'code_execution' && (
        <div className="rounded-xl border border-white/10 bg-black/30 p-4 text-sm text-gray-400">
          All parameters for this step (code, inputs, timeout) live in the Basic tab.
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  STEP TESTING CONFIG TAB                                            */
/* ------------------------------------------------------------------ */
const StepTestingConfig = ({ stepType, config, node }) => {
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const handleTest = async () => {
    setTesting(true);
    // Simulate test execution
    setTimeout(() => {
      setTestResult({
        success: true,
        output: 'Test completed successfully',
        duration: 1.23
      });
      setTesting(false);
    }, 2000);
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-blue-300 mb-3">🧪 Test Configuration</h3>
        <p className="text-sm text-gray-300 mb-4">Test your step configuration with sample data before deploying.</p>
        <button
          onClick={handleTest}
          disabled={testing}
          className="px-6 py-2.5 rounded-lg bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/50 text-white text-sm font-semibold transition-all"
        >
          {testing ? 'Testing...' : 'Run Test'}
        </button>
      </div>

      {testResult && (
        <div className={`border rounded-xl p-6 ${
          testResult.success 
            ? 'bg-green-500/10 border-green-500/30' 
            : 'bg-red-500/10 border-red-500/30'
        }`}>
          <h4 className={`text-sm font-semibold mb-3 ${
            testResult.success ? 'text-green-300' : 'text-red-300'
          }`}>
            {testResult.success ? '✅ Test Passed' : '❌ Test Failed'}
          </h4>
          <pre className="text-xs text-gray-300 font-mono bg-black/40 p-4 rounded-lg overflow-x-auto">
            {testResult.output}
          </pre>
          <p className="text-xs text-gray-400 mt-3">Duration: {testResult.duration}s</p>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  SCHEDULE TRIGGER EDITOR OVERLAY                                    */
/* ------------------------------------------------------------------ */
const ScheduleTriggerEditor = ({ node, onBack, onSave }) => {
  const config = node?.data?.configuration || {};
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [frequency, setFrequency] = useState('day');
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(0);
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [timezone, setTimezone] = useState(config.timezone || 'UTC');
  const [customCron, setCustomCron] = useState('');

  // Initialize from config — sidebar preset detection + all fields
  useEffect(() => {
    const cronExpression = config.cron_expression || '0 9 * * *';
    const parsed = cronToSchedule(cronExpression);
    const h = parsed.hour || 9;
    const m = parsed.minute || 0;
    const dow = parsed.dayOfWeek || 1;
    const dom = parsed.dayOfMonth || 1;

    setFrequency(parsed.frequency);
    setHour(h);
    setMinute(m);
    setDayOfWeek(dow);
    setDayOfMonth(dom);

    // Match sidebar preset — only highlight 'custom' when frequency is actually custom
    if (parsed.frequency === 'custom') {
      setCustomCron(parsed.customCron || cronExpression);
      setSelectedPreset('custom');
    } else if (parsed.frequency === 'day' && h === 9 && m === 0) {
      setSelectedPreset('daily_9am');
    } else if (parsed.frequency === 'day' && h === 18 && m === 0) {
      setSelectedPreset('daily_6pm');
    } else if (parsed.frequency === 'week' && h === 9 && m === 0 && dow === 1) {
      setSelectedPreset('monday_9am');
    } else if (parsed.frequency === 'month' && h === 9 && m === 0 && dom === 1) {
      setSelectedPreset('first_month');
    } else if (parsed.frequency === 'hour' && m === 0) {
      setSelectedPreset('hourly');
    } else {
      // Saved schedule is a custom time — no preset matches, highlight nothing
      setSelectedPreset(null);
    }
  }, [config.cron_expression]);

  const presets = [
    { id: 'daily_9am', label: 'Daily at 9:00 AM', icon: '🌅', freq: 'day', h: 9, m: 0 },
    { id: 'daily_6pm', label: 'Daily at 6:00 PM', icon: '🌆', freq: 'day', h: 18, m: 0 },
    { id: 'weekdays_9am', label: 'Weekdays at 9:00 AM', icon: '💼', freq: 'week', h: 9, m: 0, dow: 1 },
    { id: 'monday_9am', label: 'Every Monday at 9:00 AM', icon: '📅', freq: 'week', h: 9, m: 0, dow: 1 },
    { id: 'first_month', label: 'First day of month at 9:00 AM', icon: '📆', freq: 'month', h: 9, m: 0, dom: 1 },
    { id: 'hourly', label: 'Every hour', icon: '⏱️', freq: 'hour', m: 0 },
    { id: 'custom', label: 'Custom schedule', icon: '⚙️', freq: 'custom' }
  ];

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset.id);
    setFrequency(preset.freq);
    if (preset.h !== undefined) setHour(preset.h);
    if (preset.m !== undefined) setMinute(preset.m);
    if (preset.dow !== undefined) setDayOfWeek(preset.dow);
    if (preset.dom !== undefined) setDayOfMonth(preset.dom);
  };

  const handleSave = () => {
    let cronExpression;
    if (frequency === 'custom') {
      cronExpression = customCron || '0 9 * * *';
    } else {
      cronExpression = scheduleToCron({
        frequency,
        hour,
        minute,
        dayOfWeek,
        dayOfMonth,
      });
    }

    onSave({
      cron_expression: cronExpression,
      timezone: timezone
    });
  };

  const currentCron = frequency === 'custom' 
    ? customCron 
    : scheduleToCron({ frequency, hour, minute, dayOfWeek, dayOfMonth });

  return (
    <motion.div
      className="absolute inset-0 z-50 bg-[#09090b] flex flex-col rounded-xl overflow-hidden"
      initial={{ opacity: 0, scale: 0.97, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97, y: 12 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Header */}
      <div className="h-11 shrink-0 border-b border-white/10 flex items-center gap-3 px-4">
        <button onClick={onBack} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
          <ArrowLeft className="w-4 h-4 text-gray-400" />
        </button>
        <div className="h-4 w-px bg-white/10" />
        <div className="w-5 h-5 rounded bg-gradient-to-br from-orange-500 to-amber-500 grid place-items-center shrink-0">
          <span className="text-sm">⏰</span>
        </div>
        <span className="text-sm font-semibold text-white">Schedule Trigger</span>
        <span className="text-xs text-gray-500">/ Configuration</span>
        <div className="flex-1" />
        <button
          onClick={handleSave}
          className="px-4 py-1.5 text-xs font-semibold bg-orange-600 hover:bg-orange-500 text-white rounded-lg transition-colors"
        >
          Save schedule
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 flex min-h-0">
        {/* Presets sidebar */}
        <div className="w-64 shrink-0 border-r border-white/10 flex flex-col">
          <div className="px-4 pt-4 pb-2 text-[10px] font-bold text-gray-500 uppercase tracking-widest">
            Quick presets
          </div>
          <div className="flex-1 overflow-y-auto px-2 space-y-1 pb-4">
            {presets.map((preset) => (
              <button
                key={preset.id}
                onClick={() => handlePresetSelect(preset)}
                className={`w-full text-left px-3 py-2.5 rounded-lg transition-all ${
                  selectedPreset === preset.id
                    ? 'bg-orange-500/20 border border-orange-500/50 text-white'
                    : 'bg-white/5 border border-transparent hover:bg-white/10 text-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-base">{preset.icon}</span>
                  <span className="text-xs font-medium">{preset.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Configuration panel */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-xl space-y-6">
            {frequency === 'custom' ? (
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Custom cron expression
                </label>
                <input
                  type="text"
                  value={customCron}
                  onChange={(e) => setCustomCron(e.target.value)}
                  placeholder="0 9 * * *"
                  className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Format: <code className="text-orange-400">minute hour day month day-of-week</code>
                </p>
              </div>
            ) : (
              <>
                {/* Time picker */}
                {frequency !== 'minute' && (
                  <div>
                    <label className="block text-sm font-semibold text-white mb-3">
                      Time {frequency === 'hour' ? '(minutes)' : ''}
                    </label>
                    <div className="flex items-center gap-4">
                      {frequency !== 'hour' && (
                        <>
                          <div className="flex-1">
                            <label className="block text-xs text-gray-400 mb-2">Hour</label>
                            <input
                              type="number"
                              min="0"
                              max="23"
                              value={hour}
                              onChange={(e) => setHour(parseInt(e.target.value, 10))}
                              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-center text-lg font-semibold focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20"
                            />
                          </div>
                          <div className="text-2xl text-gray-600 font-bold mt-6">:</div>
                        </>
                      )}
                      <div className="flex-1">
                        <label className="block text-xs text-gray-400 mb-2">Minute</label>
                        <input
                          type="number"
                          min="0"
                          max="59"
                          value={minute}
                          onChange={(e) => setMinute(parseInt(e.target.value, 10))}
                          className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-center text-lg font-semibold focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Day of week for weekly */}
                {frequency === 'week' && (
                  <div>
                    <label className="block text-sm font-semibold text-white mb-3">Day of week</label>
                    <div className="grid grid-cols-7 gap-2">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                        <button
                          key={idx}
                          onClick={() => setDayOfWeek(idx)}
                          className={`px-3 py-2.5 rounded-lg text-xs font-semibold transition-all ${
                            dayOfWeek === idx
                              ? 'bg-orange-500 text-white'
                              : 'bg-white/5 text-gray-400 hover:bg-white/10'
                          }`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Day of month for monthly */}
                {frequency === 'month' && (
                  <div>
                    <label className="block text-sm font-semibold text-white mb-3">Day of month</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={dayOfMonth}
                      onChange={(e) => setDayOfMonth(parseInt(e.target.value, 10))}
                      className="w-32 px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-center text-lg font-semibold focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20"
                    />
                  </div>
                )}
              </>
            )}

            {/* Timezone */}
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Timezone</label>
              <input
                type="text"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder="UTC"
                className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20"
              />
              <p className="text-xs text-gray-500 mt-2">
                Examples: UTC, America/New_York, Europe/London, Asia/Tokyo
              </p>
            </div>

            {/* Preview */}
            <div className="bg-gradient-to-br from-orange-500/10 to-amber-500/10 border border-orange-500/30 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 grid place-items-center shrink-0">
                  <span className="text-xl">🎯</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-400 mb-1">This workflow will run:</p>
                  <p className="text-sm font-semibold text-orange-300 mb-2">{describeCron(currentCron)}</p>
                  <p className="text-xs text-gray-500 font-mono">Cron: {currentCron}</p>
                  <p className="text-xs text-gray-500 mt-1">Timezone: {timezone}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ------------------------------------------------------------------ */
/*  AGENT TASK EDITOR OVERLAY                                          */
/* ------------------------------------------------------------------ */
const AgentTaskEditor = ({
  node,
  onBack,
  onSaveToNode,
  onUpdateAssistant,
  llmModels = [],
  availableSkills = [],
  assistants = [],
  loadingAssistants = false,
  setNodes,
  allNodes = [],
  stepResults = {},
  readOnly = false
}) => {
  const agentName = node?.data?.label || 'AI Agent';
  const { toasts, removeToast, success: toastSuccess, error: toastError } = useToast();
  const { currentAccount } = useOrganizations();

  const [activeTab, setActiveTab] = useState('config');
  const [selectedAssistant, setSelectedAssistant] = useState(() => {
    const assistantId = node?.data?.assistant_id;
    return assistants.find(a => a.id === assistantId) || null;
  });
  const [executionMode, setExecutionMode] = useState(() => node?.data?.execution_mode || 'sequential');

  const [tasks, setTasks] = useState(() => {
    const existing = node?.data?.tasks;
    return Array.isArray(existing) && existing.length > 0
      ? existing.map(t => ({ ...t }))
      : [{ name: 'default', message: '{{trigger:input}}' }];
  });
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [editing, setEditing] = useState(() => {
    const existing = node?.data?.tasks;
    const t = Array.isArray(existing) && existing.length > 0
      ? existing[0]
      : { name: 'default', message: '{{trigger:input}}' };
    return { ...t };
  });
  const [nameError, setNameError] = useState('');
  const [assistantSkills, setAssistantSkills] = useState([]);
  const [dataSelectorVisible, setDataSelectorVisible] = useState(false);
  const lastInputRef = useRef(null);
  const resolveLabel = useCallback(
    (token) => resolveTokenLabel(token, allNodes),
    [allNodes]
  );

  // Update selectedAssistant when assistants load or node changes
  useEffect(() => {
    const assistantId = node?.data?.assistant_id;
    if (assistantId) {
      const assistant = assistants.find(a => a.id === assistantId);
      if (assistant) {
        setSelectedAssistant(assistant);
      }
    }
  }, [assistants, node?.data?.assistant_id]);

  // Load assistant skills when component mounts
  useEffect(() => {
    const loadAssistantSkills = async () => {
      const assistantId = node?.data?.assistant_id;
      if (!assistantId || !currentAccount?.id) return;

      try {
        const result = await AssistantService.getAssistant(assistantId, {
          accountId: currentAccount.id
        });
        
        if (result.success && result.data) {
          const assistant = result.data;
          const skills = assistant.skills || [];
          setAssistantSkills(skills);
        }
      } catch (error) {
        console.error('Error loading assistant skills:', error);
      }
    };

    loadAssistantSkills();
  }, [node?.data?.assistant_id, currentAccount?.id]);

  // Config override ahora es parte de editing.config_override (individual por task)
  const overrideEnabled = !!(editing?.config_override);
  const overrideLlmId = editing?.config_override?.llm_id || '';
  const overrideSkills = editing?.config_override?.skills || [];
  const overrideTemperature = editing?.config_override?.temperature ?? 0.7;
  const overrideMaxTokens = editing?.config_override?.max_tokens ?? 2048;
  
  const [isModelPickerOpen, setIsModelPickerOpen] = useState(false);

  const safeModels = Array.isArray(llmModels) && llmModels.length > 0 ? llmModels : [];
  const selectedModelData = safeModels.find(m => m.id === overrideLlmId) || null;

  // Filter available skills to only show skills configured in the assistant
  const filteredSkills = availableSkills.filter(skill => 
    assistantSkills.some(assistantSkill => assistantSkill === skill.id || assistantSkill === skill.name)
  );

  const validateName = (name) => {
    if (!name) return 'Name is required';
    if (!/^[a-z][a-z0-9_]*$/.test(name)) return 'snake_case only: lowercase letters, digits, underscores';
    return '';
  };

  const syncAndSelect = (nextTasks, idx) => {
    setTasks(nextTasks);
    setSelectedIdx(idx);
    setEditing({ ...nextTasks[idx] });
    setNameError('');
  };

  const handleSelectTask = (idx) => {
    const saved = tasks.map((t, i) => (i === selectedIdx ? { ...editing } : t));
    syncAndSelect(saved, idx);
  };

  const handleAddTask = () => {
    const saved = tasks.map((t, i) => (i === selectedIdx ? { ...editing } : t));
    const newTask = { name: `task_${saved.length + 1}`, message: '' };
    syncAndSelect([...saved, newTask], saved.length);
  };

  const handleRemoveTask = (idx) => {
    if (tasks.length === 1) return;
    const saved = tasks.map((t, i) => (i === selectedIdx ? { ...editing } : t));
    const next = saved.filter((_, i) => i !== idx);
    syncAndSelect(next, Math.min(idx, next.length - 1));
  };

  const handleSave = () => {
    const error = validateName(editing.name);
    if (error) { setNameError(error); return; }

    // Guardar editing actual (ya tiene su config_override individual)
    const finalTasks = tasks.map((t, i) => {
      if (i === selectedIdx) {
        const saved = { ...editing };
        // Limpiar config_override: eliminar campos vacíos/undefined que Pydantic rechaza
        if (saved.config_override) {
          const cleaned = {};
          if (saved.config_override.llm_id) cleaned.llm_id = saved.config_override.llm_id;
          if (Array.isArray(saved.config_override.skills) && saved.config_override.skills.length > 0) {
            cleaned.skills = saved.config_override.skills;
          }
          if (typeof saved.config_override.temperature === 'number') cleaned.temperature = saved.config_override.temperature;
          if (typeof saved.config_override.max_tokens === 'number') cleaned.max_tokens = saved.config_override.max_tokens;
          
          // Si no hay campos válidos, eliminar config_override completamente
          if (Object.keys(cleaned).length === 0) {
            delete saved.config_override;
          } else {
            saved.config_override = cleaned;
          }
        }
        return saved;
      }
      return { ...t };
    });
    
    onSaveToNode(finalTasks);
    toastSuccess('Tasks saved', `${finalTasks.length} task${finalTasks.length !== 1 ? 's' : ''} saved to agent.`);
  };

  const handleAssistantChange = (e) => {
    const assistantId = e.target.value;
    if (!assistantId) return;
    
    const assistant = assistants.find(a => a.id === assistantId);
    if (!assistant) return;
    
    setSelectedAssistant(assistant);
    if (onUpdateAssistant) {
      onUpdateAssistant(assistantId);
    }
  };

  const handleExecutionModeChange = (e) => {
    const mode = e.target.value;
    setExecutionMode(mode);
    // Update node immediately
    if (setNodes) {
      setNodes(nds => nds.map(n => 
        n.id === node.id 
          ? { ...n, data: { ...n.data, execution_mode: mode } }
          : n
      ));
    }
  };

  return (
    <motion.div
      className="absolute inset-0 z-50 bg-[#09090b] flex flex-col rounded-xl overflow-hidden"
      initial={{ opacity: 0, scale: 0.97, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97, y: 12 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Header */}
      <div className="h-11 shrink-0 border-b border-white/10 flex items-center gap-3 px-4">
        <button
          onClick={onBack}
          className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4 text-gray-400" />
        </button>
        <div className="h-4 w-px bg-white/10" />
        <div className="w-5 h-5 rounded bg-gradient-to-br from-violet-500 to-fuchsia-500 grid place-items-center shrink-0">
          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <span className="text-sm font-semibold text-white">{agentName}</span>
        <span className="text-xs text-gray-500">/ {activeTab === 'config' ? 'Configuration' : 'Tasks'}</span>
        <div className="flex-1" />
        {readOnly ? (
          <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded-lg">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/></svg>
            View only
          </span>
        ) : activeTab === 'tasks' && (
          <button
            onClick={handleSave}
            className="px-4 py-1.5 text-xs font-semibold bg-primary hover:bg-violet-500 text-white rounded-lg transition-colors"
          >
            Save tasks
          </button>
        )}
      </div>

      {/* Body */}
      <div
        className="flex-1 flex min-h-0"
        onFocusCapture={(e) => {
          const isTokenField = e.target.dataset?.tokenfield === 'true';
          if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || isTokenField) {
            if (!e.target.closest('[data-dataselector]')) {
              lastInputRef.current = e.target;
            }
            setDataSelectorVisible(true);
          }
        }}
        onBlurCapture={(e) => {
          if (e.relatedTarget?.closest?.('[data-dataselector]')) return;
          if (!e.currentTarget.contains(e.relatedTarget)) {
            setDataSelectorVisible(false);
          }
        }}
      >
        {/* Floating Data Selector — appears as a separate window when an input is focused */}
        <DataSelector
          nodes={allNodes}
          selectedNodeId={node?.id}
          visible={dataSelectorVisible && !readOnly}
          stepResults={stepResults}
          lastInputRef={lastInputRef}
          onClose={() => setDataSelectorVisible(false)}
        />

        {/* Sidebar with tabs */}
        <div className="w-52 shrink-0 border-r border-white/10 flex flex-col">
          {/* Tabs */}
          <div className="flex flex-col gap-1 p-2 border-b border-white/10">
            <button
              onClick={() => setActiveTab('config')}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                activeTab === 'config'
                  ? 'bg-violet-600/20 border border-violet-500/30 text-violet-200'
                  : 'border border-transparent text-gray-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              Configuration
            </button>
            <button
              onClick={() => setActiveTab('tasks')}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                activeTab === 'tasks'
                  ? 'bg-violet-600/20 border border-violet-500/30 text-violet-200'
                  : 'border border-transparent text-gray-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              Tasks ({tasks.length})
            </button>
          </div>

          {/* Task list (only shown in tasks tab) */}
          {activeTab === 'tasks' && (
            <>
              <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
                {tasks.map((task, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectTask(idx)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-xs transition-colors ${
                      idx === selectedIdx
                        ? 'bg-violet-600/20 border border-violet-500/30 text-violet-200'
                        : 'border border-transparent text-gray-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <div className="font-mono font-medium truncate">{task.name || 'unnamed'}</div>
                    <div className="text-[10px] text-gray-600 truncate mt-0.5">
                      {task.message || 'No message set'}
                    </div>
                  </button>
                ))}
              </div>
              <div className="p-3 border-t border-white/10">
                <button
                  onClick={handleAddTask}
                  className="w-full py-2 text-xs font-medium text-gray-500 border border-dashed border-white/15 rounded-lg hover:text-white hover:border-white/30 transition-colors"
                >
                  + Add task
                </button>
              </div>
            </>
          )}
        </div>

        {/* Content area */}
        <fieldset disabled={readOnly} className={readOnly ? 'flex-1 overflow-hidden opacity-70 pointer-events-none select-text' : 'flex-1 overflow-hidden'}>
        <div className="h-full overflow-y-auto p-6">
          {activeTab === 'config' && (
            <div className="space-y-6">
              <h2 className="text-sm font-semibold text-white mb-5">Agent Configuration</h2>
              
              {/* Assistant selector */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Agent <span className="text-red-400">*</span>
                </label>
                {loadingAssistants ? (
                  <div className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-gray-400 flex items-center gap-2 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading...
                  </div>
                ) : (
                  <select
                    className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
                    value={selectedAssistant?.id || ''}
                    onChange={handleAssistantChange}
                  >
                    <option value="">Select agent...</option>
                    {assistants.map((assistant) => (
                      <option key={assistant.id} value={assistant.id}>{assistant.name}</option>
                    ))}
                  </select>
                )}
                {selectedAssistant && (
                  <div className="mt-3 p-3 bg-violet-500/10 border border-violet-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      {selectedAssistant.avatar_url && (
                        <img 
                          src={selectedAssistant.avatar_url} 
                          alt={selectedAssistant.name}
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white">{selectedAssistant.name}</div>
                        {selectedAssistant.description && (
                          <div className="text-xs text-gray-400 mt-0.5">{selectedAssistant.description}</div>
                        )}
                        <div className="text-[10px] text-gray-500 mt-1">
                          Model: {selectedAssistant.model_name || 'N/A'} • Skills: {selectedAssistant.skills?.length || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Execution mode */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Execution Mode</label>
                <select
                  className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20"
                  value={executionMode}
                  onChange={handleExecutionModeChange}
                >
                  <option value="sequential">Sequential - Tasks run one after another</option>
                  <option value="parallel">Parallel - Tasks run concurrently</option>
                </select>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <p className="text-sm text-blue-300">
                  💡 Switch to "Tasks" tab to configure what this agent will do
                </p>
              </div>
            </div>
          )}

          {activeTab === 'tasks' && (
            <>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-sm font-semibold text-white">Edit task</h2>
                {tasks.length > 1 && (
                  <button
                    onClick={() => handleRemoveTask(selectedIdx)}
                    className="text-xs text-red-400 hover:text-red-300 transition-colors"
                  >
                    Remove task
                  </button>
                )}
              </div>

          <div className="grid grid-cols-2 gap-6 items-start">
            {/* Left column: Name + Message */}
            <div className="space-y-5">
              {/* Name */}
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">
                  Name
                </label>
                <input
                  type="text"
                  value={editing.name || ''}
                  onChange={(e) => { setEditing(prev => ({ ...prev, name: e.target.value })); setNameError(''); }}
                  placeholder="e.g. analyze_input"
                  className={`w-full px-3 py-2 bg-white/5 border rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-1 transition-colors font-mono ${
                    nameError
                      ? 'border-red-500/60 focus:ring-red-500/30'
                      : 'border-white/10 focus:border-violet-500/50 focus:ring-violet-500/20'
                  }`}
                />
                {nameError ? (
                  <p className="text-[10px] text-red-400 mt-1">{nameError}</p>
                ) : (
                  <p className="text-[10px] text-gray-600 mt-1">
                    snake_case identifier. Other tasks reference this output via{' '}
                    <code className="text-violet-400">{`{{${editing.name || 'task_name'}:output}}`}</code>
                  </p>
                )}
              </div>

              {/* Message */}
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">
                  Message
                </label>
                <textarea
                  value={editing.message || ''}
                  onChange={(e) => setEditing(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Prompt sent to the agent. Supports workflow templates."
                  rows={10}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20 transition-colors resize-none font-mono"
                />
              </div>
            </div>

            {/* Right column: Config Override */}
            <div className="border border-white/10 rounded-xl overflow-hidden">
              <button
                onClick={() => {
                  if (overrideEnabled) {
                    // Desactivar: eliminar config_override
                    setEditing(prev => ({ ...prev, config_override: undefined }));
                  } else {
                    // Activar: inicializar con valores por defecto
                    setEditing(prev => ({ 
                      ...prev, 
                      config_override: { 
                        llm_id: '',
                        skills: [],
                        temperature: 0.7,
                        max_tokens: 2048
                      } 
                    }));
                  }
                }}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
              >
                <div>
                  <p className="text-sm font-semibold text-white text-left">Config override</p>
                  <p className="text-[10px] text-gray-500 text-left mt-0.5">
                    Override the agent's model, skills and generation parameters for this task.
                  </p>
                </div>
                <div
                  className={`relative inline-flex h-5 w-9 shrink-0 ml-4 items-center rounded-full transition-colors duration-200 ${
                    overrideEnabled ? 'bg-primary' : 'bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform duration-200 ${
                      overrideEnabled ? 'translate-x-4' : 'translate-x-0.5'
                    }`}
                  />
                </div>
              </button>

              {overrideEnabled && (
                <div className="px-4 pb-5 space-y-5 border-t border-white/10 pt-4">
                  {/* Model */}
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">
                      Model
                    </label>
                    <button
                      onClick={() => setIsModelPickerOpen(true)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/10 rounded-lg hover:border-violet-500/40 transition-colors text-sm"
                    >
                      <span className={selectedModelData ? 'text-white' : 'text-gray-500'}>
                        {selectedModelData ? selectedModelData.name : 'Select model…'}
                      </span>
                      <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
                    </button>
                  </div>

                  {/* Skills */}
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">
                      Skills
                    </label>
                    <SkillStoreSelector
                      skills={filteredSkills}
                      selectedSkills={overrideSkills}
                      onChange={(skills) => setEditing(prev => ({ 
                        ...prev, 
                        config_override: { ...prev.config_override, skills } 
                      }))}
                    />
                    <p className="text-[10px] text-gray-500 mt-1.5">
                      Only skills configured in the agent are available
                    </p>
                  </div>

                  {/* Temperature */}
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">
                      Temperature: {overrideTemperature}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.1"
                      value={overrideTemperature}
                      onChange={(e) => setEditing(prev => ({ 
                        ...prev, 
                        config_override: { ...prev.config_override, temperature: parseFloat(e.target.value) } 
                      }))}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-[10px] text-gray-600 mt-1">
                      <span>Precise (0)</span>
                      <span>Balanced (1)</span>
                      <span>Creative (2)</span>
                    </div>
                  </div>

                  {/* Max tokens */}
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">
                      Max tokens: {overrideMaxTokens}
                    </label>
                    <input
                      type="range"
                      min="256"
                      max="4096"
                      step="256"
                      value={overrideMaxTokens}
                      onChange={(e) => setEditing(prev => ({ 
                        ...prev, 
                        config_override: { ...prev.config_override, max_tokens: parseInt(e.target.value) } 
                      }))}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-[10px] text-gray-600 mt-1">
                      <span>Short (256)</span>
                      <span>Medium (2048)</span>
                      <span>Long (4096)</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
            </>
          )}
        </div>
        </fieldset>
      </div>

      <ModelSelectorDialog
        open={isModelPickerOpen}
        onOpenChange={setIsModelPickerOpen}
        models={safeModels}
        selectedModelId={overrideLlmId}
        recommendedModelId={safeModels[0]?.id}
        onSelectModel={(id) => { 
          setEditing(prev => ({ 
            ...prev, 
            config_override: { ...prev.config_override, llm_id: id } 
          })); 
          setIsModelPickerOpen(false); 
        }}
      />
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </motion.div>
  );
};

// Componente interno que usa useReactFlow
function WorkflowCanvasInner({ workflowId, projectId, workflowName, onSave, onTest, onRun, onPublish, onWorkflowDataLoaded, style, isDraftEditMode = false }) {
  const { currentAccount } = useOrganizations();
  const { user } = useAuth();
  const { llmModels, loadLLMModels } = useAssistantContext();
  const [availableSkills, setAvailableSkills] = useState([]);
  const { toasts, removeToast, success: toastSuccess, error: toastError, info: toastInfo } = useToast();
  const [currentZoom, setCurrentZoom] = useState(100);
  const [loading, setLoading] = useState(!!workflowId);
  const [workflowData, setWorkflowData] = useState(null);
  const [activeWorkflowId, setActiveWorkflowId] = useState(workflowId || null);
  const isReadOnly = workflowData?.status === 'active' && !isDraftEditMode;

  // La paleta de pasos mide 288px. En un teléfono de 390, con el aire lateral,
  // al lienzo le quedaban unos 40px: no se veía el workflow que se viene a
  // mirar. En móvil se convierte en cajón —cerrada de entrada, encima del
  // lienzo al abrirla— y el lienzo recupera la pantalla entera.
  const isMobile = useIsMobile();
  const [showStepPalette, setShowStepPalette] = useState(false);
  const [rfInstance, setRfInstance] = useState(null);
  const [stepCatalog, setStepCatalog] = useState(null);
  const [triggerCatalog, setTriggerCatalog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [agentTaskEditorNode, setAgentTaskEditorNode] = useState(null);
  const [scheduleTriggerEditorNode, setScheduleTriggerEditorNode] = useState(null);
  const [stepEditorNode, setStepEditorNode] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showHelpMessage, setShowHelpMessage] = useState(true);
  const [lastExecutionId, setLastExecutionId] = useState(null);
  const [showRunInspector, setShowRunInspector] = useState(false);
  const [assistants, setAssistants] = useState([]);
  const [loadingAssistants, setLoadingAssistants] = useState(false);
  const [nodeExecutionState, setNodeExecutionState] = useState({});
  const [activeStreamController, setActiveStreamController] = useState(null);
  const [pinnedStepIds, setPinnedStepIds] = useState(new Set());
  const [lastStepResults, setLastStepResults] = useState({});

  // Iniciar con canvas vacío
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    setActiveWorkflowId(workflowId || null);
  }, [workflowId]);

  // Cargar step catalog y trigger catalog al montar
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [stepsResult, triggersResult] = await Promise.all([
          WorkflowService.getStepsCatalog(),
          WorkflowService.getTriggerCatalog(),
        ]);
        if (stepsResult.success && stepsResult.data) {
          setStepCatalog(stepsResult.data.catalog || stepsResult.data);
        }
        if (triggersResult.success && triggersResult.data) {
          setTriggerCatalog(triggersResult.data);
        }
      } catch (error) {
        console.error('Error loading catalogs:', error);
      }
    };

    loadCatalogs();
  }, []);

  // Load pinned steps for this workflow
  const reloadTestPins = useCallback(async (workflowIdOverride) => {
    const wfId = workflowIdOverride || activeWorkflowId;
    if (!wfId) return;
    const result = await WorkflowService.listTestPins(wfId);
    if (result.success) {
      setPinnedStepIds(new Set((result.data.pins || []).map(p => p.step_id)));
    }
  }, [activeWorkflowId]);

  useEffect(() => {
    if (!activeWorkflowId) return;
    reloadTestPins(activeWorkflowId);
  }, [activeWorkflowId, reloadTestPins]);

  // Propagate isPinned flag to node data whenever the pinned set changes
  useEffect(() => {
    setNodes(nds => nds.map(n => ({
      ...n,
      data: { ...n.data, isPinned: pinnedStepIds.has(n.data?.step_id) }
    })));
  }, [pinnedStepIds]);

  // Load LLM models and skills for agent config override
  useEffect(() => {
    loadLLMModels();
    AssistantService.getSkills()
      .then(result => {
        if (result.success) setAvailableSkills(result.data?.skills || result.data || []);
      })
      .catch(() => {});
  }, []);

  // Load assistants for agent configuration
  useEffect(() => {
    if (!currentAccount?.id || !projectId) return;

    const loadAssistants = async () => {
      setLoadingAssistants(true);
      try {
        const result = await AssistantService.listAssistants({
          accountId: currentAccount.id,
          projectId: projectId,
          limit: 100
        });
        if (result.success && result.data) {
          setAssistants(result.data);
        }
      } catch (error) {
        console.error('Error loading assistants:', error);
      } finally {
        setLoadingAssistants(false);
      }
    };

    loadAssistants();
  }, [currentAccount?.id, projectId]);

  // Actualizar nodos con estado de ejecución (incluso cuando se vacía, para limpiar spinners).
  // useEffect en lugar de useLayoutEffect para evitar cascadas síncronas de renders que
  // disparan "Maximum update depth exceeded" cuando se combina con flushSync en el run handler.
  useEffect(() => {
    setNodes(nds => {
      let changed = false;
      const next = nds.map(node => {
        const executionState = node.data?.step_id ? nodeExecutionState[node.data.step_id] || null : null;
        if (node.data?.executionState === executionState) return node;
        changed = true;
        return { ...node, data: { ...node.data, executionState } };
      });
      return changed ? next : nds;
    });
  }, [nodeExecutionState, setNodes]);

  // Limpiar stream SSE al desmontar
  useEffect(() => {
    return () => {
      if (activeStreamController) {
        activeStreamController.abort();
      }
    };
  }, [activeStreamController]);

  // Reconciliation: when SSE closes (cleanly or via reconnect on a fast run that
  // already finished), pull the snapshot from GET /executions/{id} and apply
  // step_progress to the canvas. Per SSE spec, the stream has no replay.
  const reconcileFromSnapshot = useCallback(async (executionId) => {
    const result = await WorkflowService.getExecution(executionId, {
      project_id: projectId,
      include_step_results: true,
      include_logs_summary: false,
    });
    if (!result.success) return null;
    const exec = result.data?.execution || {};
    const stepProgress = exec.step_progress || {};
    const updates = {};
    for (const sp of Object.values(stepProgress)) {
      if (!sp?.step_id) continue;
      let uiStatus = sp.status;
      // Treat 'pending' as 'completed' / 'failed' once the run reached a terminal state.
      if (uiStatus === 'pending' && (exec.status === 'completed' || exec.status === 'failed')) {
        uiStatus = exec.status;
      }
      if (uiStatus === 'running' || uiStatus === 'completed' || uiStatus === 'failed' || uiStatus === 'skipped') {
        updates[sp.step_id] = {
          status: uiStatus === 'skipped' ? 'completed' : uiStatus,
          timestamp: sp.completed_at || sp.started_at || new Date().toISOString(),
        };
      }
    }
    if (Object.keys(updates).length > 0) {
      setNodeExecutionState(prev => ({ ...prev, ...updates }));
    }
    const stepResults = exec.execution_context?.step_results || {};
    if (Object.keys(stepResults).length > 0) {
      setLastStepResults(stepResults);
    }
    return exec;
  }, [projectId]);

  // Cargar workflow desde API si workflowId existe
  useEffect(() => {
    if (!activeWorkflowId || !currentAccount?.id) {
      // Nuevo workflow - canvas vacío
      setNodes([]);
      setEdges([]);
      setLoading(false);
      return;
    }

    const loadWorkflow = async () => {
      setLoading(true);
      try {
        // Load workflow; trigger list endpoints may not be implemented yet so we handle 404 gracefully
        const workflowResult = await WorkflowService.getWorkflow(activeWorkflowId, projectId);

        if (workflowResult.success && workflowResult.data) {
          const workflow = workflowResult.data.workflow || workflowResult.data;
          setWorkflowData(workflow);
          
          const newNodes = [];
          const newEdges = [];
          const agentNodesForSatellites = [];

          // Try list endpoint first; fall back to cached IDs fetched individually
          let triggersToRender = [];
          const apiTriggersResult = await WorkflowService.getWorkflowTriggers(activeWorkflowId, {
            account_id: currentAccount.id,
            is_active: undefined,
            limit: 100
          });

          if (apiTriggersResult.success && Array.isArray(apiTriggersResult.data?.triggers) && apiTriggersResult.data.triggers.length > 0) {
            triggersToRender = apiTriggersResult.data.triggers;
          } else {
            // Fall back to triggers embedded in the workflow GET response
            const workflowPayloadTriggers =
              Array.isArray(workflowResult.data?.triggers) ? workflowResult.data.triggers :
              (Array.isArray(workflow?.triggers) ? workflow.triggers : []);

            if (workflowPayloadTriggers.length > 0) {
              triggersToRender = workflowPayloadTriggers;
            } else {
              // Last resort: load each trigger individually using IDs cached in localStorage
              const cachedIds = JSON.parse(localStorage.getItem(`wf_triggers_${activeWorkflowId}`) || '[]');
              if (cachedIds.length > 0) {
                const fetched = await Promise.all(
                  cachedIds.map(id => WorkflowService.getTrigger(id))
                );
                triggersToRender = fetched
                  .filter(r => r.success && r.data?.trigger)
                  .map(r => r.data.trigger);
              }
            }
          }

          triggersToRender.forEach((trigger, index) => {
            if (!isPlainObject(trigger)) return;
            newNodes.push(buildTriggerNodeFromApi(trigger, index));
          });

          // Convertir steps a nodos
          const detailedSteps = Array.isArray(workflowResult.data?.steps) ? workflowResult.data.steps : [];
          const legacySteps = Array.isArray(workflow?.steps) ? workflow.steps : [];
          const stepsToRender = detailedSteps.length > 0 ? detailedSteps : legacySteps;

          if (stepsToRender.length > 0) {
            stepsToRender.forEach((rawStep, index) => {
              const step = isPlainObject(rawStep) ? rawStep : {};
              const stepParameters = isPlainObject(step.parameters)
                ? step.parameters
                : isPlainObject(step.configuration)
                  ? step.configuration
                  : {};
              const resolvedStepType =
                (typeof step.step_type === 'string' && step.step_type.trim()) ||
                (typeof stepParameters.step_type === 'string' && stepParameters.step_type.trim()) ||
                null;
              const nodeType = mapStepTypeToNodeType(resolvedStepType);
              const stepId = typeof step.id === 'string' && step.id ? step.id : `${index}`;
              const assistantId = step.assistant_id || stepParameters.assistant_id || null;
              
              // Normalizar tasks: convertir config_override de camelCase a snake_case
              const rawTasks = Array.isArray(stepParameters.tasks) ? stepParameters.tasks : (Array.isArray(step.tasks) ? step.tasks : []);
              const normalizedTasks = rawTasks.map(task => ({
                ...task,
                config_override: normalizeConfigOverride(task.config_override)
              }));
              
              // Para condition, los campos están directamente en parameters (no en parameters.condition)
              let configuration = stepParameters;
              if (resolvedStepType === 'condition') {
                configuration = {
                  ...stepParameters,
                  left: stepParameters.left || '',
                  operator: stepParameters.operator || 'eq',
                  right: stepParameters.right || ''
                };
              }
              
              const stepNode = {
                id: `step-${stepId}`,
                type: nodeType,
                position: step.position || { x: 400, y: 100 + (index * 150) },
                data: {
                  label: step.name || step.display_name || resolvedStepType || `Step ${index + 1}`,
                  sub: step.category || '',
                  step_id: step.id,
                  step_type: resolvedStepType,
                  assistant_id: assistantId,
                  tasks: normalizedTasks,
                  configuration: configuration,
                  // Snapshot crudo del step tal cual viene del backend. Se usa como
                  // source-of-truth de passthrough al re-serializar: preserva step_order,
                  // timeout_seconds de nivel step, on_max_iterations y cualquier campo
                  // que la UI no expone como editable. Sin esto, el round-trip los pierde.
                  rawStep: step,
                  icon: getStepIcon(resolvedStepType)
                }
              };
              newNodes.push(stepNode);

              if (nodeType === 'agent' && assistantId) {
                agentNodesForSatellites.push({
                  nodeId: stepNode.id,
                  assistantId,
                  position: stepNode.position,
                  label: stepNode.data.label
                });
              }

              // Crear edges desde el nodo anterior (solo si no hay edges guardados)
              if (index > 0 || newNodes.length > 1) {
                const sourceId = index === 0 && newNodes.length > 1
                  ? newNodes[newNodes.length - 2].id
                  : `step-${(isPlainObject(stepsToRender[index - 1]) && stepsToRender[index - 1].id) || index - 1}`;
                newEdges.push({
                  id: `edge-${sourceId}-to-${stepNode.id}`,
                  source: sourceId,
                  target: stepNode.id,
                  type: 'default',
                  style: { stroke: '#52525b', strokeWidth: 2 }
                });
              }
            });
          }

          if (agentNodesForSatellites.length > 0) {
            const assistantIds = [...new Set(agentNodesForSatellites.map((entry) => entry.assistantId).filter(Boolean))];
            const assistantsById = new Map();

            await Promise.all(assistantIds.map(async (assistantId) => {
              const assistantResult = await AssistantService.getAssistant(assistantId);
              if (assistantResult.success && assistantResult.data) {
                assistantsById.set(assistantId, assistantResult.data);
              }
            }));

            agentNodesForSatellites.forEach((agentEntry) => {
              const assistant = assistantsById.get(agentEntry.assistantId);
              
              // Update agent node with assistant info
              if (assistant) {
                const agentNodeIndex = newNodes.findIndex(n => n.id === agentEntry.nodeId);
                if (agentNodeIndex !== -1) {
                  newNodes[agentNodeIndex] = {
                    ...newNodes[agentNodeIndex],
                    data: {
                      ...newNodes[agentNodeIndex].data,
                      avatar_url: assistant.avatar || assistant.avatar_url || assistant.profile_image_url || null,
                      llm_id: assistant.llm_id,
                      model_name: assistant.model_name,
                      skills_count: assistant.skills?.length || 0
                    }
                  };
                }
              }
              
              const satelliteGraph = buildAgentSatelliteGraph({
                parentNodeId: agentEntry.nodeId,
                parentPosition: agentEntry.position,
                assistant: assistant,
                fallbackLabel: agentEntry.label,
                llmModels
              });
              if (satelliteGraph.nodes.length > 0) {
                newNodes.push(...satelliteGraph.nodes);
                newEdges.push(...satelliteGraph.edges);
              }
            });
          }

          // El backend solo persiste edges entre steps (no triggers ni satellites).
          // Por eso preservamos de newEdges todo lo que es UI-only: satellites y trigger→step.
          const uiOnlyEdges = newEdges.filter(e =>
            e.id.includes('satellite-') ||
            e.sourceHandle === 'satellites' ||
            (typeof e.source === 'string' && e.source.startsWith('trigger-'))
          );
          const savedEdges = workflowResult.data?.edges || workflow?.edges;
          const finalEdges = Array.isArray(savedEdges) && savedEdges.length > 0
            ? [
                ...savedEdges.map(edge => ({
                  id: edge.id || `edge-${edge.source}-to-${edge.target}`,
                  source: `step-${edge.source}`,
                  target: `step-${edge.target}`,
                  sourceHandle: edge.sourceHandle || null,
                  targetHandle: edge.targetHandle || null,
                  type: edge.type && edge.type !== 'bezier' ? edge.type : 'default',
                  style: edge.style || { stroke: '#52525b', strokeWidth: 2 }
                })),
                ...uiOnlyEdges
              ]
            : newEdges;

          // Reconstruct back-edges (loops) that were stripped before saving to avoid
          // CYCLIC_GRAPH errors. Condition steps encode loops via on_true/on_false
          // (step_order indices). If the edge is missing in finalEdges, rebuild it.
          const stepOrderToNodeId = new Map();
          stepsToRender.forEach(s => {
            if (isPlainObject(s) && s.id !== undefined && s.step_order !== undefined) {
              stepOrderToNodeId.set(s.step_order, `step-${s.id}`);
            }
          });
          stepsToRender.forEach(s => {
            if (!isPlainObject(s)) return;
            const params = isPlainObject(s.parameters) ? s.parameters : {};
            if (params.step_type !== 'condition' || !s.id) return;
            const condNodeId = `step-${s.id}`;
            [
              { handle: 'true', order: params.on_true },
              { handle: 'false', order: params.on_false }
            ].forEach(({ handle, order }) => {
              if (order === null || order === undefined) return;
              const targetNodeId = stepOrderToNodeId.get(order);
              if (!targetNodeId) return;
              const alreadyExists = finalEdges.some(
                e => e.source === condNodeId && e.sourceHandle === handle
              );
              if (!alreadyExists) {
                finalEdges.push({
                  id: `edge-${condNodeId}-${handle}-to-${targetNodeId}`,
                  source: condNodeId,
                  target: targetNodeId,
                  sourceHandle: handle,
                  targetHandle: null,
                  type: 'default',
                  style: { stroke: '#52525b', strokeWidth: 2 }
                });
              }
            });
          });

          setNodes(newNodes);
          setEdges(finalEdges);

          // Resolve the published version number.
          // The single GET /workflows/{id} response does not include version_number,
          // so fetch it from the versions endpoint when the workflow has been published.
          let resolvedVersionNumber = null;
          if (workflow.published_version_id) {
            const versionsResult = await WorkflowService.listWorkflowVersions(workflow.id || activeWorkflowId, { limit: 1 });
            if (versionsResult.success) {
              resolvedVersionNumber = versionsResult.data?.current_published_version_number ?? null;
            }
          }

          if (onWorkflowDataLoaded) {
            onWorkflowDataLoaded({
              name: workflow.name || 'Untitled Workflow',
              status: workflow.status || 'draft',
              version: resolvedVersionNumber,
              triggerCount: triggersToRender.length,
              steps: workflow.steps || []
            });
          }
        }
      } catch (error) {
        console.error('Error loading workflow:', error);
      } finally {
        setLoading(false);
      }
    };

    loadWorkflow();
  }, [activeWorkflowId, currentAccount?.id, projectId, setNodes, setEdges]);

  // Preload the most recent execution's step_results so the Data Selector
  // can show real output values for {{context:step_N.*}} templating right away
  // (without requiring the user to re-run the workflow in this session).
  useEffect(() => {
    if (!activeWorkflowId || !currentAccount?.id || !projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await WorkflowService.listExecutions({
          account_id: currentAccount.id,
          project_id: projectId,
          workflow_id: activeWorkflowId,
          status_filter: ['completed', 'failed'],
          limit: 1,
          sort_by: 'started_at',
          sort_order: 'desc',
        });
        if (cancelled || !list?.success) return;
        const latest = list.data?.executions?.[0];
        if (!latest?.id) return;
        const detail = await WorkflowService.getExecution(latest.id, {
          project_id: projectId,
          include_step_results: true,
          include_logs_summary: false,
        });
        if (cancelled || !detail.success) return;
        const stepResults = detail.data?.execution?.execution_context?.step_results || {};
        if (Object.keys(stepResults).length > 0) {
          setLastStepResults(prev =>
            Object.keys(prev || {}).length > 0 ? prev : stepResults
          );
        }
      } catch (err) {
        console.warn('Could not preload latest execution results:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [activeWorkflowId, currentAccount?.id, projectId]);

  // Helper para mapear step_type a node type
  const mapStepTypeToNodeType = (stepType) => {
    if (stepType === 'ai_agent') return 'agent';
    if (stepType === 'webhook' || stepType === 'scheduled' || stepType === 'manual' || stepType === 'shopify_event' || stepType === 'whatsapp_inbound') return 'trigger';
    if (stepType === 'condition' || stepType === 'if_else') return 'condition';
    return 'action';
  };
  // Helper para obtener icono de step
  const getStepIcon = (stepType) => {
    const icons = {
      'ai_agent': '✨',
      'http_request': '🌐',
      'database': '💾',
      'code_execution': '🐍',
      'condition': '⚡',
      'whatsapp_send': '💬',
      'email_send': '📧'
    };
    return icons[stepType] || '📦';
  };

  const [selectedNode, setSelectedNode] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  // Inline rename of a node's display name, triggered from the context menu.
  const [renamingNode, setRenamingNode] = useState(null); // { id, value }
  const [expandedCategories, setExpandedCategories] = useState({
    trigger: true,
    ai: false,
    data: false,
    control: false,
    notifications: false,
  });
  const [stepSearchQuery, setStepSearchQuery] = useState('');

  const createNodeId = useCallback(() => `node-${Date.now()}-${Math.floor(Math.random() * 100000)}`, []);

  const deleteNodesByIds = useCallback(async (nodeIds = []) => {
    if (isReadOnly) return;
    if (!nodeIds.length) return;

    const idsToDelete = new Set(nodeIds);

    // If a parent node (agent) is removed, also remove its satellites.
    nodes.forEach((node) => {
      const parentId = node?.data?.parentId;
      if (parentId && idsToDelete.has(parentId)) {
        idsToDelete.add(node.id);
      }
    });

    // Delete triggers from backend if they have trigger_id
    const triggersToDelete = nodes.filter(node => 
      idsToDelete.has(node.id) && 
      node.type === 'trigger' && 
      node.data?.trigger_id
    );

    for (const triggerNode of triggersToDelete) {
      try {
        await WorkflowService.deleteTrigger(triggerNode.data.trigger_id);
        // Remove from localStorage cache
        if (activeWorkflowId) {
          const cachedIds = JSON.parse(localStorage.getItem(`wf_triggers_${activeWorkflowId}`) || '[]');
          const updated = cachedIds.filter(id => id !== triggerNode.data.trigger_id);
          localStorage.setItem(`wf_triggers_${activeWorkflowId}`, JSON.stringify(updated));
        }
      } catch (error) {
        console.error('Failed to delete trigger:', error);
      }
    }

    setNodes((nds) => nds.filter((node) => !idsToDelete.has(node.id)));
    setEdges((eds) => eds.filter((edge) => !idsToDelete.has(edge.source) && !idsToDelete.has(edge.target)));

    if (selectedNode && idsToDelete.has(selectedNode.id)) {
      setSelectedNode(null);
    }

    setContextMenu(null);
  }, [nodes, selectedNode, setEdges, setNodes, activeWorkflowId]);

  const onConnect = useCallback((params) => {
    if (isReadOnly) return;
    setEdges((eds) => addEdge({
      ...params,
      type: "default",
      style: { stroke: "#52525b", strokeWidth: 2 }
    }, eds));
  }, [setEdges, isReadOnly]);

  // Delete selected edges on Delete/Backspace (guards against firing while typing)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isReadOnly) return;
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || document.activeElement?.isContentEditable) return;
      setEdges((eds) => {
        const selected = eds.filter((ed) => ed.selected);
        if (!selected.length) return eds;
        return eds.filter((ed) => !ed.selected);
      });
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isReadOnly, setEdges]);

  const onNodeClick = useCallback((event, node) => {
    setContextMenu(null);
  }, []);

  const onNodeDoubleClick = useCallback((event, node) => {
    // Skip satellite nodes always
    if (node.type === 'satellite') return;

    // Special editors for agent and scheduled trigger
    if (node.type === 'agent') {
      setContextMenu(null);
      setSelectedNode(null);
      setAgentTaskEditorNode(node);
    } else if (node.type === 'trigger' && node.data?.trigger_type === 'scheduled') {
      setContextMenu(null);
      setSelectedNode(null);
      setScheduleTriggerEditorNode(node);
    } else {
      // All other nodes use the unified step editor modal
      setContextMenu(null);
      setSelectedNode(null);
      setStepEditorNode(node);
    }
  }, [isReadOnly]);

  const onPaneClick = useCallback(() => {
    setContextMenu(null);
    setSelectedNode(null);
    setRenamingNode(null);
  }, []);

  const handlePinFromLastRun = useCallback(async (nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    const stepId = node?.data?.step_id;
    if (!stepId || !lastExecutionId) return;
    setContextMenu(null);
    try {
      const result = await WorkflowService.createTestPinFromExecution(activeWorkflowId, stepId, { execution_id: lastExecutionId });
      if (result.success) {
        setPinnedStepIds(prev => new Set([...prev, stepId]));
        toastSuccess('Pinned', 'Step output pinned for next test run.');
      } else {
        toastError('Pin failed', result.error || 'Failed to pin step output.');
      }
    } catch (err) {
      toastError('Pin failed', err.message || 'Failed to pin step output.');
    }
  }, [nodes, activeWorkflowId, lastExecutionId, toastSuccess, toastError]);

  const handleUnpinStep = useCallback(async (nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    const stepId = node?.data?.step_id;
    if (!stepId) return;
    setContextMenu(null);
    try {
      const result = await WorkflowService.deleteTestPin(activeWorkflowId, stepId);
      if (result.success) {
        setPinnedStepIds(prev => { const s = new Set(prev); s.delete(stepId); return s; });
        if (result.alreadyMissing) {
          toastSuccess('Unpinned', 'Pin had already been removed on the server.');
        } else {
          toastSuccess('Unpinned', 'Test pin removed.');
        }
      } else {
        toastError('Unpin failed', result.error || 'Failed to remove pin.');
      }
    } catch (err) {
      toastError('Unpin failed', err.message || 'Failed to remove pin.');
    }
  }, [nodes, activeWorkflowId, toastSuccess, toastError]);

  const onNodeContextMenu = useCallback((event, node) => {
    event.preventDefault();
    event.stopPropagation();
    if (isReadOnly) return;

    const menuWidth = 200;
    const menuHeight = 176;
    const x = Math.min(event.clientX, window.innerWidth - menuWidth - 12);
    const y = Math.min(event.clientY, window.innerHeight - menuHeight - 12);

    setContextMenu({
      nodeId: node.id,
      x: Math.max(12, x),
      y: Math.max(12, y),
    });
  }, []);

  const handleStartRename = useCallback((nodeId) => {
    setContextMenu(null);
    const target = nodes.find((n) => n.id === nodeId);
    if (!target || target.type === 'satellite') return;
    setRenamingNode({ id: nodeId, value: target.data?.label || '' });
  }, [nodes]);

  const handleCommitRename = useCallback(() => {
    setRenamingNode((current) => {
      if (!current) return null;
      const trimmed = current.value.trim();
      setNodes((nds) => nds.map((n) =>
        // Empty keeps the previous label so a node is never left blank.
        n.id === current.id ? { ...n, data: { ...n.data, label: trimmed || n.data?.label } } : n
      ));
      return null;
    });
  }, [setNodes]);

  const handleDuplicateNode = useCallback((nodeId) => {
    setNodes((nds) => {
      const sourceNode = nds.find((node) => node.id === nodeId);
      if (!sourceNode || sourceNode.type === 'satellite') return nds;

      const duplicateNode = {
        ...sourceNode,
        id: createNodeId(),
        selected: false,
        dragging: false,
        position: {
          x: sourceNode.position.x + 40,
          y: sourceNode.position.y + 40,
        },
      };

      return nds.concat(duplicateNode);
    });

    setContextMenu(null);
  }, [createNodeId, setNodes]);

  const handleDeleteNode = useCallback((nodeId) => {
    deleteNodesByIds([nodeId]);
  }, [deleteNodesByIds]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();

    if (isReadOnly) return;
    if (!rfInstance) return;

    const nodeData = event.dataTransfer.getData('application/reactflow');
    
    if (!nodeData) return;

    const stepInfo = JSON.parse(nodeData);
    const position = rfInstance.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });

    const nodeDataWithFixes = { ...(stepInfo.data || {}) };
    
    // Fix step names to be template-variable friendly (no spaces, no special chars)
    if (nodeDataWithFixes.step_type === 'docs_get_document') {
      nodeDataWithFixes.label = 'docs_get_document';
    }

    const newNode = {
      id: createNodeId(),
      type: stepInfo.type,
      position,
      data: nodeDataWithFixes
    };

    setNodes((nds) => nds.concat(newNode));
  }, [createNodeId, rfInstance, setNodes]);

  useEffect(() => {
    if (!selectedNode) return;

    const refreshedSelection = nodes.find((node) => node.id === selectedNode.id);
    if (!refreshedSelection) {
      setSelectedNode(null);
      return;
    }

    if (refreshedSelection !== selectedNode) {
      setSelectedNode(refreshedSelection);
    }
  }, [nodes, selectedNode]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!selectedNode) return;

      const key = event.key;
      if (key !== 'Delete' && key !== 'Backspace') return;

      const target = event.target;
      const tagName = target?.tagName?.toLowerCase?.();
      const isEditable = target?.isContentEditable || tagName === 'input' || tagName === 'textarea' || tagName === 'select';
      if (isEditable) return;

      event.preventDefault();
      deleteNodesByIds([selectedNode.id]);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [deleteNodesByIds, selectedNode]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  // Transformar step catalog a stepCategories
  const stepCategories = React.useMemo(() => {
    // Emoji fallback por trigger_type (para cuando el catálogo no trae icon)
    const TRIGGER_ICON_FALLBACK = {
      webhook: '🔗', scheduled: '⏰', manual: '📋',
      shopify_event: '🛍️', whatsapp_inbound: '💬',
    };
    // Configuración inicial por trigger_type
    const TRIGGER_DEFAULT_CONFIG = {
      webhook: { path: '', method: 'POST', trigger_type: 'webhook' },
      scheduled: { cron_expression: '0 9 * * *', timezone: 'UTC' },
    };

    // Hardcoded fallback (si el catálogo aún no ha cargado o falla)
    const HARDCODED_TRIGGERS = [
      { type: "trigger", icon: "🔗", label: "Webhook", description: "HTTP POST starts the flow", data: { label: "On webhook", sub: "HTTP POST", trigger_type: "webhook", configuration: { path: "", method: "POST", trigger_type: "webhook" } } },
      { type: "trigger", icon: "⏰", label: "Schedule", description: "Cron expression", data: { label: "On schedule", sub: "cron", trigger_type: "scheduled", configuration: { cron_expression: "0 9 * * *", timezone: "UTC" } } },
      { type: "trigger", icon: "📋", label: "Manual", description: "Runs on demand", data: { label: "On manual", sub: "user trigger", trigger_type: "manual" } },
      { type: "trigger", icon: "🛍️", label: "Shopify", description: "Store event", data: { label: "On order", sub: "shopify", trigger_type: "shopify_event" } },
      { type: "trigger", icon: "💬", label: "WhatsApp", description: "Inbound message", data: { label: "On message", sub: "whatsapp", trigger_type: "whatsapp_inbound" } },
      { type: "trigger", icon: "🔁", label: "Callable by Workflow", description: "Called by another workflow", data: { label: "On call", sub: "inter-workflow", trigger_type: "executable_by_workflow", configuration: { trigger_type: "executable_by_workflow", enabled: true, inputs: [] } } },
    ];

    // Construir trigger steps desde el catálogo del backend
    const triggerSteps = triggerCatalog?.entries?.length
      ? triggerCatalog.entries.map((entry) => ({
          type: 'trigger',
          icon: entry.icon || TRIGGER_ICON_FALLBACK[entry.trigger_type] || '⚡',
          label: entry.display_name || entry.trigger_type,
          description: entry.description || '',
          data: {
            label: entry.display_name || entry.trigger_type,
            sub: entry.category || '',
            trigger_type: entry.trigger_type,
            schema: entry.json_schema,
            configuration: TRIGGER_DEFAULT_CONFIG[entry.trigger_type] || {},
          },
        }))
      : HARDCODED_TRIGGERS;

    const triggerCategory = {
      key: "trigger",
      title: "Trigger",
      steps: triggerSteps,
    };

    if (!stepCatalog?.entries) {
      // Fallback a categorías por defecto
      return [
        triggerCategory,
        {
          key: "ai",
          title: "AI",
          steps: [
            { type: "agent", icon: "✨", label: "AI Agent", description: "Agent with tools", data: { label: "AI Agent", sub: "AI agent" } },
          ],
        },
        {
          key: "data",
          title: "Data",
          steps: [
            { type: "action", icon: "🌐", label: "API call", description: "HTTP request", data: { label: "API call", sub: "GET /api", icon: "🌐" } },
            { type: "action", icon: "💾", label: "Database", description: "CRUD on your DB", data: { label: "Save data", sub: "database", icon: "💾" } },
            { type: "action", icon: "🐍", label: "Code (Python)", description: "Sandboxed Lambda", data: { label: "Run code", sub: "python", icon: "🐍" } },
          ],
        },
        {
          key: "control",
          title: "Control flow",
          steps: [
            { type: "condition", icon: "⚡", label: "Condition", description: "if / else", data: { label: "Condition", sub: "Control Flow", step_type: "condition", configuration: { operator: "eq" } } },
          ],
        },
        {
          key: "notifications",
          title: "Notifications",
          steps: [
            { type: "action", icon: "💬", label: "WhatsApp", description: "Send message", data: { label: "Send message", sub: "whatsapp", icon: "💬" } },
            { type: "action", icon: "📧", label: "Email", description: "Send email", data: { label: "Send email", sub: "SMTP", icon: "📧" } },
          ],
        },
      ];
    }

    // Mapear step_type del API a tipos de nodos visuales
    const mapStepTypeToNodeType = (stepType) => {
      if (stepType === 'ai_agent') return 'agent';
      if (stepType === 'webhook' || stepType === 'scheduled' || stepType === 'manual' || stepType === 'shopify_event' || stepType === 'whatsapp_inbound') return 'trigger';
      if (stepType === 'condition' || stepType === 'if_else') return 'condition';
      return 'action'; // Por defecto, todo lo demás es action (http_request, database, code_execution, etc.)
    };

    // Agrupar entries por categoría (sin incluir triggers, ya los tenemos arriba)
    const categoriesMap = {};
    stepCatalog.entries.forEach(entry => {
      // Saltar triggers del catálogo, ya los agregamos manualmente
      if (entry.step_type === 'webhook' || entry.step_type === 'scheduled' || entry.step_type === 'manual' || 
          entry.step_type === 'shopify_event' || entry.step_type === 'whatsapp_inbound') {
        return;
      }

      const category = entry.category || 'other';
      if (!categoriesMap[category]) {
        categoriesMap[category] = {
          key: category.toLowerCase().replace(/\s+/g, '_'),
          title: category.charAt(0).toUpperCase() + category.slice(1),
          steps: []
        };
      }

      const nodeType = mapStepTypeToNodeType(entry.step_type);

      categoriesMap[category].steps.push({
        type: nodeType,
        icon: entry.icon || '📦',
        label: entry.display_name,
        description: entry.description || '',
        data: { 
          label: entry.display_name,
          sub: entry.category || '',
          step_type: entry.step_type,
          icon: entry.icon,
          schema: entry.json_schema
        }
      });
    });

    return [triggerCategory, ...Object.values(categoriesMap)];
  }, [stepCatalog, triggerCatalog]);

  // Filter steps by search query and group HTTP & Integrations by service.
  const displayedStepCategories = React.useMemo(() => {
    const q = stepSearchQuery.trim().toLowerCase();
    const matchesQuery = (step) => {
      if (!q) return true;
      const haystack = [
        step.label,
        step.description,
        step?.data?.step_type,
        step?.data?.trigger_type,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    };

    const result = [];
    for (const category of stepCategories) {
      const filteredSteps = category.steps.filter(matchesQuery);
      if (filteredSteps.length === 0) continue;
      const next = { ...category, steps: filteredSteps };
      if (isHttpIntegrationsCategory(category)) {
        next.groups = groupStepsByService(filteredSteps);
      }
      result.push(next);
    }
    return result;
  }, [stepCategories, stepSearchQuery]);

  // Handlers para botones de acción
  const handleSaveDraft = async () => {
    if (isReadOnly) return;
    if (!currentAccount?.id || !projectId) return;
    
    setSaving(true);
    try {
      const actingUserId = user?.id || user?.user_id || user?.userId || user?.sub || null;

      // Separar triggers de steps
      const triggerNodes = nodes.filter(node => node.type === 'trigger');
      const allStepNodes = nodes.filter(node => node.type !== 'trigger' && node.type !== 'satellite');

      // Ordenar step nodes siguiendo el grafo de edges desde el trigger (BFS),
      // para que step_order refleje el orden de ejecución real y no el orden de inserción en el canvas.
      const stepNodes = (() => {
        const stepIdSet = new Set(allStepNodes.map(n => n.id));
        const adj = new Map();
        allStepNodes.forEach(n => adj.set(n.id, []));
        edges.forEach(e => {
          if (stepIdSet.has(e.source) && stepIdSet.has(e.target)) {
            adj.get(e.source).push(e.target);
          }
        });

        // Nodos conectados directamente desde un trigger
        const startIds = [];
        edges.forEach(e => {
          if (!stepIdSet.has(e.source) && stepIdSet.has(e.target)) {
            startIds.push(e.target);
          }
        });
        // Fallback: nodos sin incoming edges de otros steps
        if (startIds.length === 0) {
          const hasIncoming = new Set();
          edges.forEach(e => { if (stepIdSet.has(e.target)) hasIncoming.add(e.target); });
          allStepNodes.forEach(n => { if (!hasIncoming.has(n.id)) startIds.push(n.id); });
        }

        const visited = new Set();
        const ordered = [];
        const queue = [...startIds];
        while (queue.length > 0) {
          const id = queue.shift();
          if (visited.has(id)) continue;
          visited.add(id);
          const node = allStepNodes.find(n => n.id === id);
          if (node) ordered.push(node);
          (adj.get(id) || []).forEach(tid => { if (!visited.has(tid)) queue.push(tid); });
        }
        // Agregar nodos desconectados al final
        allStepNodes.forEach(n => { if (!visited.has(n.id)) ordered.push(n); });
        return ordered;
      })();

      const serializedTriggerEntries = triggerNodes.map((triggerNode, index) => ({
        nodeId: triggerNode.id,
        triggerId: triggerNode?.data?.trigger_id || null,
        position: triggerNode.position || null,
        payload: normalizeWorkflowTriggerForRequest(triggerNode, index)
      }));
      const serializedSteps = [];
      const unsupportedStepNodes = [];
      const nodeIdToStepIdMap = new Map(); // Mapeo de node.id del canvas a step.id del backend

      stepNodes.forEach((node) => {
        const serialized = serializeWorkflowStep(node);
        if (serialized) {
          if (serialized.step_type === 'ai_agent' && !serialized.assistant_id) {
            unsupportedStepNodes.push({
              id: node.id,
              type: node.type,
              reason: 'ai_agent requires assistant_id'
            });
            return;
          }
          // Guardar mapping: nodeId -> stepId (existente o será generado)
          const stepId = serialized.id || node.data?.step_id || generateUUID();
          if (!serialized.id) {
            serialized.id = stepId;
          }
          // Adjuntar el snapshot crudo para el passthrough en normalize().
          serialized._rawStep = isPlainObject(node.data?.rawStep) ? node.data.rawStep : null;
          nodeIdToStepIdMap.set(node.id, stepId);
          serializedSteps.push(serialized);
          return;
        }

        // UI-only nodes like satellites must never be sent to backend.
        if (node.type !== 'satellite') {
          unsupportedStepNodes.push({
            id: node.id,
            type: node.type,
            reason: 'unsupported step type for backend'
          });
        }
      });

      if (unsupportedStepNodes.length > 0) {
        const unsupportedSummary = unsupportedStepNodes
          .map((node) => `${node.id}(${node.type}): ${node.reason}`)
          .join(', ');
        throw new Error(`Unsupported step types in canvas: ${unsupportedSummary}`);
      }

      // Validate required fields per step type before hitting the API
      const incompleteSteps = serializedSteps.filter((step) => {
        // La rama genérica de serialización anida los campos bajo `parameters`,
        // así que los campos requeridos viven ahí (con fallback a la raíz).
        const cfg = isPlainObject(step?.parameters) ? step.parameters : step;
        if (step.step_type === 'sheets_write_range') {
          return !cfg.spreadsheet_id || !cfg.range || !cfg.values ||
            (Array.isArray(cfg.values) && cfg.values.length === 0);
        }
        if (step.step_type === 'sheets_append_row') {
          return !cfg.spreadsheet_id || !cfg.range || !cfg.values;
        }
        if (step.step_type === 'gmail_send') {
          return !cfg.to || !cfg.subject || (!cfg.body_text && !cfg.body_html);
        }
        return false;
      });

      if (incompleteSteps.length > 0) {
        const names = incompleteSteps
          .map((s) => s.name || s.step_type)
          .join(', ');
        throw new Error(`Complete all required fields before saving: ${names}`);
      }

      if (stepNodes.length > 0 && serializedSteps.length === 0) {
        throw new Error('No valid backend steps found. Configure at least one valid step before saving.');
      }

      let savedWorkflowId = activeWorkflowId;
      const incomingWorkflowName = typeof workflowName === 'string' ? workflowName.trim() : '';
      let currentWorkflowName = incomingWorkflowName || workflowData?.name || 'New Workflow';
      let currentWorkflowStatus = workflowData?.status || 'draft';
      let currentWorkflowVersion = workflowData?.version || null;
      const requestWorkflowId =
        (typeof savedWorkflowId === 'string' && WorkflowService.isValidUUID(savedWorkflowId) && savedWorkflowId) ||
        (typeof workflowData?.id === 'string' && WorkflowService.isValidUUID(workflowData.id) && workflowData.id) ||
        (typeof workflowData?.workflow_id === 'string' && WorkflowService.isValidUUID(workflowData.workflow_id) && workflowData.workflow_id) ||
        generateUUID();
      // Asignar step_order de forma estable: preservar el orden original del snapshot
      // para los steps existentes y anexar los nuevos al final. NO renumerar todo el
      // grafo en cada guardado evita dejar colgados los punteros que referencian
      // step_order pero que la UI no maneja como edges (p. ej. on_max_iterations).
      const usedStepOrders = new Set();
      serializedSteps.forEach((s) => {
        const original = s?._rawStep?.step_order;
        if (Number.isInteger(original)) usedStepOrders.add(original);
      });
      let nextStepOrder = (usedStepOrders.size ? Math.max(...usedStepOrders) : -1) + 1;
      serializedSteps.forEach((s) => {
        const original = s?._rawStep?.step_order;
        if (Number.isInteger(original)) {
          s.step_order = original;
        } else {
          while (usedStepOrders.has(nextStepOrder)) nextStepOrder += 1;
          s.step_order = nextStepOrder;
          usedStepOrders.add(nextStepOrder);
          nextStepOrder += 1;
        }
      });

      const serializedStepsForRequest = serializedSteps.map((step, index) =>
        normalizeWorkflowStepForRequest(step, index, requestWorkflowId)
      );

      // Filtrar y mapear edges: solo edges entre steps reales (no satélites, no triggers)
      const workflowEdges = edges
        .filter(edge => {
          // Excluir edges de satélites
          if (edge.id.includes('satellite-') || edge.sourceHandle === 'satellites') {
            return false;
          }
          // Solo incluir si ambos source y target están en el mapa
          return nodeIdToStepIdMap.has(edge.source) && nodeIdToStepIdMap.has(edge.target);
        })
        .map(edge => ({
          id: edge.id,
          source: nodeIdToStepIdMap.get(edge.source),
          target: nodeIdToStepIdMap.get(edge.target),
          sourceHandle: edge.sourceHandle || null,
          targetHandle: edge.targetHandle || null,
          type: edge.type && edge.type !== 'bezier' ? edge.type : 'default'
        }));

      // Derivar on_true / on_false en condition steps a partir de los handles de los edges.
      // Sin esto, el runner recibe null y no sabe qué rama tomar.
      const stepOrderById = new Map(
        serializedStepsForRequest
          .filter(s => s.id)
          .map(s => [s.id, s.step_order])
      );
      serializedStepsForRequest.forEach(step => {
        if (step?.parameters?.step_type !== 'condition' || !step.id) return;
        const outgoing = workflowEdges.filter(e => e.source === step.id);
        const trueEdge = outgoing.find(e => e.sourceHandle === 'true');
        const falseEdge = outgoing.find(e => e.sourceHandle === 'false');
        if (trueEdge && stepOrderById.has(trueEdge.target)) {
          step.parameters.on_true = stepOrderById.get(trueEdge.target);
        }
        if (falseEdge && stepOrderById.has(falseEdge.target)) {
          step.parameters.on_false = stepOrderById.get(falseEdge.target);
        }
      });

      // Remapear on_max_iterations (y cualquier puntero a step_order que NO sea un edge
      // del canvas). on_max_iterations no se reconstruye desde los handles, así que su
      // valor entero quedaría colgado si los step_order cambian. Lo traducimos a través
      // del snapshot original: orden viejo -> step.id -> orden nuevo. Con step_order
      // preservado esto es identidad (no-op), pero blinda el caso de renumeración.
      const originalOrderToStepId = new Map();
      serializedSteps.forEach((s) => {
        const original = s?._rawStep?.step_order;
        if (Number.isInteger(original) && s.id) {
          originalOrderToStepId.set(original, s.id);
        }
      });
      serializedStepsForRequest.forEach(step => {
        if (step?.parameters?.step_type !== 'condition') return;
        const oldTarget = step.parameters.on_max_iterations;
        if (!Number.isInteger(oldTarget)) return;
        const targetStepId = originalOrderToStepId.get(oldTarget);
        if (!targetStepId) return;
        const newOrder = stepOrderById.get(targetStepId);
        if (newOrder !== undefined) {
          step.parameters.on_max_iterations = newOrder;
        }
      });

      // Back-edges (where target step_order <= source step_order) are already encoded
      // in condition step parameters (on_true/on_false). Exclude them from the edges
      // array to avoid CYCLIC_GRAPH rejection from the backend.
      const workflowEdgesForApi = workflowEdges.filter(edge => {
        const sourceOrder = stepOrderById.get(edge.source);
        const targetOrder = stepOrderById.get(edge.target);
        if (sourceOrder !== undefined && targetOrder !== undefined) {
          return targetOrder > sourceOrder;
        }
        return true;
      });

      if (savedWorkflowId && !actingUserId) {
        throw new Error('Missing authenticated user ID required to update workflow');
      }

      if (savedWorkflowId) {
        // Actualizar workflow existente
        const updateResult = await WorkflowService.updateWorkflow(savedWorkflowId, {
          workflow_id: savedWorkflowId,
          account_id: currentAccount.id,
          project_id: projectId,
          user_id: actingUserId,
          name: currentWorkflowName,
          steps: serializedStepsForRequest,
          edges: workflowEdgesForApi
        });

        if (!updateResult.success) {
          throw new Error(updateResult.error || 'Unable to update workflow');
        }

        // Recargar workflow completo para obtener steps con position actualizada
        const reloadResult = await WorkflowService.getWorkflow(savedWorkflowId, projectId);
        if (reloadResult.success && reloadResult.data) {
          const reloadedWorkflow = reloadResult.data.workflow || reloadResult.data;
          setWorkflowData(reloadedWorkflow);
          currentWorkflowName = reloadedWorkflow.name || currentWorkflowName;
          currentWorkflowStatus = reloadedWorkflow.status || currentWorkflowStatus;
          currentWorkflowVersion = reloadedWorkflow.published_version_number ?? reloadedWorkflow.current_published_version_number ?? reloadedWorkflow.version ?? currentWorkflowVersion;

          // Actualizar posiciones en canvas desde backend
          const detailedSteps = Array.isArray(reloadResult.data?.steps) ? reloadResult.data.steps : [];
          if (detailedSteps.length > 0) {
            setNodes((nds) => nds.map((node) => {
              if (node.type === 'trigger' || node.type === 'satellite') return node;
              const backendStep = detailedSteps.find(s => s.id === node.data?.step_id);
              if (backendStep) {
                const updates = {};
                if (backendStep.position) updates.position = backendStep.position;

                // Refrescar el snapshot crudo con lo que el backend acaba de persistir,
                // para que un segundo guardado en la misma sesión preserve los step_order
                // y campos de passthrough vigentes (no los del snapshot inicial, ya viejo).
                const nextData = { ...node.data, rawStep: backendStep };

                // Normalizar tasks con config_override del backend
                if (backendStep.parameters?.tasks) {
                  nextData.tasks = backendStep.parameters.tasks.map(task => ({
                    ...task,
                    config_override: normalizeConfigOverride(task.config_override)
                  }));
                }
                updates.data = nextData;

                return { ...node, ...updates };
              }
              return node;
            }));
          }
        } else if (updateResult.data?.workflow) {
          setWorkflowData(updateResult.data.workflow);
          currentWorkflowName = updateResult.data.workflow.name || currentWorkflowName;
          currentWorkflowStatus = updateResult.data.workflow.status || currentWorkflowStatus;
          currentWorkflowVersion = updateResult.data.workflow.published_version_number ?? updateResult.data.workflow.current_published_version_number ?? updateResult.data.workflow.version ?? currentWorkflowVersion;
        }
      } else {
        // Crear nuevo workflow
        const result = await WorkflowService.createWorkflow({
          id: requestWorkflowId,
          account_id: currentAccount.id,
          project_id: projectId,
          name: currentWorkflowName,
          description: '',
          steps: serializedStepsForRequest,
          tags: [],
          edges: workflowEdgesForApi
        });
        
        const createdWorkflowId =
          result?.data?.workflow?.id ||
          result?.data?.workflow?.workflow_id ||
          result?.data?.workflow_id ||
          null;

        if (result.success && createdWorkflowId) {
          savedWorkflowId = createdWorkflowId;
          setActiveWorkflowId(savedWorkflowId);

          if (result.data?.workflow && typeof result.data.workflow === 'object') {
            setWorkflowData(result.data.workflow);
            currentWorkflowName = result.data.workflow.name || currentWorkflowName;
            currentWorkflowStatus = result.data.workflow.status || currentWorkflowStatus;
            currentWorkflowVersion = result.data.workflow.published_version_number ?? result.data.workflow.current_published_version_number ?? result.data.workflow.version ?? currentWorkflowVersion;
          } else {
            setWorkflowData((prev) => ({
              ...(prev || {}),
              id: savedWorkflowId,
              name: currentWorkflowName,
              status: currentWorkflowStatus,
              version: currentWorkflowVersion
            }));
          }

          window.history.replaceState(null, '', `?workflowId=${savedWorkflowId}&projectId=${projectId}`);
        } else {
          throw new Error(result.error || 'Unable to create workflow');
        }
      }

      if (!WorkflowService.isValidUUID(savedWorkflowId)) {
        throw new Error('Invalid workflow_id returned while saving draft');
      }

      // Actualizar step_id en los nodos del canvas con los IDs del backend
      setNodes((nds) => nds.map((node) => {
        const stepId = nodeIdToStepIdMap.get(node.id);
        if (stepId && node.type !== 'trigger' && node.type !== 'satellite') {
          return {
            ...node,
            data: {
              ...node.data,
              step_id: stepId
            }
          };
        }
        return node;
      }));

      // Persist triggers using dedicated triggers endpoint after workflow ID exists.
      if (serializedTriggerEntries.length > 0) {
        for (const entry of serializedTriggerEntries) {
          if (entry.triggerId) {
            const updateTriggerResult = await WorkflowService.updateTrigger(entry.triggerId, {
              workflow_id: savedWorkflowId,
              account_id: currentAccount.id,
              project_id: projectId,
              configuration: entry.payload,
              is_active: true,
              position: entry.position
            });
            if (!updateTriggerResult.success) {
              throw new Error(updateTriggerResult.error || `Unable to update trigger ${entry.triggerId}`);
            }
            // keep existing trigger ID cached
            const exCache = JSON.parse(localStorage.getItem(`wf_triggers_${savedWorkflowId}`) || '[]');
            if (!exCache.includes(entry.triggerId)) {
              localStorage.setItem(`wf_triggers_${savedWorkflowId}`, JSON.stringify([...exCache, entry.triggerId]));
            }
            continue;
          }

          const createTriggerResult = await WorkflowService.createTrigger({
            id: generateUUID(),
            workflow_id: savedWorkflowId,
            account_id: currentAccount.id,
            project_id: projectId,
            configuration: entry.payload,
            is_active: true,
            position: entry.position
          });
          if (!createTriggerResult.success) {
            throw new Error(createTriggerResult.error || 'Unable to create trigger');
          }

          const createdTriggerId = createTriggerResult?.data?.trigger?.id || null;
          if (createdTriggerId) {
            setNodes((nds) => nds.map((node) => {
              if (node.id !== entry.nodeId) return node;
              return {
                ...node,
                data: {
                  ...node.data,
                  trigger_id: createdTriggerId
                }
              };
            }));
            // persist new trigger ID so it can be loaded on re-open
            const newCache = JSON.parse(localStorage.getItem(`wf_triggers_${savedWorkflowId}`) || '[]');
            if (!newCache.includes(createdTriggerId)) {
              localStorage.setItem(`wf_triggers_${savedWorkflowId}`, JSON.stringify([...newCache, createdTriggerId]));
            }
          }
        }
      }

      if (savedWorkflowId) {
        setActiveWorkflowId(savedWorkflowId);
      }

      if (onWorkflowDataLoaded) {
        onWorkflowDataLoaded({
          name: currentWorkflowName,
          status: currentWorkflowStatus,
          version: currentWorkflowVersion,
          triggerCount: triggerNodes.length,
          steps: workflowData?.steps || []
        });
      }

      if (savedWorkflowId === activeWorkflowId && workflowId) {
        toastSuccess('Updated', 'Workflow updated successfully.');
      } else {
        toastSuccess('Draft saved', 'Workflow saved as draft.');
      }

      // Resync test pins from backend: workflow updates can orphan/clear pins,
      // so the local set may be out of date. Refetching keeps the UI honest.
      if (savedWorkflowId) {
        reloadTestPins(savedWorkflowId);
      }
    } catch (err) {
      console.error('Error saving workflow:', err);
      toastError('Save failed', err.message || 'Unable to save workflow.');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = () => {
    if (!activeWorkflowId || !currentAccount?.id || !projectId) return;
    setShowTestModal(true);
  };

  const handleTestExecution = async (inputParameters) => {
    setShowTestModal(false);

    if (!activeWorkflowId || !currentAccount?.id || !projectId) return;

    // Limpiar estado anterior de ejecución
    setNodeExecutionState({});
    if (activeStreamController) {
      activeStreamController.abort();
      setActiveStreamController(null);
    }

    setTesting(true);
    try {
      // Workflows activos (publicados) usan /executions/ para correr la versión publicada.
      // Workflows en draft usan /test-executions que siempre lee del draft table.
      const isActive = workflowData?.status === 'active';
      const result = isActive
        ? await WorkflowService.startExecution({
            account_id: currentAccount.id,
            project_id: projectId,
            workflow_id: activeWorkflowId,
            input_parameters: inputParameters
          })
        : await WorkflowService.testExecution({
            account_id: currentAccount.id,
            project_id: projectId,
            workflow_id: activeWorkflowId,
            input_parameters: inputParameters
          });

      const executionId = result?.data?.execution_id || result?.data?.execution?.execution_id;
      if (result.success && executionId) {
        setLastExecutionId(executionId);
        toastInfo('Test started', `Execution ${executionId} is running.`);

        const stepNodes = nodes.filter(n => n.data?.step_id && n.type !== 'trigger' && n.type !== 'satellite');
        const orderedStepIds = stepNodes
          .sort((a, b) => (a.position?.x || 0) - (b.position?.x || 0))
          .map(n => n.data.step_id);

        // Optimistic spinner on the first step for instant visual feedback;
        // SSE / snapshot reconciliation will correct it.
        if (orderedStepIds.length > 0) {
          flushSync(() => {
            setNodeExecutionState({
              [orderedStepIds[0]]: { status: 'running', timestamp: new Date().toISOString() }
            });
          });
        }

        let executionTerminated = false;
        // Fallback positional pointers for backends that don't include step_id in events.
        let runningIdx = 0;   // index of the next step_started to map
        let completedIdx = 0; // index of the next step_completed/failed/skipped to map

        const finalize = async () => {
          if (executionTerminated) return;
          executionTerminated = true;
          await reconcileFromSnapshot(executionId);
          setTesting(false);
          setActiveStreamController(null);
        };

        const streamController = await WorkflowService.streamExecutionUpdates(
          executionId,
          projectId,
          (event) => {
            const explicitStepId = event.step_id;
            const isStepEvent = event.event_type === 'step_started'
              || event.event_type === 'step_completed'
              || event.event_type === 'step_failed'
              || event.event_type === 'step_skipped';

            // Resolve which canvas node this event refers to.
            let stepId = explicitStepId;
            if (!stepId && isStepEvent) {
              const idx = event.event_type === 'step_started' ? runningIdx : completedIdx;
              if (idx < orderedStepIds.length) stepId = orderedStepIds[idx];
            }

            if (event.event_type === 'step_started') {
              if (stepId) {
                flushSync(() => {
                  setNodeExecutionState(prev => ({
                    ...prev,
                    [stepId]: { status: 'running', timestamp: event.timestamp || new Date().toISOString() }
                  }));
                });
              }
              if (!explicitStepId) runningIdx++;
            } else if (event.event_type === 'step_completed') {
              if (stepId) {
                setNodeExecutionState(prev => ({
                  ...prev,
                  [stepId]: { status: 'completed', timestamp: event.timestamp || new Date().toISOString() }
                }));
              }
              if (!explicitStepId) completedIdx++;
            } else if (event.event_type === 'step_failed') {
              if (stepId) {
                setNodeExecutionState(prev => ({
                  ...prev,
                  [stepId]: { status: 'failed', timestamp: event.timestamp || new Date().toISOString() }
                }));
              }
              if (!explicitStepId) completedIdx++;
            } else if (event.event_type === 'step_skipped') {
              if (stepId) {
                setNodeExecutionState(prev => ({
                  ...prev,
                  [stepId]: { status: 'completed', timestamp: event.timestamp || new Date().toISOString() }
                }));
              }
              if (!explicitStepId) completedIdx++;
            } else if (event.event_type === 'execution_completed' || event.event_type === 'execution_failed') {
              if (streamController) streamController.abort();
              finalize();
            }
          },
          (error) => {
            console.error('❌ SSE Stream error:', error);
            finalize();
          },
          () => {
            finalize();
          }
        );

        setActiveStreamController(streamController);
      } else if (!result.success) {
        toastError('Test failed', result.error || 'Unable to start test execution.');
        setTesting(false);
      }
    } catch (err) {
      console.error('Error testing workflow:', err);
      toastError('Test failed', err.message || 'Unable to start test execution.');
      setTesting(false);
    }
  };

  const handleRun = async () => {
    if (!activeWorkflowId || !currentAccount?.id || !projectId) return;

    setNodeExecutionState({});
    if (activeStreamController) {
      activeStreamController.abort();
      setActiveStreamController(null);
    }

    setRunning(true);
    try {
      const result = await WorkflowService.startExecution({
        account_id: currentAccount.id,
        project_id: projectId,
        workflow_id: activeWorkflowId,
        input_parameters: {}
      });

      const executionId = result?.data?.execution_id || result?.data?.execution?.execution_id;
      if (result.success && executionId) {
        toastSuccess('Run started', `Execution ${executionId} is running.`);

        const stepNodes = nodes.filter(n => n.data?.step_id && n.type !== 'trigger' && n.type !== 'satellite');
        const orderedStepIds = stepNodes
          .sort((a, b) => (a.position?.x || 0) - (b.position?.x || 0))
          .map(n => n.data.step_id);

        if (orderedStepIds.length > 0) {
          flushSync(() => {
            setNodeExecutionState({
              [orderedStepIds[0]]: { status: 'running', timestamp: new Date().toISOString() }
            });
          });
        }

        let executionTerminated = false;
        let runningIdx = 0;
        let completedIdx = 0;

        const finalize = async () => {
          if (executionTerminated) return;
          executionTerminated = true;
          await reconcileFromSnapshot(executionId);
          setRunning(false);
          setActiveStreamController(null);
        };

        const streamController = await WorkflowService.streamExecutionUpdates(
          executionId,
          projectId,
          (event) => {
            const explicitStepId = event.step_id;
            const isStepEvent = event.event_type === 'step_started'
              || event.event_type === 'step_completed'
              || event.event_type === 'step_failed'
              || event.event_type === 'step_skipped';

            let stepId = explicitStepId;
            if (!stepId && isStepEvent) {
              const idx = event.event_type === 'step_started' ? runningIdx : completedIdx;
              if (idx < orderedStepIds.length) stepId = orderedStepIds[idx];
            }

            if (event.event_type === 'step_started') {
              if (stepId) {
                flushSync(() => {
                  setNodeExecutionState(prev => ({
                    ...prev,
                    [stepId]: { status: 'running', timestamp: event.timestamp || new Date().toISOString() }
                  }));
                });
              }
              if (!explicitStepId) runningIdx++;
            } else if (event.event_type === 'step_completed') {
              if (stepId) {
                setNodeExecutionState(prev => ({
                  ...prev,
                  [stepId]: { status: 'completed', timestamp: event.timestamp || new Date().toISOString() }
                }));
              }
              if (!explicitStepId) completedIdx++;
            } else if (event.event_type === 'step_failed') {
              if (stepId) {
                setNodeExecutionState(prev => ({
                  ...prev,
                  [stepId]: { status: 'failed', timestamp: event.timestamp || new Date().toISOString() }
                }));
              }
              if (!explicitStepId) completedIdx++;
            } else if (event.event_type === 'step_skipped') {
              if (stepId) {
                setNodeExecutionState(prev => ({
                  ...prev,
                  [stepId]: { status: 'completed', timestamp: event.timestamp || new Date().toISOString() }
                }));
              }
              if (!explicitStepId) completedIdx++;
            } else if (event.event_type === 'execution_completed' || event.event_type === 'execution_failed') {
              if (streamController) streamController.abort();
              finalize();
            }
          },
          (error) => {
            console.error('❌ SSE Stream error:', error);
            finalize();
          },
          () => {
            finalize();
          }
        );

        setActiveStreamController(streamController);
      } else if (!result.success) {
        toastError('Run failed', result.error || 'Unable to start execution.');
        setRunning(false);
      }
    } catch (err) {
      console.error('Error running workflow:', err);
      toastError('Run failed', err.message || 'Unable to start execution.');
      setRunning(false);
    }
  };

  const handlePublish = async () => {
    if (!activeWorkflowId) return;
    
    setPublishing(true);
    try {
      const validationResult = await WorkflowService.validateWorkflow(activeWorkflowId, workflowData || {});
      if (!validationResult.success) {
        throw new Error(validationResult.error || 'Workflow validation failed');
      }

      if (validationResult.data && validationResult.data.valid === false) {
        throw new Error('Workflow has validation errors and cannot be published');
      }

      const publishResult = await WorkflowService.publishWorkflow(activeWorkflowId, {
        notes: 'Published from canvas'
      });

      if (!publishResult.success) {
        throw new Error(publishResult.error || 'Unable to publish workflow');
      }

      const publishedWorkflow = publishResult.data?.workflow || null;
      // The publish response returns the new WorkflowVersion as `version` object with `version_number`.
      // Fall back to list-enriched fields if the response shape differs.
      const versionObj = publishResult.data?.version;
      const publishedVersion =
        (typeof versionObj === 'object' ? versionObj?.version_number : versionObj) ??
        publishedWorkflow?.published_version_number ??
        null;

      if (publishedWorkflow) {
        setWorkflowData(publishedWorkflow);
      }

      if (onWorkflowDataLoaded) {
        onWorkflowDataLoaded({
          name: publishedWorkflow?.name || workflowData?.name || 'Untitled Workflow',
          status: publishedWorkflow?.status || 'active',
          version: publishedVersion,
          triggerCount: nodes.filter((node) => node.type === 'trigger').length,
          steps: publishedWorkflow?.steps || workflowData?.steps || []
        });
      }

      toastSuccess('Published', `Workflow published${publishedVersion ? ` — v${publishedVersion}` : ''}.`);
    } catch (err) {
      console.error('Error publishing workflow:', err);
      toastError('Publish failed', err.message || 'Unable to publish workflow.');
    } finally {
      setPublishing(false);
    }
  };

  // Ejecutar callbacks externos cuando cambien los handlers internos
  React.useEffect(() => {
    if (onSave) onSave.current = handleSaveDraft;
    if (onTest) onTest.current = handleTest;
    if (onRun) onRun.current = handleRun;
    if (onPublish) onPublish.current = handlePublish;
  }, [onSave, onTest, onRun, onPublish, handleSaveDraft, handleTest, handleRun, handlePublish]);

  return (
    <div className="h-full flex flex-col" style={style}>
      {/* Inspector Panel - debajo del TopNav, colapsable */}
      {/* Main Content */}
      <div className="flex-1 flex min-h-0 gap-3 px-3 py-3 md:gap-4 md:px-6 md:py-4">
        {/* Velo del cajón: cerrar tocando fuera es como se sale de aquí con el
            pulgar, antes que buscar una X. */}
        {isMobile && showStepPalette && (
          <div
            className="fixed inset-0 z-40 bg-black/60"
            onClick={() => setShowStepPalette(false)}
          />
        )}
        {/* Left Sidebar - Step Palette (hidden in read-only) */}
        {isReadOnly ? (
          <div className={`w-72 bg-black/20 backdrop-blur-sm flex flex-col border border-white/10 rounded-xl shadow-2xl shadow-black/50 overflow-hidden ${
            isMobile
              ? `fixed inset-y-0 left-0 z-50 max-w-[85vw] !rounded-none transition-transform duration-200 ${showStepPalette ? "translate-x-0" : "-translate-x-full"}`
              : "shrink-0"
          }`}>
            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center">
              <div className="w-10 h-10 rounded-xl bg-amber-400/10 border border-amber-400/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/></svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-white mb-1">Published version</p>
                <p className="text-xs text-gray-500">Create a draft to add, remove or edit steps.</p>
              </div>
            </div>
          </div>
        ) : (
        <div data-step-palette="true" className={`w-72 bg-black/20 backdrop-blur-sm flex flex-col border border-white/10 rounded-xl shadow-2xl shadow-black/50 overflow-hidden ${
          isMobile
            ? `fixed inset-y-0 left-0 z-50 max-w-[85vw] !rounded-none transition-transform duration-200 ${showStepPalette ? "translate-x-0" : "-translate-x-full"}`
            : "shrink-0"
        }`}>
        <div className="p-4 shrink-0">
          <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-3">
            Add a step
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search steps..."
              value={stepSearchQuery}
              onChange={(e) => setStepSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-black/40 backdrop-blur-sm border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20 transition-all"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
          {displayedStepCategories.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-gray-500">
              No steps match "{stepSearchQuery}"
            </div>
          ) : (
            displayedStepCategories.map(category => {
              const isSearching = stepSearchQuery.trim().length > 0;
              return (
                <StepPaletteCategory
                  key={category.key}
                  title={category.title}
                  steps={category.steps}
                  groups={category.groups}
                  expanded={isSearching ? true : expandedCategories[category.key]}
                  onToggle={() => toggleCategory(category.key)}
                />
              );
            })
          )}
        </div>
      </div>
        )}

      {/* Main Canvas */}
      <div className="flex-1 relative border border-white/10 rounded-xl shadow-2xl shadow-black/50 overflow-hidden" style={{ backgroundColor: "#09090b" }}>
        {/* Con la paleta convertida en cajón hace falta algo que la abra. Va
            sobre el lienzo, arriba a la izquierda, que es de donde entra. */}
        {isMobile && !showStepPalette && (
          <button
            type="button"
            onClick={() => setShowStepPalette(true)}
            aria-label="Add a step"
            className="absolute left-3 top-3 z-10 inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-[#141416]/90 text-gray-300 shadow-lg backdrop-blur-sm transition-colors active:bg-white/10 active:text-white"
          >
            <Plus className="h-5 w-5" />
          </button>
        )}
        {/* Gradiente de fondo */}
        <div style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          background: `
            radial-gradient(600px circle at 15% 10%, rgba(139,92,246,0.18), transparent 55%),
            radial-gradient(700px circle at 85% 110%, rgba(217,70,239,0.12), transparent 60%)
          `
        }}/>
        
        {/* Loading State */}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center z-50">
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-violet-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-400 text-sm">Loading workflow...</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
            <div className="text-center max-w-md">
              <div className="w-20 h-20 rounded-2xl bg-violet-500/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">🎨</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Start Building Your Workflow</h3>
              <p className="text-sm text-gray-400">
                Drag nodes from the left panel onto the canvas to create your workflow.
                Connect them together to define your automation logic.
              </p>
            </div>
          </div>
        )}
        
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onNodeDoubleClick={onNodeDoubleClick}
          onNodeContextMenu={onNodeContextMenu}
          onPaneClick={onPaneClick}
          onDrop={onDrop}
          onDragOver={onDragOver}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodesDraggable={!isReadOnly}
          nodesConnectable={!isReadOnly}
          zoomOnDoubleClick={false}
          defaultViewport={{ x: 50, y: 50, zoom: 1 }}
          minZoom={0.3}
          maxZoom={2}
          deleteKeyCode={null}
          multiSelectionKeyCode={["Meta", "Control"]}
          selectionKeyCode={["Meta", "Control"]}
          className="bg-transparent"
          defaultEdgeOptions={{
            type: "default",
            interactionWidth: 30,
          }}
          proOptions={{ hideAttribution: true }}
          onInit={(instance) => {
            setRfInstance(instance);
            const zoom = instance.getZoom();
            setCurrentZoom(Math.round(zoom * 100));
          }}
          onMove={(event, viewport) => {
            const zoom = viewport.zoom;
            setCurrentZoom(Math.round(zoom * 100));
          }}
        >
          <Background 
            variant="dots"
            gap={24}
            size={1}
            color="rgba(255,255,255,0.07)"
          />
          <Controls 
            style={{
              background: "rgba(24,24,27,0.8)",
              border: "1px solid #27272a",
              borderRadius: 6,
            }}
          />
          {/* El minimapa se cae en móvil: es una vista general del lienzo
              dibujada encima del lienzo, y en 390px la caja no cabe entera —se
              salía por el borde derecho tapando lo que venía a resumir. */}
          <MiniMap 
            className="!hidden md:!block !bg-slate-950/90 !backdrop-blur-sm !border-slate-800 !rounded-lg !shadow-xl" 
            nodeColor={(node) => {
              if (node.type === "trigger") return "#ea580c";
              if (node.type === "agent") return "#8b5cf6";
              if (node.type === "condition") return "#0f172a";
              if (node.type === "action") return "#14b8a6";
              if (node.type === "satellite") return "#8b5cf6";
              return "#64748b";
            }}
            maskColor="rgba(0, 0, 0, 0.6)"
          />
        </ReactFlow>

        {contextMenu && (
          <div
            style={{ left: contextMenu.x, top: contextMenu.y }}
            className="fixed z-[1200] w-[200px] overflow-hidden rounded-xl border border-white/15 bg-zinc-950/95 shadow-2xl shadow-black/70 backdrop-blur-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="w-full px-4 py-2.5 text-left text-sm text-gray-100 hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handleDuplicateNode(contextMenu.nodeId)}
              disabled={nodes.find((item) => item.id === contextMenu.nodeId)?.type === 'satellite'}
            >
              Duplicate node
            </button>
            <button
              type="button"
              className="w-full px-4 py-2.5 text-left text-sm text-gray-100 hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handleStartRename(contextMenu.nodeId)}
              disabled={nodes.find((item) => item.id === contextMenu.nodeId)?.type === 'satellite'}
            >
              Rename node
            </button>
            {(() => {
              const ctxNode = nodes.find(n => n.id === contextMenu.nodeId);
              const stepId = ctxNode?.data?.step_id;
              if (!stepId) return null;
              const isPinned = pinnedStepIds.has(stepId);
              return (
                <>
                  <div className="border-t border-white/10 my-0.5" />
                  {isPinned ? (
                    <button
                      type="button"
                      className="w-full px-4 py-2.5 text-left text-sm text-blue-300 hover:bg-blue-500/10 transition-colors flex items-center gap-2"
                      onClick={() => handleUnpinStep(contextMenu.nodeId)}
                    >
                      <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/>
                      </svg>
                      Remove pin
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={!lastExecutionId}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-100 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                      onClick={() => handlePinFromLastRun(contextMenu.nodeId)}
                    >
                      <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/>
                      </svg>
                      {lastExecutionId ? 'Pin from last run' : 'Pin output (run first)'}
                    </button>
                  )}
                </>
              );
            })()}
            <div className="border-t border-white/10 my-0.5" />
            <button
              type="button"
              className="w-full px-4 py-2.5 text-left text-sm text-red-300 hover:bg-red-500/20 transition-colors"
              onClick={() => handleDeleteNode(contextMenu.nodeId)}
            >
              Delete node
            </button>
          </div>
        )}

        {/* Inline rename input — floats over the node being renamed */}
        {renamingNode && rfInstance && (() => {
          const target = nodes.find((n) => n.id === renamingNode.id);
          if (!target) return null;
          const screen = rfInstance.flowToScreenPosition({
            x: target.position.x,
            y: target.position.y,
          });
          const zoom = rfInstance.getViewport?.().zoom ?? 1;
          const width = (target.measured?.width || target.width || 220) * zoom;
          return (
            <div
              style={{ left: screen.x, top: screen.y }}
              className="fixed z-[1300] flex items-center gap-1"
            >
              <input
                autoFocus
                style={{ minWidth: Math.max(160, width) }}
                className="px-3 py-2 rounded-lg border border-violet-500/60 bg-zinc-950/95 text-sm text-white shadow-2xl shadow-black/70 backdrop-blur-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                value={renamingNode.value}
                placeholder="Step name"
                onChange={(e) => setRenamingNode((c) => (c ? { ...c, value: e.target.value } : c))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); handleCommitRename(); }
                  else if (e.key === 'Escape') { e.preventDefault(); setRenamingNode(null); }
                }}
              />
              <button
                type="button"
                title="Confirm"
                onClick={handleCommitRename}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary hover:bg-violet-500 text-white shadow-lg transition-colors shrink-0"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                type="button"
                title="Cancel"
                onClick={() => setRenamingNode(null)}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-gray-300 shadow-lg transition-colors shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })()}

        {/* Zoom Controls */}
        {/* Se cae en móvil. Los controles propios de React Flow, abajo a la
            izquierda, ya hacen acercar, alejar y ajustar: esta píldora sólo
            añade el porcentaje, y por él no vale la pena tener dos controles de
            zoom peleándose por el pie de un lienzo de 390px. */}
        <div className="absolute bottom-6 right-6 hidden items-center gap-3 rounded-xl border border-white/10 bg-black/40 px-4 py-2.5 shadow-2xl backdrop-blur-xl md:flex">
          <button 
            onClick={() => rfInstance?.zoomOut()}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <Minus className="w-4 h-4" />
          </button>
          <span className="text-sm font-semibold text-white tabular-nums min-w-[3rem] text-center">{currentZoom}%</span>
          <button 
            onClick={() => rfInstance?.zoomIn()}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* Agent Task Editor overlay */}
        <AnimatePresence>
          {agentTaskEditorNode && (
            <AgentTaskEditor
              node={agentTaskEditorNode}
              llmModels={llmModels}
              availableSkills={availableSkills}
              allNodes={nodes}
              stepResults={lastStepResults}
              readOnly={isReadOnly}
              onBack={() => setAgentTaskEditorNode(null)}
              onSaveToNode={(tasks) => {
                // Get current execution_mode from agentTaskEditorNode's internal state
                // (it will be updated via the separate execution mode handler)
                const currentNode = nodes.find(n => n.id === agentTaskEditorNode.id);
                setNodes(nds =>
                  nds.map(n =>
                    n.id === agentTaskEditorNode.id
                      ? { ...n, data: { ...n.data, tasks, execution_mode: currentNode?.data?.execution_mode || 'sequential' } }
                      : n
                  )
                );
                setAgentTaskEditorNode(null);
              }}
              onUpdateAssistant={(assistantId) => {
                const assistant = assistants.find(a => a.id === assistantId);
                if (!assistant) return;
                
                // Get avatar from multiple possible field names
                const avatarUrl = assistant.avatar_url || assistant.avatar || assistant.profile_image_url;
                
                // Update node data
                setNodes(nds => nds.map(n => {
                  if (n.id === agentTaskEditorNode.id) {
                    return {
                      ...n,
                      data: {
                        ...n.data,
                        assistant_id: assistant.id,
                        label: assistant.name,
                        sub: assistant.description || 'AI Agent',
                        avatar_url: avatarUrl,
                        avatar: avatarUrl,
                        llm_id: assistant.config?.llmId || assistant.llm_id,
                        model_name: assistant.config?.modelLabel || assistant.model_name,
                        skills_count: assistant.skills?.length || 0
                      }
                    };
                  }
                  return n;
                }));
                
                // Regenerate satellite nodes
                setNodes(nds => nds.filter(node =>
                  !(node.type === 'satellite' && node.data.parentId === agentTaskEditorNode.id)
                ));
                setEdges(eds => eds.filter(edge =>
                  !edge.id.startsWith(`satellite-${agentTaskEditorNode.id}`)
                ));
                
                const satelliteGraph = buildAgentSatelliteGraph({
                  parentNodeId: agentTaskEditorNode.id,
                  parentPosition: agentTaskEditorNode.position,
                  assistant,
                  fallbackLabel: assistant.name || 'AI Agent',
                  llmModels
                });

                setNodes(nds => [
                  ...nds.map(n => n.id === agentTaskEditorNode.id
                    ? { ...n, data: { ...n.data, satellitesExpanded: true } }
                    : n
                  ),
                  ...satelliteGraph.nodes
                ]);
                setEdges(eds => [...eds, ...satelliteGraph.edges]);
              }}
              assistants={assistants}
              loadingAssistants={loadingAssistants}
              setNodes={setNodes}
            />
          )}
        </AnimatePresence>

        {/* Schedule Trigger Editor overlay */}
        <AnimatePresence>
          {scheduleTriggerEditorNode && (
            <ScheduleTriggerEditor
              node={scheduleTriggerEditorNode}
              onBack={() => setScheduleTriggerEditorNode(null)}
              onSave={(configuration) => {
                setNodes(nds =>
                  nds.map(n =>
                    n.id === scheduleTriggerEditorNode.id
                      ? { 
                          ...n, 
                          data: { 
                            ...n.data, 
                            configuration: {
                              ...n.data.configuration,
                              ...configuration
                            }
                          } 
                        }
                      : n
                  )
                );
                setScheduleTriggerEditorNode(null);
              }}
            />
          )}
        </AnimatePresence>

        {/* Step Editor Modal (unified for all node types) */}
        <AnimatePresence>
          {stepEditorNode && (
            <StepEditorModal
              node={stepEditorNode}
              llmModels={llmModels}
              allNodes={nodes}
              stepResults={lastStepResults}
              readOnly={isReadOnly}
              schema={(() => {
                const data = stepEditorNode?.data || {};
                if (data.schema?.properties) return data.schema;
                const tt = data.trigger_type;
                if (tt) {
                  const te = triggerCatalog?.entries?.find((x) => x.trigger_type === tt);
                  if (te?.json_schema) return te.json_schema;
                }
                const st = data.step_type || stepEditorNode?.type;
                const se = stepCatalog?.entries?.find((x) => x.step_type === st);
                return se?.json_schema || data.schema || null;
              })()}
              onBack={() => setStepEditorNode(null)}
              onSave={(updatedNode) => {
                setNodes(nds =>
                  nds.map(n =>
                    n.id === updatedNode.id ? updatedNode : n
                  )
                );
                setStepEditorNode(null);
              }}
            />
          )}
        </AnimatePresence>

        <ToastContainer toasts={toasts} onRemoveToast={removeToast} />

        <WorkflowTestModal
          open={showTestModal}
          onClose={() => setShowTestModal(false)}
          onExecute={handleTestExecution}
        />

        {/* View Execution Button */}
        <AnimatePresence>
          {lastExecutionId && !showRunInspector && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="absolute top-6 right-6 z-40"
            >
              <button
                onClick={() => setShowRunInspector(true)}
                className="flex items-center gap-2 bg-primary hover:bg-violet-700 text-white px-5 py-3 rounded-xl shadow-lg shadow-violet-600/30 transition-all font-medium text-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                View Execution
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Run Inspector Modal */}
        <AnimatePresence>
          {showRunInspector && lastExecutionId && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="h-full w-full"
              >
                <RunInspector 
                  runId={lastExecutionId} 
                  projectId={projectId}
                  onClose={() => setShowRunInspector(false)} 
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom Help Text */}
        {!agentTaskEditorNode && !scheduleTriggerEditorNode && showHelpMessage && (
        <div className="absolute bottom-3 left-3 right-3 md:bottom-6 md:left-80 md:right-auto md:max-w-2xl">
          <div className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-xl px-4 py-3 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="text-base">💡</div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-white mb-1">
                  <span className="font-bold">Save</span> persists the draft. <span className="font-bold">Publish</span> creates an immutable version snapshot and routes new runs to it.
                </div>
                <div className="text-[10px] text-gray-400">
                  The runner reads from the published snapshot, so draft edits never affect in-flight executions.
                </div>
              </div>
              <button 
                onClick={() => setShowHelpMessage(false)}
                className="text-gray-400 hover:text-white transition-colors text-sm shrink-0"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
        )}
      </div>
      {/* End Main Content */}
    </div>
    {/* End flex container */}
  </div>
  );
}

// Wrapper con ReactFlowProvider para usar useReactFlow hook
export default function WorkflowCanvas({ workflowId, projectId, workflowName, onSave, onTest, onRun, onPublish, onWorkflowDataLoaded, style, isDraftEditMode = false }) {
  return (
    <ReactFlowProvider>
      <WorkflowCanvasInner
        workflowId={workflowId}
        projectId={projectId}
        workflowName={workflowName}
        onSave={onSave}
        onTest={onTest}
        onRun={onRun}
        onPublish={onPublish}
        onWorkflowDataLoaded={onWorkflowDataLoaded}
        style={style}
        isDraftEditMode={isDraftEditMode}
      />
    </ReactFlowProvider>
  );
}
